import { AdminDivisionGrid, AdminPagedCards } from "./production_admin_divisions";
import { ShopeeCancelTag } from "./shopee_cancel_tag";
import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useAutoRefresh } from "./use_auto_refresh";
import axios from "axios";
import { ShipTodayPdfButton } from "./ship_today_pdf";
import { guardedUpload, withQuery } from "./upload_guard";
import { X, ClipboardCheck, Warehouse, Sparkles, Printer, Brush, Layers, Store, CheckCircle2, GitBranch, AlertTriangle, Palette, ChevronDown, ChevronUp, Clock, User, Lock, Sparkle, Image as ImageIcon, Boxes, Package, Search, Camera, Calendar, QrCode, Copy, Check, ChevronRight, Upload, ClipboardPaste } from "lucide-react";
import { PROD_TABS, ROUTE_STAGES_UI, ROUTE_CHOICES, PARALLEL_STAGE_TRACKS, STAGE_TEAM_LABEL } from "./production_config";
import { ProductFilesPanel } from "./product_files";
import { UseExistingMockupButton, NotaGroupGudangInfo } from "./nota_group";
import { ProductionMonitoring } from "./production_monitoring";
import { MockupPreviewModal } from "./mockup_preview";
import { StockModule } from "./stock";
import { ShopeeDirectWarehouseSection } from "./shopee_direct_warehouse";
import { ShopeeWarehouseSection, ShopeeEmbroiderySection, ShopeeAccWaitingSection, ShopeeBordirOperatorSection, ShopeeErrorBoundary, ShopeeMockupThumb } from "./warehouse_module";
import { groupRiwayatByProduct } from "./riwayat_grouping";
import { DesainTodayCard } from "./desain_today_card";
import { RidoShopeeTiles } from "./rido_tiles";
import { StockCheckBoard } from "./stock_check_board";
import { GlobalFind, GlobalFindButton } from "./global_find";
import { RidoHeaderMenu, RidoMoreMenu } from "./rido_desk";
import { RidoPantauOverlay } from "./rido_pantau";
import "./desain_bordir_gold.css";
import "./rido_hairline.css";
import "./rido_ref.css";
import { OperatorBordirPerformance } from "./bordir_operator_performance";
import { RiwayatPekerjaanCard } from "./riwayat_pekerjaan";
import { MonitorThumb } from "./production_monitoring";
import { CetakMockupSizeModal, KonfirmasiCetakModal, unduhMockup, cetakPdfA5P, cetakMockup } from "./cetak_mockup";
import { notify } from "./modal_center";
import { BordirCancelDialog, priorProgressText } from "./bordir_cancel_dialog";
import { DesignPointsDialog } from "./bordir_design_points";
import { OfflinePressWaiting, useOfflineWaitCount } from "./offline_press_waiting";
import { RepeatOrderBadge } from "./repeat_order";
import { JenisPengerjaanBadge } from "./jenis_pengerjaan_badge";
import { toast } from "sonner";
import ScanMockupModal from "./scan_mockup";
import { AccFastScan } from "./acc_fast_scan";
import { ShellaFastScan } from "./shella_fast_scan";
import { PressLiveBar } from "./press_live";
import { LiveQueueBar } from "./live_queue";
import { PressChannelNav, LateBanner, usePressShopeeSummary, PressTabs } from "./press_summary";
import { useChannel, useChannelReset, pickSource, initSource, tabOf, takeStash, TokopediaBanner, getChannel, setChannel, stashFor } from "./channel";
import { SourceTab } from "./marketplace_brand";
import { isCostItem, mockupSourceLabel } from "./order_item_utils";
import { DesignFormButton } from "./design_form";
import { MockupTemplateButton } from "./mockup_template_button";
import ShopeeSablonPanel from "./shopee_sablon_panel";
import ShopeePressPanel from "./shopee_press_panel";
import { ownerManualTag } from "./owner_manual";
import { MyBordirTasks, BordirSplitPanel, useBordirReadiness, ReadinessList } from "./bordir_tasks";
import { ClaimLookup, useClaimSummary, isSharedBordir } from "./bordir_claims";
import { BordirTeamActions, FinishWithClaim, ResumeScanButton, TeamStatus, teamRole, needsFinishForm } from "./bordir_team";
import { AiMockupSummary } from "./ai_mockup_summary";
import { MockupSummaryBox, MockupProductList, MockupStatusRow, MockupMoreMenu, PasteMockupModal, MockupWaLine } from "./mockup_card_ui";


// Iter 127 — Tim yang mendapat akses "Lihat Mockup" (read-only) di kartu produksi.
// Gudang sudah punya tab "Pemantauan" khusus, jadi kita fokus tim eksekusi lain.
// Iter 287av — "Sablon/DTF" (Ibnu dkk) ditambahkan agar operator Sablon bisa preview
// mockup langsung dari kartu (tanpa buka Detail). Backend endpoint sudah mengizinkan.
// Iter 346 — Tambah "Gudang" (Rido) agar inline thumbnail mockup muncul juga di antrean
//   Gudang Offline + Shopee. "Jersey/Sublim" & "Selesai" ditambahkan supaya union logic
//   render inline thumb (sudah di MOCKUP_THUMB_TEAMS).
const MOCKUP_PREVIEW_TEAMS = new Set(["Bordir", "Operator Bordir", "Mesin Press", "Finishing", "Sablon/DTF", "Gudang", "Jersey/Sublim", "Selesai"]);
// Iter 332.8 — Tim yang pakai pola compact: thumbnail 80px + Preview Mockup kecil
// (bukan tombol full-width). Pattern konsisten dengan kartu Mia (Mockup) & Shopee.
// Iter 333 — Operator Bordir (jahit mesin) ditambahkan supaya dapat thumbnail mockup di kartu stage Bordir.
// Iter 346 — Tambah "Gudang" (Rido) + "Sablon/DTF" (Ibnu) agar thumbnail inline muncul
//   di kartu Antrean/Dikerjakan/Riwayat kedua tim. Pakai komponen `QueueMockupThumb`
//   yang sudah ada (reuse endpoint /mockup.preview + onError placeholder).
const MOCKUP_THUMB_TEAMS = new Set(["Bordir", "Operator Bordir", "Mesin Press", "Jersey/Sublim", "Selesai", "Gudang", "Sablon/DTF"]);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter 71 — Stage icon + accent mapping (visual consistency dengan prod-tab)
const STAGE_META = {
  "Perlu Diperiksa": { icon: AlertTriangle, key: "perlu-diperiksa" },
  "Mockup": { icon: ClipboardCheck, key: "mockup" },
  "Toko": { icon: Store, key: "toko" },
  "Gudang": { icon: Warehouse, key: "gudang" },
  "Bordir": { icon: Sparkles, key: "bordir" },
  "Sablon/DTF": { icon: Brush, key: "sablon-dtf" },
  "Sablon Paralel": { icon: GitBranch, key: "sablon-paralel" },
  "Persiapan Paralel": { icon: GitBranch, key: "persiapan-paralel" },
  "Sablon Plastisol": { icon: Palette, key: "sablon-plastisol" },
  "Mesin Press": { icon: Printer, key: "mesin-press" },
  "Jersey/Sublim": { icon: Layers, key: "jersey-sublim" },
  "Finishing": { icon: Sparkle, key: "finishing" },
  "Selesai": { icon: CheckCircle2, key: "selesai" },
};
const stageKey = (stage) => STAGE_META[stage]?.key || "default";

// Serah terima desain bordir berlaku untuk putaran Bordir saat ini (sama dengan server _design_handed_over).
export function designHandedOver(p) {
  const at = String(p?.bordir_design_handover?.at || "");
  const entered = (p?.production_pic_history || []).filter(h => h.stage_to === "Bordir" && h.stage_from !== "Bordir")
    .reduce((m, h) => (String(h.at || "") > m ? String(h.at || "") : m), "");
  return !!at && at > entered;
}

// Iter 287ar — Short deadline formatter: ISO "YYYY-MM-DD" → "5 Okt" (sesuai desain Haikal).
//   Fallback: tampilkan string asli bila parse gagal.
const BULAN_SHORT = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
function fmtDLShort(iso) {
  if (!iso) return "—";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  const day = parseInt(m[3], 10);
  const mon = BULAN_SHORT[parseInt(m[2], 10) - 1] || "";
  return `${day} ${mon}`;
}

// Iter 287au — Highlight match untuk search bar operator. Case-insensitive, membungkus
// substring cocok dengan <mark className="qmark">. Aman: query di-escape untuk regex,
// query kosong/invalid → kembalikan text apa adanya (string, bukan array).
function highlightMatch(text, query) {
  const t = text == null ? "" : String(text);
  const q = String(query || "").trim();
  if (!q || !t) return t;
  const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(safe, "ig");
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(t)) !== null) {
    if (m.index > last) out.push(t.slice(last, m.index));
    out.push(<mark key={`qm-${i++}`} className="qmark">{t.slice(m.index, m.index + m[0].length)}</mark>);
    last = m.index + m[0].length;
    if (m[0].length === 0) re.lastIndex++; // guard
  }
  if (last < t.length) out.push(t.slice(last));
  return out.length ? out : t;
}

const DEADLINE_ORDER = { "Terlambat":0, "Hampir Deadline":1, "Aman":2, "Selesai":3 };
const wibDay = (d) => {
  const s = String(d || "");
  if (!s) return "";
  if (s.length <= 10) return s;
  try { return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(s)); }
  catch { return s.slice(0, 10); }
};
// Antrean Desain Bordir Offline: Target Selesai (tanggal WIB) terdekat/terlambat dulu, lalu paling lama menunggu; tanpa deadline di bawah.
export function sortDesignQueue(groups) {
  const waitAt = (g) => (g.productCards || [g]).reduce((m, c) => {
    const t = String(c.product?.production_updated_at || "");
    return t && (!m || t < m) ? t : m;
  }, "");
  return [...groups].sort((a, b) => {
    const da = wibDay(a.order?.deadline), db = wibDay(b.order?.deadline);
    if (!da !== !db) return da ? -1 : 1;
    if (da !== db) return da < db ? -1 : 1;
    return waitAt(a).localeCompare(waitAt(b));
  });
}
function sortByDeadlinePriority(cards) {
  return [...cards].sort((a,b) => {
    const da = DEADLINE_ORDER[a.product.deadline_status ?? "Aman"] ?? 2;
    const db = DEADLINE_ORDER[b.product.deadline_status ?? "Aman"] ?? 2;
    if (da !== db) return da - db;
    return String(a.order.deadline||"").localeCompare(String(b.order.deadline||""));
  });
}

// Iter 246 — Group cards by (order_number + stage + parallel track + route + claim state)
// so multi-product 1 nota di divisi yg sama tampil sebagai 1 kartu combined.
// Kartu tetap terpisah jika:
//   - Track paralel berbeda (mis. sablon vs gudang di stage Sablon Paralel)
//   - Jalur produksi (route) berbeda → next stage berbeda → tombol "Selesai Tahap"
//     tidak boleh salah target.
//   - Claim state berbeda (mis. 1 produk sudah di-claim operator lain, 1 belum).
function groupTeamCards(cards, tabKey, opName) {
  // Iter 259 — Group per (order_number + team). Team implicit via `tabKey`.
  //   SEMUA produk dalam nota yang jadi tanggung jawab tim ini masuk 1 card.
  //   Route/stage/track boleh beda antar produk — sub-action per sub-group ditangani
  //   di TeamProductCard (route-aware, tidak batching produk beda route jadi 1 tombol).
  const groups = new Map();
  for (const c of cards) {
    const gk = `${c.order.order_number}::${tabKey}`;
    if (!groups.has(gk)) {
      groups.set(gk, { ...c, groupKey: gk, productCards: [c] });
    } else {
      groups.get(gk).productCards.push(c);
    }
  }
  // Sub-group signature untuk kebutuhan render action-row di dalam card.
  const subKey = (c) => {
    const tracks = PARALLEL_STAGE_TRACKS[c.stage];
    const track = tracks ? (tracks.find(tr => tr.team === tabKey)?.track || "") : "";
    const trackKey = track || "single";
    const routeKey = c.product.production_route || "";
    let claimKey = "unclaimed";
    if (track) {
      const cb = c.product.parallel_status?.[`${track}_claimed_by`];
      if (cb) claimKey = (opName && cb === opName) ? "mine" : `other:${cb}`;
    } else {
      const cb = c.product.production_pic;
      if (cb) claimKey = (opName && cb === opName) ? "mine" : `other:${cb}`;
    }
    return `${c.stage}::${trackKey}::${routeKey}::${claimKey}`;
  };
  for (const g of groups.values()) {
    const subMap = new Map();
    for (const pc of g.productCards) {
      const sk = subKey(pc);
      if (!subMap.has(sk)) subMap.set(sk, { key: sk, cards: [pc], primary: pc });
      else subMap.get(sk).cards.push(pc);
    }
    g.subGroups = Array.from(subMap.values());
  }
  return Array.from(groups.values());
}

// Kanal Shopee/Tokopedia: ganti kanal me-remount antrean agar semua data Shopee dimuat ulang dengan filter kanal baru.
// Pantau Produksi Rido/Rika dipasang DI LUAR antrean ber-key kanal: pindah tab Offline/Shopee/Tokopedia di dalamnya
// boleh mengganti kanal tanpa menutup Pantau; saat ditutup kanal, tab & bagian Gudang sebelumnya dipulihkan.
export function ProductionQueue(props) {
  const ch = useChannel();
  useChannelReset();
  const [pantau, setPantau] = useState(null);
  const pRef = useRef(null);
  const restore = (p) => { stashFor("gudang", p.src); stashFor("gudang_section", p.section); };
  const switchChannel = useCallback((t) => {
    const p = pRef.current;
    if (!p || t === getChannel()) return;
    p.remounted = true;
    if (t === p.ch) restore(p);
    setChannel(t);
  }, []);
  const closePantau = useCallback(() => {
    const p = pRef.current;
    pRef.current = null; setPantau(null);
    if (!p) return;
    if (getChannel() !== p.ch) { restore(p); setChannel(p.ch); }
    if (p.remounted) restoreScroll(p.y);
  }, []);
  const openPantau = useCallback((o) => { pRef.current = { ...o, ch: getChannel(), y: window.scrollY }; setPantau(pRef.current); }, []);
  // Prioritas internal Offline memakai Deadline Produksi; nota lama tanpa field ini tetap memakai deadline lama.
  const orders = useMemo(() => (props.orders || []).map(o => (o?.production_deadline ? { ...o, deadline: o.production_deadline,
    products: (o.products || []).map(p => (p.production_deadline_status ? { ...p, deadline_status: p.production_deadline_status } : p)) } : o)), [props.orders]);
  return <>
    <ProductionQueueInner key={ch} {...props} orders={orders} onOpenPantau={openPantau}/>
    {pantau && <div data-team={props.operator?.team}><RidoPantauOverlay operator={props.operator} onOpen={props.onOpen} onClose={closePantau} onSwitchChannel={switchChannel}
      initialSource={pantau.src === "offline" ? "offline" : pantau.ch} extraStats={pantau.extra}/></div>}
  </>;
}

function restoreScroll(y) {
  let n = 0;
  const t = setInterval(() => {
    window.scrollTo(0, y);
    if (Math.abs(window.scrollY - y) < 4 || ++n > 25) clearInterval(t);
  }, 100);
}

function ProductionQueueInner({ user, operator, orders, onOpen, onChanged, onOperatorLogout, onOpenPantau }) {
  const role = String(user?.role||"").toLowerCase();
  const isAdmin = role === "owner" || role === "admin";
  // Operator mode: hanya tab timnya. Owner/Admin: semua tab.
  const visibleTabs = operator
    ? PROD_TABS.filter(t => t.teamName === operator.team)
    : PROD_TABS.filter(t => t.roles.includes(role));
  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.key || "Perlu Diperiksa");
  const [busyKey, setBusyKey] = useState("");
  const [pointsCard, setPointsCard] = useState(null);
  const [cancelCards, setCancelCards] = useState([]);
  const [correctCard, setCorrectCard] = useState(null);
  // Iter 88 — Operator mode: 3-section internal tab (default: sedang bila ada, else antrean)
  // Iter 142 — Gudang operator: default section = "prep" (Penyiapan Barang) yang menggantikan
  //   Antrean/Sedang untuk fokus tugas penyiapan barang.
  // Iter 307 — Scanner barcode mockup overlay (HP kamera).
  const [scanOpen, setScanOpen] = useState(false);
  // Iter 304 — Operator ACC-only (Adit): akses sangat terbatas.
  //   Default landing = "acc"; hide tab Penyiapan/Pemantauan/Stok/Shopee.
  const isAccOnly = operator?.team === "Gudang" && !!operator?.is_acc_only;
  const [opSection, setOpSection] = useState(() =>
    takeStash("gudang_section", isAccOnly ? "acc" : (operator?.team === "Gudang" ? "prep" : "sedang"))
  );
  // Iter 287ar — Search bar untuk operator team (non-Gudang): filter berdasar nama customer / nomor nota.
  const [opSearch, setOpSearch] = useState("");
  // Haikal (Mesin Press): satu area daftar — kanal Shopee/Offline + tab; filter Terlambat dari kartu ringkasan.
  const [pressCh, setPressCh] = useState(() => initSource("press", "shopee"));
  const [pressTab, setPressTab] = useState("siap_press");
  const [pressLate, setPressLate] = useState(false);
  const pressShp = usePressShopeeSummary();
  const pressOffWait = useOfflineWaitCount();
  // Iter 262 — Gudang operator: page-level toggle antara "Pesanan Gudang" (default,
  //   existing sections) dan "Stok Barang" (StockModule inline). Reuse modul stok
  //   Owner tanpa duplikasi backend/frontend.
  const isGudangOp = operator?.team === "Gudang";
  // Iter 287ae — Tim Desain Bordir juga dapat tab Shopee terpisah.
  const isBordirOp = operator?.team === "Bordir";
  // Iter 398 — Operator Bordir fisik (jahit mesin) juga butuh akses modul Shopee
  //   untuk melihat kartu yang desainnya selesai di-handoff ke mereka.
  const isBordirOperatorOp = operator?.team === "Operator Bordir";
  const [gudangPage, setGudangPage] = useState("pesanan"); // "pesanan" | "stok"
  // Iter 287h — Sub-source di Pesanan Gudang: pisahkan antrean Offline vs Shopee.
  const [gudangSource, setGudangSource] = useState(() => initSource("gudang", "offline")); // "offline" | "shopee"
  // Iter 323 — Sub-source di dalam tab ACC (Adit/Rido/Owner): Offline vs Shopee.
  const [accSource, setAccSource] = useState("offline"); // "offline" | "shopee"
  // Iter 287ae — Sub-source di antrean Bordir: Offline vs Shopee (dari penerusan Rido).
  const [bordirSource, setBordirSource] = useState(() => initSource("bordir", "offline")); // "offline" | "shopee"
  // Iter 550 — Sub-source di antrean Sablon/DTF (Ibnu): pisahkan Offline vs Shopee
  //   agar tidak bertumpuk. Tab tunggal di atas; satu panel saja aktif pada satu waktu.
  const isSablonOp = operator?.team === "Sablon/DTF";
  const [sablonSource, setSablonSource] = useState(() => initSource("sablon", "offline")); // "offline" | "shopee"
  // Iter 471 — Auto-refresh 60s untuk tim Desain Bordir (team=Bordir atau
  //   Operator Bordir) di SISI OFFLINE. Memanggil `onChanged` (parent load())
  //   yang mem-fetch /api/orders. SILENT: parent load tidak sentuh state kartu
  //   yang sudah ada, hanya overwrite setelah response sukses. Inflight guard
  //   bawaan hook mencegah duplicate request.
  useAutoRefresh({
    enabled: (isBordirOp || isBordirOperatorOp) && bordirSource === "offline" && typeof onChanged === "function",
    intervalMs: 60_000,
    onRefresh: () => { try { onChanged && onChanged(); } catch {} },
  });
  // Iter 127 — Mockup preview state (Bordir / Mesin Press / Finishing)
  const [mockupPreview, setMockupPreview] = useState(null);
  const openMockupPreview = (order) => setMockupPreview({ order_number: order.order_number, customer_name: order.customer_name, is_repeat_order: !!order.is_repeat_order, repeat_ref: order.repeat_ref || "" });
  const cards = [];
  for (const o of (orders||[])) {
    if (!o || !o.order_status || o.order_status === "Draft" || o.order_status === "Dibatalkan") continue;
    for (const p of (o.products || [])) {
      // Iter 317 — Item biaya (ongkir/biaya admin) BUKAN item produksi. Tidak masuk
      //   antrean produksi, counter, kebutuhan mockup, maupun syarat penyelesaian.
      //   Nominalnya tetap ada di rincian tagihan nota — tidak dihapus dari data.
      if (isCostItem(p)) continue;
      const stage = p.production_stage || "Perlu Diperiksa";
      cards.push({ order:o, product:p, stage, key:`${o.order_number}::${p.id||p.name}` });
    }
  }
  const currentTab = visibleTabs.find(t => t.key === activeTab) || visibleTabs[0];
  // Iter 44 — kartu paralel: masuk ke tab tim yang match (via PARALLEL_STAGE_TRACKS) selama track-nya belum done.
  // Iter 333 — Ganti parameter dari `tabKey` → `teamName` agar tidak ambigu saat
  //   dua team berbagi stage yang sama (misal "Bordir" key tapi teamName "Bordir"
  //   untuk desainer vs "Operator Bordir" untuk jahit mesin). Track `bordir_design`
  //   wajib hanya cocok untuk team "Bordir".
  const parallelMatch = (card, teamName) => {
    const tracks = PARALLEL_STAGE_TRACKS[card.stage];
    if (!tracks) return false;
    const ps = card.product.parallel_status || {};
    return tracks.some(tr => tr.team === teamName && !ps[`${tr.track}_done`]);
  };
  const cardsInTab = sortByDeadlinePriority(cards.filter(c =>
    (c.stage === (currentTab?.key || "")) ||
    (currentTab?.key === "Perlu Diperiksa" && (!c.product.production_route || c.product.production_route === "Perlu Diperiksa")) ||
    parallelMatch(c, currentTab?.teamName || "")
  ));
  const setRoute = async (card, newRoute) => {
    setBusyKey(card.key);
    try {
      await axios.patch(`${API}/orders/${card.order.order_number}/products/${card.product.id}/route`, { route:newRoute }, { withCredentials:true });
      await onChanged();
    } catch (e) { notify.error({ title:"Gagal menetapkan jalur produksi", message: e.response?.data?.detail || "Terjadi kesalahan saat mengubah jalur produksi." }); }
    finally { setBusyKey(""); }
  };
  const claim = async (card) => {
    setBusyKey(card.key);
    try {
      await axios.post(`${API}/orders/${card.order.order_number}/products/${card.product.id}/claim`, {}, { withCredentials:true });
      await onChanged();
      notify.success(operator?.team === "Mesin Press" ? { title:"Berhasil. Pekerjaan sudah disimpan.", message:"Pekerjaan pindah ke DIKERJAKAN." }
        : { title:"Berhasil mengambil pekerjaan", message:"Pekerjaan sekarang ada di tab Sedang Dikerjakan." });
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail;
      if (status === 409) {
        // Race: sudah di-claim operator lain — refresh supaya UI update
        notify.warning({ title:"Sudah diambil operator lain", message: msg || "Pekerjaan ini sudah diambil operator lain saat Anda menekan tombol. Antrean akan diperbarui." });
        await onChanged();
      } else {
        notify.error({ title: operator?.team === "Mesin Press" ? "Gagal menyimpan. Coba lagi." : "Gagal menerima pekerjaan", message: msg || "Silakan coba lagi." });
      }
    }
    finally { setBusyKey(""); }
  };
  // Iter 351 — Kembalikan ke Antrean (release claim). Operator melepas pekerjaan yang salah ambil.
  //   Endpoint /release memvalidasi kepemilikan di backend; frontend hanya menyediakan UX dialog.
  //   Idempotent via guard atomic. Setelah berhasil: refresh kartu & counter.
  // Bordir: "Batalkan Pekerjaan" wajib alasan + pcs terkerjakan (dialog), lalu kirim ke /release yang sama.
  const release = async (card, cancelBody) => {
    if (card.product?.production_stage === "Bordir" && !cancelBody) { setCancelCards(prev => prev.some(c => c.key === card.key) ? prev : [...prev, card]); return; }
    setBusyKey(card.key);
    try {
      await axios.post(`${API}/orders/${card.order.order_number}/products/${card.product.id}/release`, cancelBody || {}, { withCredentials:true });
      await onChanged();
      notify.success(cancelBody ? { title:"Pekerjaan dibatalkan", message:"Pekerjaan kembali ke antrean dan dapat diambil operator lain. Progres pcs tercatat di riwayat." }
        : { title:"Dikembalikan ke antrean", message:"Pekerjaan ini sudah dilepas dan dapat diambil operator lain." });
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail;
      if (status === 409) {
        notify.warning({ title:"Status pekerjaan berubah", message: msg || "Muat ulang untuk melihat kondisi terbaru." });
        await onChanged();
      } else {
        notify.error({ title:"Gagal mengembalikan ke antrean", message: msg || "Silakan coba lagi." });
      }
    }
    finally { setBusyKey(""); }
  };
  const parallelRelease = async (card, track) => {
    setBusyKey(card.key);
    try {
      await axios.post(`${API}/orders/${card.order.order_number}/products/${card.product.id}/parallel-release`, { track }, { withCredentials:true });
      await onChanged();
      notify.success({ title:"Track dikembalikan ke antrean", message:"Track ini sudah dilepas dan dapat diambil operator lain." });
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail;
      if (status === 409) {
        notify.warning({ title:"Status track berubah", message: msg || "Muat ulang untuk melihat kondisi terbaru." });
        await onChanged();
      } else {
        notify.error({ title:"Gagal mengembalikan track", message: msg || "Silakan coba lagi." });
      }
    }
    finally { setBusyKey(""); }
  };
  // Iter 635 — Desainer Bordir: desain selesai di tahap Bordir = serahkan ke Antrean Operator Bordir (tahap tetap Bordir).
  const designHandover = (card) => setPointsCard({ card, target: { source: "offline", order_number: card.order.order_number, product_id: card.product.id } });
  const submitHandover = async (card, designs) => {
    setBusyKey(card.key);
    try {
      const r = await axios.post(`${API}/orders/${card.order.order_number}/products/${card.product.id}/bordir-design-handover`, { designs }, { withCredentials: true });
      if (r?.data?.state !== "antrean_operator") throw new Error("Penyerahan belum terkonfirmasi. Silakan coba lagi.");
      setPointsCard(null);
      await onChanged();
      notify.success({ title: "Berhasil dikirim", message: `${card.order.order_number} · ${designs.length} desain · ${designs.reduce((a, d) => a + d.titik, 0)} titik · masuk Antrean Operator Bordir.` });
    } finally { setBusyKey(""); }
  };
  const advance = async (card) => {
    if (operator?.team === "Bordir" && card.stage === "Bordir" && String(card.order?.source || "").toLowerCase() !== "shopee") return designHandover(card);
    const route = card.product.production_route;
    const stagesList = ROUTE_STAGES_UI[route] || [];
    const curI = stagesList.indexOf(card.stage);
    const nextStage = curI >= 0 && curI < stagesList.length - 1 ? stagesList[curI+1] : null;
    if (!nextStage) { notify.warning({ title:"Sudah pada tahap akhir", message:"Produk ini tidak dapat dimajukan lagi karena sudah berada di tahap terakhir." }); return; }
    if (operator?.team === "Mesin Press" && !window.confirm("Pekerjaan press ini sudah selesai?")) return;
    setBusyKey(card.key);
    try {
      await axios.patch(`${API}/orders/${card.order.order_number}/products/${card.product.id}/stage`, { stage: nextStage }, { withCredentials:true });
      await onChanged();
      if (operator?.team === "Mesin Press") notify.success({ title:"Berhasil. Pekerjaan sudah disimpan.", message:`Diteruskan ke ${nextStage}.` });
    } catch (e) { notify.error({ title: operator?.team === "Mesin Press" ? "Gagal menyimpan. Coba lagi." : "Gagal memindahkan tahap", message: e.response?.data?.detail || "Silakan coba lagi." }); }
    finally { setBusyKey(""); }
  };
  const parallelDone = async (card, track) => {
    setBusyKey(card.key);
    try {
      await axios.post(`${API}/orders/${card.order.order_number}/products/${card.product.id}/parallel-done`, { track }, { withCredentials:true });
      await onChanged();
    } catch (e) { notify.error({ title:"Gagal menyelesaikan track", message: e.response?.data?.detail || "Silakan coba lagi." }); }
    finally { setBusyKey(""); }
  };
  // Iter 92 — Klaim 1 track paralel (Mulai Tahap untuk stage paralel).
  const parallelClaim = async (card, track) => {
    setBusyKey(card.key);
    try {
      await axios.post(`${API}/orders/${card.order.order_number}/products/${card.product.id}/parallel-claim`, { track }, { withCredentials:true });
      await onChanged();
      notify.success(operator?.team === "Mesin Press" ? { title:"Berhasil. Pekerjaan sudah disimpan.", message:"Pekerjaan pindah ke DIKERJAKAN." }
        : { title:"Berhasil mengambil pekerjaan", message:"Pekerjaan sekarang ada di tab Sedang Dikerjakan." });
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail;
      if (status === 409) {
        notify.warning({ title:"Sudah diambil operator lain", message: msg || "Track ini sudah diambil operator lain. Antrean akan diperbarui." });
        await onChanged();
      } else {
        notify.error({ title:"Gagal memulai track", message: msg || "Silakan coba lagi." });
      }
    }
    finally { setBusyKey(""); }
  };
  // Iter 303 — Tandai Mockup Dicetak (Rido). Aksi terpisah dari ACC Barang Siap.
  const markMockupPrint = async (card) => {
    const key = card.key || `mp::${card.order.order_number}::${card.product.id}`;
    setBusyKey(key);
    try {
      await axios.post(`${API}/orders/${card.order.order_number}/products/${card.product.id}/mockup-print`, {}, { withCredentials:true });
      await onChanged();
      // Iter 332.2 — Ganti modal sukses dengan toast singkat (tanpa tombol Selesai).
      // Kartu langsung pindah dari "Siapkan Barang" ke "Menunggu ACC" via reload di atas.
      toast.success(`✓ Mockup sudah dicetak — masuk Menunggu ACC Adit`);
    } catch (e) {
      notify.error({ title:"Gagal menandai mockup dicetak", message: e?.response?.data?.detail || "Silakan coba lagi." });
    }
    finally { setBusyKey(""); }
  };
  // Iter 303 — ACC Barang Siap (Adit / Rido / Owner). Tidak mengurangi stok.
  //   Backend auto-advance saat SEMUA item di nota di-ACC.
  const markAccReady = async (card) => {
    const key = card.key || `acc::${card.order.order_number}::${card.product.id}`;
    setBusyKey(key);
    try {
      const r = await axios.post(`${API}/orders/${card.order.order_number}/products/${card.product.id}/acc-ready`, {}, { withCredentials:true });
      // Iter 646 — Muat ulang daftar di latar belakang; jangan tahan notifikasi sukses.
      Promise.resolve(onChanged()).catch(() => {});
      const cut = r.data?.acc_stock_cut;
      notify.success({ title:"ACC Barang Siap tercatat", message:"Pelaksana & waktu disimpan terpisah. Tahap otomatis maju bila semua item di nota sudah di-ACC." + (cut?.message ? ` · ${cut.message}` : "") });
    } catch (e) {
      notify.error({ title:"Gagal ACC Barang Siap", message: e?.response?.data?.detail || "Silakan coba lagi." });
    }
    finally { setBusyKey(""); }
  };
  // Iter 309 — Kembalikan ke Mockup dari kartu Riwayat. Alasan WAJIB. Backend menegakkan guard
  //   aktivitas lanjutan; frontend hanya meminta reason dan mengarahkan ke tab Dikerjakan setelah sukses.
  const revertMockup = async (item) => {
    const order = item.order; const product = item.product;
    const key = `rev::${order.order_number}::${product.id}`;
    if (busyKey === key) return; // cegah double-tap
    const reason = await notify.prompt({
      title: "Kembalikan produk ini ke Mockup?",
      message: `${order.order_number} · ${order.customer_name || "—"}\nProduk: ${product.custom_name || product.name}\n\nTahap berikutnya akan ditarik kembali. Pengembalian hanya diizinkan bila belum ada Mockup Dicetak, ACC Barang, atau tim berikutnya mulai bekerja. File mockup tetap tersimpan.`,
      placeholder: "Alasan koreksi (wajib) — mis. salah klik, perlu revisi warna, dll.",
      multiline: true,
      required: true,
      primaryLabel: "Kembalikan ke Mockup",
      secondaryLabel: "Batal",
    });
    if (!reason) return;
    setBusyKey(key);
    try {
      await axios.post(`${API}/orders/${order.order_number}/products/${product.id}/revert-mockup`,
        { reason }, { withCredentials: true });
      await onChanged();
      try { setOpSection("sedang"); } catch { /* noop */ }
      notify.success({ title:"Dikembalikan ke Mockup", message:"Produk kembali di tab 'Sedang Dikerjakan'. File mockup dipertahankan." });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Silakan coba lagi.";
      if (status === 409) notify.warning({ title:"Pengembalian langsung diblokir", message: detail });
      else if (status === 403) notify.error({ title:"Tidak berwenang", message: detail });
      else notify.error({ title:"Gagal mengembalikan", message: detail });
    }
    finally { setBusyKey(""); }
  };
  const adminCorrect = (card) => setCorrectCard(card);
  const submitCorrect = async ({ stage, note }) => {
    if (!correctCard) return;
    setBusyKey(correctCard.key);
    try {
      await axios.patch(`${API}/orders/${correctCard.order.order_number}/products/${correctCard.product.id}/stage`, { stage, note }, { withCredentials:true });
      setCorrectCard(null);
      await onChanged();
    } catch (e) { notify.error({ title:"Gagal koreksi tahap", message: e.response?.data?.detail || "Silakan coba lagi." }); }
    finally { setBusyKey(""); }
  };
  if (visibleTabs.length === 0) {
    // Iter 548 — Tim Sablon/DTF & Mesin Press tetap ditampilkan Shopee panel
    //   meski offline tidak memberi tab (mis. Mesin Press tanpa stage aktif).
    // Iter 550 — Jika Sablon/DTF tanpa tab offline sama sekali, langsung render
    //   panel Shopee (tidak perlu tab Offline/Shopee karena offline kosong).
    if (operator?.team === "Sablon/DTF" || operator?.team === "Mesin Press") {
      return <div className="section" data-testid="production-queue-root">
        <div className="op-antrean-head">
          <h3 className="op-antrean-title">
            {operator?.team === "Mesin Press" ? "Antrean Press" : "Antrean Sablon / DTF"}
          </h3>
          <small className="muted small">
            <strong>{operator?.name}</strong>
            {operator?.team && <> · <strong>{operator.team}</strong></>}
          </small>
        </div>
        {operator?.team === "Sablon/DTF" && <ShopeeSablonPanel operator={operator} />}
        {operator?.team === "Mesin Press" && <PressLiveBar onOfflineChanged={onChanged}/>}
        {operator?.team === "Mesin Press" && <OfflinePressWaiting />}
        {operator?.team === "Mesin Press" && <ShopeePressPanel operator={operator} />}
      </div>;
    }
    return <div className="section"><h3>Antrean produksi</h3><p className="muted small">Anda tidak memiliki tab produksi yang ditugaskan.</p></div>;
  }

  // Iter 550 — Tim Sablon/DTF (Ibnu/Fatir) dgn tab Shopee aktif: render HANYA
  //   tab bar + panel Shopee (tanpa antrean offline di bawah) agar tidak bertumpuk.
  //   Tab OFFLINE tetap tampilkan antrean offline normal via fall-through ke bawah.
  if (isSablonOp && sablonSource === "shopee") {
    return <div className="section" data-testid="production-queue-root">
      <div className="op-antrean-head">
        <h3 className="op-antrean-title">Antrean Sablon / DTF</h3>
        <small className="muted small">
          <strong>{operator?.name}</strong>
          {operator?.team && <> · <strong>{operator.team}</strong></>}
        </small>
      </div>
      <nav className="sablon-source-tabs mpb-tabs" role="tablist" aria-label="Sumber antrean Sablon/DTF" data-testid="sablon-source-tabs">
        <SourceTab channel="offline" active={sablonSource==="offline"} onClick={() => { pickSource("sablon", "offline", setSablonSource); }} testid="sablon-source-tab-offline"/>
        <SourceTab channel="shopee" active={tabOf(sablonSource)==="shopee"} onClick={() => pickSource("sablon", "shopee", setSablonSource)} testid="sablon-source-tab-shopee"/>
        <SourceTab channel="tokopedia" active={tabOf(sablonSource)==="tokopedia"} onClick={() => pickSource("sablon", "tokopedia", setSablonSource)} testid="sablon-source-tab-tokopedia"/>
    </nav>
      <ShopeeSablonPanel operator={operator} />
    </div>;
  }

  // Iter 88 — Operator mode: pisahkan 3 seksi (Antrean Tim / Sedang Dikerjakan / Riwayat Saya)
  // Iter 302 — Gunakan tanggal berjalan di zona Asia/Jakarta agar KPI "Selesai hari ini"
  //   tidak bergeser mengikuti UTC browser. Format "YYYY-MM-DD" (en-CA).
  const todayISO = (() => {
    try {
      return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta", year:"numeric", month:"2-digit", day:"2-digit" }).format(new Date());
    } catch {
      return new Date().toISOString().slice(0,10);
    }
  })();
  const opName = operator?.name || "";
  const opTeamStages = new Set(); // stages yang jadi milik tim operator
  if (operator) {
    for (const t of visibleTabs) opTeamStages.add(t.key);
  }
  // cards yang stage-nya di tim operator
  const teamCards = operator
    ? cards.filter(c => opTeamStages.has(c.stage) || (parallelMatch && visibleTabs.some(t => parallelMatch(c, t.teamName))))
    : [];
  // Antrean Tim: belum di-claim (production_pic kosong untuk stage solo; _claimed_by kosong untuk track paralel).
  const antreanTim = teamCards.filter(c => {
    if (PARALLEL_STAGE_TRACKS[c.stage]) {
      // Iter 92 — parallel: track saya BELUM di-claim & BELUM done
      // Iter 333 — Match via `teamName`, bukan tab `key`, supaya Operator Bordir
      //   (key="Bordir" juga) TIDAK keliru masuk ke track `bordir_design` (team="Bordir").
      const ps = c.product.parallel_status || {};
      const myTrack = (PARALLEL_STAGE_TRACKS[c.stage] || []).find(tr => visibleTabs.some(t => t.teamName === tr.team));
      if (!myTrack) return false;
      if (ps[`${myTrack.track}_done`]) return false;
      const claimedBy = ps[`${myTrack.track}_claimed_by`] || "";
      return !claimedBy; // belum di-claim siapapun
    }
    if (operator?.team === "Bordir" && c.stage === "Bordir" && designHandedOver(c.product)) return false;
    return !c.product.production_pic;
  });
  // Semua Pekerjaan (Operator Bordir): seluruh pekerjaan aktif/menunggu dilanjutkan + nama operator aktif (baca-saja).
  const semuaTim = operator?.team === "Operator Bordir"
    ? teamCards.filter(c => c.stage === "Bordir" && c.product.production_stage === "Bordir" && (c.product.production_pic || c.product.bordir_team?.waiting)
      && String(c.order?.source || "").toLowerCase() !== "shopee")
    : [];
  // Sedang Dikerjakan Saya
  const mineActive = teamCards.filter(c => {
    if (PARALLEL_STAGE_TRACKS[c.stage]) {
      // Iter 92 — parallel: track saya di-claim oleh saya & belum done
      // Iter 333 — Match via `teamName`.
      const ps = c.product.parallel_status || {};
      const myTrack = (PARALLEL_STAGE_TRACKS[c.stage] || []).find(tr => visibleTabs.some(t => t.teamName === tr.team));
      if (!myTrack) return false;
      if (ps[`${myTrack.track}_done`]) return false;
      return (ps[`${myTrack.track}_claimed_by`] || "") === opName;
    }
    if (c.product.production_pic === opName) return true;
    return isSharedBordir(c, operator, opName);
  });
  // Parallel yg operator sudah selesai track-nya (menunggu track lain) → tetap dianggap "mine" tampil di active
  // Riwayat Saya (dari semua orders + history event)
  // Iter 311 — Pengayaan event riwayat:
  //   - Deteksi event REVERT (stage_to="Mockup" + revert_of_event_at) → label "Dikembalikan ke Mockup".
  //   - Deteksi completion yang DIBATALKAN (ada event revert yang menunjuk `at`-nya) → beri flag `canceled`.
  //   - Operator pelaku revert (moved_by) juga melihat event ini di Riwayat-nya.
  // Iter 312 — Riwayat Saya (daftar KARTU "pekerjaan selesai") HANYA memuat event
  //   yang masih berlaku. Event REVERT dan event completion yang DIBATALKAN
  //   TIDAK boleh muncul sebagai kartu di sini — audit trail-nya ada di
  //   Detail → Riwayat Proses (HistoryList).
  let riwayatSaya = [];
  if (operator) {
    for (const o of (orders || [])) {
      if (!o) continue;
      for (const p of (o.products || [])) {
        const hist = p.production_pic_history || [];
        // Set `at` dari completion yang sudah dibatalkan oleh event revert.
        const canceledAts = new Set();
        for (const h of hist) {
          if (h?.revert_of_event_at) canceledAts.add(String(h.revert_of_event_at));
        }
        for (const h of hist) {
          if (!h?.at) continue;
          if (h.handed_over_by !== opName && h.moved_by !== opName) continue;
          const isRevert = !!h.revert_of_event_at && (h.stage_to === "Mockup");
          const isCanceled = canceledAts.has(String(h.at));
          // Iter 312 — Skip event REVERT & CANCELED dari daftar utama Riwayat.
          //   Tetap tersedia di Detail → Riwayat Proses untuk audit.
          if (isRevert || isCanceled) continue;
          riwayatSaya.push({
            order: o,
            product: p,
            event: h,
            // Iter 302 — Snapshot order_status agar RiwayatItem bisa menampilkan
            //   "Menunggu Finalisasi" bila produk sudah masuk stage "Selesai" tapi
            //   nota masih menunggu finalisasi Shella.
            orderStatus: o.order_status || "",
            currentStage: p.production_stage,
            currentPic: p.production_pic,
            parallelStatus: p.parallel_status || {},
            isRevert,
            isCanceled,
          });
        }
        // Iter 304 — Tambahkan event ACC Barang Siap sebagai riwayat (bukan event stage).
        //   Dipakai operator ACC-only (Adit) untuk melihat jejak pekerjaannya.
        if (p.barang_acc_by && p.barang_acc_at && p.barang_acc_by === opName) {
          riwayatSaya.push({
            order: o,
            product: p,
            event: {
              stage_from: "ACC Barang",
              stage_to: "",
              handed_over_by: p.barang_acc_by,
              moved_by: p.barang_acc_by,
              at: p.barang_acc_at,
              note: "ACC Barang Siap",
            },
            orderStatus: o.order_status || "",
            currentStage: p.production_stage,
            currentPic: p.production_pic,
            parallelStatus: p.parallel_status || {},
            isAcc: true,
          });
        }
      }
    }
    // Iter 493 — DEDUP RIWAYAT ADIT OFFLINE: 1 nota = 1 kartu.
    //   Scope WAJIB: hanya Adit (operator.team=Gudang + is_acc_only=true) di
    //   nota Offline (source≠Shopee). Shopee tidak boleh diubah.
    //   Masalah: loop di atas push event ACC per produk → 1 nota dengan N
    //   produk muncul N kali dengan order_number yang sama (duplikat visual).
    //   Solusi: setelah loop, agregasi entry `isAcc=true` per order_number
    //   menjadi 1 entry representatif. Timestamp pakai yang terbaru supaya
    //   urutan sort tetap benar; detail per-produk disimpan di
    //   `accAggregatedProducts` untuk kebutuhan render internal kartu.
    //   Entry lain (non-isAcc, mis. stage events) TIDAK disentuh.
    if (operator?.is_acc_only && operator?.team === "Gudang") {
      const accMap = new Map(); // order_number → aggregated entry
      const nonAcc = [];
      for (const r of riwayatSaya) {
        const srcLower = String(r?.order?.source || "").trim().toLowerCase();
        const isOffline = srcLower !== "shopee";
        if (!r.isAcc || !isOffline) { nonAcc.push(r); continue; }
        const key = r?.order?.order_number;
        if (!key) { nonAcc.push(r); continue; }
        const existing = accMap.get(key);
        if (!existing) {
          accMap.set(key, {
            ...r,
            accAggregatedProducts: [{
              id: r.product?.id,
              name: r.product?.custom_name || r.product?.name,
              quantity: r.product?.quantity,
              at: r.event?.at,
            }],
            accProductCount: 1,
          });
        } else {
          existing.accAggregatedProducts.push({
            id: r.product?.id,
            name: r.product?.custom_name || r.product?.name,
            quantity: r.product?.quantity,
            at: r.event?.at,
          });
          existing.accProductCount = (existing.accProductCount || 1) + 1;
          // Pakai timestamp paling baru supaya sorting akurat.
          if ((r.event?.at || "") > (existing.event?.at || "")) {
            existing.event = { ...existing.event, at: r.event.at };
          }
        }
      }
      riwayatSaya = nonAcc.concat(Array.from(accMap.values()));
    }
    // sort by at desc
    riwayatSaya.sort((a,b) => (b.event.at || "").localeCompare(a.event.at || ""));
  }

  // Iter 359 — Riwayat Pekerjaan untuk tim Mockup Offline.
  //   1 produk = 1 entry (bukan per event). Scope: produk yang pernah/sedang
  //   berada pada stage Mockup DAN operator ini sebagai PIC Mockup (handed_over_by
  //   atau production_pic). Status mengikuti pola Shopee (BELUM_FIX/REVISI/FIX/BATAL)
  //   agar UI reuse-able:
  //     - BATAL  : order_status === "Dibatalkan"
  //     - FIX    : produk sudah PINDAH dari stage "Mockup" (ada stage_to≠Mockup
  //                berikutnya & tidak di-revert) → mockup "lulus" diteruskan.
  //     - REVISI : pernah masuk kembali ke Mockup (revert event stage_to="Mockup").
  //     - BELUM_FIX: masih di stage "Mockup".
  //   Jumlah revisi = jumlah event revert ke Mockup.
  //   `created_at` = waktu pertama kali masuk Mockup; `updated_at` = waktu event
  //   terbaru di jejak Mockup produk.
  const isMockupTeam = !!operator && operator.team === "Mockup";
  const riwayatMockupOffline = [];
  if (isMockupTeam) {
    for (const o of (orders || [])) {
      if (!o) continue;
      for (const p of (o.products || [])) {
        const hist = p.production_pic_history || [];
        if (!hist.length && p.production_pic !== opName && p.production_stage !== "Mockup") continue;
        // Cek keterlibatan operator pada track Mockup.
        const touchedByMe = hist.some(h => (
          (h.handed_over_by === opName || h.moved_by === opName)
          && (h.stage_from === "Mockup" || h.stage_to === "Mockup")
        )) || (p.production_stage === "Mockup" && p.production_pic === opName);
        if (!touchedByMe) continue;
        // Hitung event revert ke Mockup (= revisi) & deteksi "FIX".
        let revisionCount = 0;
        let firstMockupAt = null;
        let lastEventAt = null;
        for (const h of hist) {
          if (!h?.at) continue;
          if (h.stage_from === "Mockup" || h.stage_to === "Mockup") {
            if (!firstMockupAt || h.at < firstMockupAt) firstMockupAt = h.at;
            if (!lastEventAt || h.at > lastEventAt) lastEventAt = h.at;
          }
          if (h.revert_of_event_at && h.stage_to === "Mockup") revisionCount += 1;
        }
        const currentStage = p.production_stage || "";
        const isCancelled = (o.order_status || "").toLowerCase() === "dibatalkan";
        const stillMockup = currentStage === "Mockup";
        const movedForward = !stillMockup && currentStage && currentStage !== "" && !isCancelled;
        let status = "BELUM_FIX";
        if (isCancelled) status = "BATAL";
        else if (stillMockup && revisionCount > 0) status = "REVISI";
        else if (stillMockup) status = "BELUM_FIX";
        else if (movedForward) status = "FIX";
        const next = movedForward
          ? { label: `Diteruskan ke ${currentStage}`, cls: "riw-chip-success" }
          : stillMockup
            ? { label: "Masih di Mockup", cls: "riw-chip-neutral" }
            : isCancelled
              ? { label: "Dibatalkan", cls: "riw-chip-danger" }
              : { label: "—", cls: "riw-chip-neutral" };
        // Iter 366 — Aturan Riwayat Mockup:
        //   Antrean    = pekerjaan yang belum dimulai (handled oleh queue groupings).
        //   Dikerjakan = pekerjaan masih di stage "Mockup" (BELUM_FIX / REVISI).
        //   Riwayat    = HANYA pekerjaan yang SUDAH keluar dari stage Mockup, yaitu
        //                FIX (stage sudah pindah ke tahap berikutnya) atau BATAL.
        //   Produk yang masih di Mockup tidak boleh masuk Riwayat.
        if (status !== "FIX" && status !== "BATAL") continue;
        riwayatMockupOffline.push({
          id: `${o.order_number}:${p.id || p.product_id || p.product_name || ""}`,
          order_number: o.order_number,
          customer: o.customer_name || "",
          pic_name: p.production_pic || opName,
          pic_id: p.production_pic || opName,
          job_label: p.product_name || p.product_type || "—",
          status,
          created_at: firstMockupAt || o.created_at || null,
          updated_at: lastEventAt || p.updated_at || o.updated_at || firstMockupAt || null,
          revision_count: revisionCount,
          is_fix: status === "FIX",
          next_label: next.label,
          next_cls: next.cls,
          is_cancelled: isCancelled,
          cancel_reason: o.cancel_reason || "",
          extra_label: p.product_type || "",
          // Iter 536 — Jenis pengerjaan per-item (dialirkan ke RiwayatPekerjaanCard)
          process_mode: p.process_mode || "",
          order_type: o.order_type || "",
          custom_type: o.custom_type || "",
          // Iter 499 — Simpan referensi minimal order untuk fallback preview
          //   bila order tidak lagi ada di `orders` prop aktif (mis. sudah
          //   dipindah ke stage selanjutnya & list dibatasi scope).
          _raw: { is_repeat_order: !!o.is_repeat_order, repeat_ref: o.repeat_ref || "" },
        });
      }
    }
    riwayatMockupOffline.sort((a, b) => (
      new Date(b.updated_at || b.created_at || 0).getTime()
      - new Date(a.updated_at || a.created_at || 0).getTime()
    ));
  }

  // KPI
  const kpi = {
    menunggu: 0, dikerjakan: 0, hampir: 0, terlambat: 0, selesai_today: 0,
    // Iter 287ax — Operator mode: pisahkan "nota" vs "produk". Ringkasan Antrean/Dikerjakan
    // pakai nota count (sesuai hasil groupTeamCards yang dirender di daftar).
    menunggu_nota: 0, dikerjakan_nota: 0, menunggu_produk: 0, dikerjakan_produk: 0,
  };
  if (operator) {
    const tabKey = visibleTabs?.[0]?.key || "";
    // Nota count = jumlah group (per order_number + team) — sama dengan jumlah kartu di daftar.
    kpi.menunggu_nota = groupTeamCards(antreanTim, tabKey, opName).length;
    kpi.dikerjakan_nota = groupTeamCards(mineActive, tabKey, opName).length;
    kpi.menunggu_produk = antreanTim.length;
    kpi.dikerjakan_produk = mineActive.length;
    // Default `menunggu`/`dikerjakan` ikut jumlah nota agar konsisten dengan daftar & tab badge.
    kpi.menunggu = kpi.menunggu_nota;
    kpi.dikerjakan = kpi.dikerjakan_nota;
    // Iter 287at — Hampir/Terlambat dihitung dari seluruh pekerjaan aktif tim operator
    // (antrean + sedang dikerjakan) agar konsisten dengan badge yang tampil di kartu.
    for (const c of [...antreanTim, ...mineActive]) {
      if (c.product.deadline_status === "Terlambat") kpi.terlambat += 1;
      if (c.product.deadline_status === "Hampir Deadline") kpi.hampir += 1;
    }
    kpi.selesai_today = riwayatSaya.filter(r => r.event.at && r.event.at.startsWith(todayISO)).length;
    // Iter 332.5 — Badge Riwayat untuk operator (dipakai tab Mockup/Mia).
    //   Iter 381 — Untuk team Mockup, counter dihitung dari UNIQUE nota di
    //   `riwayatMockupOffline` (yang di-render MockupRiwayatPane setelah
    //   grouping), bukan dari total event `riwayatSaya`. Dengan ini badge
    //   dan jumlah kartu di list sinkron (1 nota = 1 kartu = 1 hitungan).
    //   Team non-Mockup tetap pakai total entri riwayatSaya.
    if (isMockupTeam) {
      const uniqOrders = new Set(riwayatMockupOffline.map(r => r.order_number).filter(Boolean));
      kpi.riwayat_nota = uniqOrders.size;
    } else {
      // Iter 398 — Riwayat per NOTA UNIK (bukan per event). RiwayatSaya list
      //   dirender grouped by order_number (lihat line 1709 — "1 nota = 1 kartu"),
      //   jadi counter juga wajib dedup via order_number agar konsisten dengan
      //   jumlah kartu di tab Riwayat. Sebelum ini `kpi.riwayat_nota = riwayatSaya.length`
      //   menghitung activity row sehingga Operator Bordir dgn 1 nota + 3 produk
      //   terbaca 3, padahal daftar hanya menampilkan 1 kartu nota.
      const uniq = new Set();
      for (const r of riwayatSaya) {
        const n = r?.order?.order_number;
        if (n) uniq.add(n);
      }
      kpi.riwayat_nota = uniq.size;
    }

    // Iter 332.6 — Counter tab Gudang/Rido (Offline only — Shopee punya UI terpisah).
    //   Count dihitung ulang dari `orders` yang sudah tersedia; predikat MIRROR dari
    //   komponen PenyiapanBarang, AccBarangList, GudangSelesaiList agar konsisten
    //   dengan daftar yang dirender. Tidak menyentuh backend/state machine.
    if (operator?.team === "Gudang") {
      let gPrep = 0, gAcc = 0, gSelesai = 0;
      const seenPrep = new Set(), seenAcc = new Set(), seenSel = new Set();
      const opN = operator?.name || "";
      for (const o of (orders || [])) {
        if (!o) continue;
        const inactiveOrder = o.order_status === "Draft" || o.order_status === "Dibatalkan";
        const closedOrder = o.order_status === "Selesai" || o.order_status === "Menunggu Finalisasi";
        const isOffline = String(o.source || "").trim().toLowerCase() !== "shopee";
        for (const p of (o.products || [])) {
          const stage = p.production_stage || "";
          const tracks = PARALLEL_STAGE_TRACKS[stage];
          const gTrack = tracks ? tracks.find(tr => tr.team === "Gudang") : null;
          const inGudangScope = stage === "Gudang" || !!gTrack;
          // Siapkan Barang: offline, dalam scope Gudang, belum cetak mockup, order aktif.
          if (isOffline && inGudangScope && !inactiveOrder && !closedOrder && !p.mockup_print_at) {
            if (stage === "Gudang") {
              const k = `${o.order_number}::${p.id || p.name}`;
              if (!seenPrep.has(k)) { seenPrep.add(k); gPrep++; }
            } else if (gTrack) {
              const ps = p.parallel_status || {};
              if (!ps[`${gTrack.track}_done`]) {
                const k = `${o.order_number}::${p.id || p.name}::${gTrack.track}`;
                if (!seenPrep.has(k)) { seenPrep.add(k); gPrep++; }
              }
            }
          }
          // Menunggu ACC: offline, dalam scope Gudang, mockup sudah dicetak & belum di-ACC, order aktif.
          // Iter 393 — Counter MENGHITUNG NOTA UNIK (bukan per produk) supaya selaras dengan
          //   kartu grouped 1-nota-1-kartu. Variasi/size/warna berbeda tidak menambah counter.
          if (isOffline && inGudangScope && !inactiveOrder && !closedOrder
              && p.mockup_print_at && !p.barang_acc_at) {
            const k = o.order_number;
            if (!seenAcc.has(k)) { seenAcc.add(k); gAcc++; }
          }
          // Selesai Hari Ini: event history hari ini dengan handed_over_by=operator
          //   dan event mewakili keluar dari Gudang (single stage_from===Gudang atau track===gudang).
          for (const h of (p.production_pic_history || [])) {
            if (!h?.at || !h.at.startsWith(todayISO || "")) continue;
            if (h.handed_over_by !== opN) continue;
            const single = h.stage_from === "Gudang";
            const paralel = h.track === "gudang";
            if (!single && !paralel) continue;
            const mode = paralel ? "parallel" : "single";
            const k = `${o.order_number}::${p.id || p.name}::${mode}`;
            if (seenSel.has(k)) continue;
            seenSel.add(k);
            gSelesai++;
          }
        }
      }
      kpi.gudang_prep = gPrep;
      kpi.gudang_acc = gAcc;
      kpi.gudang_selesai = gSelesai;
      kpi.gudang_riwayat = riwayatSaya.length;
    }
  } else {
    // Admin mode: KPI lama (untuk currentTab)
    const cardsForCurrentTab = cards.filter(c =>
      (c.stage === (currentTab?.key || "")) ||
      (currentTab?.key === "Perlu Diperiksa" && (!c.product.production_route || c.product.production_route === "Perlu Diperiksa")) ||
      parallelMatch(c, currentTab?.key || "")
    );
    for (const c of cardsForCurrentTab) {
      const dl = c.product.deadline_status;
      const pic = c.product.production_pic;
      if (dl === "Terlambat") kpi.terlambat += 1;
      if (dl === "Hampir Deadline") kpi.hampir += 1;
      if (pic) kpi.dikerjakan += 1; else kpi.menunggu += 1;
    }
    const tabStage = currentTab?.key || "";
    for (const o of (orders||[])) {
      if (!o || o.order_status === "Draft" || o.order_status === "Dibatalkan") continue;
      for (const p of (o.products || [])) {
        // Iter 311 — Set `at` completion yang telah dibatalkan oleh revert.
        const canceledAts = new Set();
        for (const h of (p.production_pic_history || [])) {
          if (h?.revert_of_event_at) canceledAts.add(String(h.revert_of_event_at));
        }
        for (const h of (p.production_pic_history || [])) {
          if (!h?.at || !h.at.startsWith(todayISO)) continue;
          if (h.stage_from !== tabStage) continue;
          // Jangan hitung event revert (stage_to="Mockup" + revert_of_event_at).
          if (h.revert_of_event_at && h.stage_to === "Mockup") continue;
          // Jangan hitung completion yang telah dibatalkan.
          if (canceledAts.has(String(h.at))) continue;
          kpi.selesai_today += 1;
        }
      }
    }
  }

  // Iter 314 — Scope UI khusus dashboard tim Mockup (Mia dkk). Rapihkan tampilan HP
  //   tanpa menyentuh CSS global divisi lain. Dipakai sebagai kelas tambahan di root
  //   `.section` dan diteruskan ke kartu produk melalui className.
  const isMockupDashboard = !!operator && operator.team === "Mockup";
  const isPress = operator?.team === "Mesin Press";
  const pressOffLateNota = isPress ? groupTeamCards([...antreanTim, ...mineActive].filter(c => c.product.deadline_status === "Terlambat"),
    visibleTabs?.[0]?.key || "", opName).length : 0;
  const SHP_UT = { siap: "siap_press", dik: "dikerjakan", wait: "menunggu", hist: "riwayat", hold: "ditahan" };
  const OFF_UT = { siap: "antrean", dik: "sedang", wait: "menunggu", hist: "riwayat" };
  const pressUT = pressCh === "shopee" ? (Object.keys(SHP_UT).find(k => SHP_UT[k] === pressTab) || "siap")
    : (Object.keys(OFF_UT).find(k => OFF_UT[k] === opSection) || "siap");
  const setPressUT = (k) => { if (pressCh === "shopee") setPressTab(SHP_UT[k]); else setOpSection(OFF_UT[k] || "antrean"); };

  return <div className={`section ${isMockupDashboard ? "op-queue-mockup" : ""}`} data-team={operator?.team || ""} data-section={opSection} data-testid="production-queue-root">
    {cancelCards[0] && <BordirCancelDialog key={cancelCards[0].key} label={`${cancelCards[0].order.order_number} · ${cancelCards[0].product.name || ""}`}
      maxPcs={Number(cancelCards[0].product.quantity) || 0} onClose={() => setCancelCards(prev => prev.slice(1))}
      onSubmit={async (body) => { await release(cancelCards[0], body); setCancelCards(prev => prev.slice(1)); }}/>}
    {pointsCard && <DesignPointsDialog target={pointsCard.target} label={`${pointsCard.card.order.order_number} · ${pointsCard.card.product.name || ""}`}
      onSubmit={(designs) => submitHandover(pointsCard.card, designs)} onClose={() => setPointsCard(null)} />}
    {/* Iter 86 — Header berbeda untuk operator (compact "Antrean Saya") vs admin (tab per tim) */}
    {operator ? (
      <div className="op-antrean-head" data-testid="op-antrean-head">
        <div className="op-antrean-head-left">
          <h3 className="op-antrean-title" data-testid="op-antrean-title">
            {/* Iter 287ar — Title dinamis per tim: "Antrean Press", "Antrean Bordir", dst.
                Pola: drop prefix "Mesin " untuk Mesin Press agar ringkas. Gudang/Selesai pakai label dasar. */}
            {(() => {
              const t = currentTab?.label || operator.team || "";
              if (!t) return "Antrean Saya";
              if (t === "Mesin Press") return "Pekerjaan Press";
              if (operator.team === "Gudang") return `Gudang — ${operator.name}`;
              return `Antrean ${t}`;
            })()}
          </h3>
          {operator.team !== "Gudang" && <span className="op-antrean-sub muted small" data-testid="op-antrean-sub">
            <span className="op-antrean-meta">
              <strong>{operator.name}</strong>
              {/* Iter 333.2 — Tampilkan nama TEAM operator (bukan label tab), supaya
                  "Operator Bordir" tampil utuh (sebelumnya terbaca "Bordir" karena
                  tab key tetap "Bordir").
                  Iter 379 — Map team "Bordir" (tim desainer DST, bukan Operator Bordir
                  fisik) ke display label "Desain Bordir". Key internal tidak diubah. */}
              {operator.team && <> · <strong>{operator.team === "Bordir" ? "Desain Bordir" : operator.team}</strong></>}
            </span>
          </span>}
        </div>
        {isGudangOp && !isAccOnly && <RidoHeaderMenu page={gudangPage} setPage={setGudangPage} onScan={()=>setScanOpen(true)} onPantau={()=>onOpenPantau?.({ src: gudangSource, section: opSection,
          extra: <GudangOverviewPanel operator={operator} orders={orders} todayISO={todayISO} teamCards={teamCards}/> })}/>}
        {!(isGudangOp && !isAccOnly) && <button
          type="button"
          className="op-scan-btn"
          data-testid="operator-scan-barcode-btn"
          onClick={()=>setScanOpen(true)}
          aria-label="Scan Pindah Tahap"
          title="Scan Pindah Tahap"
        >
          <Camera size={14} aria-hidden="true"/>
          <span className="op-scan-btn-text">Scan Pindah Tahap</span><span className="op-scan-btn-short" aria-hidden="true">Pindah</span>
        </button>}
        <button
          type="button"
          className="op-lock-link"
          data-testid="operator-lock-btn"
          onClick={onOperatorLogout}
          aria-label="Kunci sesi operator"
          title="Kunci sesi"
        >
          <Lock size={14} aria-hidden="true"/>
          <span className="op-lock-link-text">Kunci</span>
        </button>
      </div>
    ) : (<>
        <div className="pad-head">
          <h3>Antrean Produksi Offline</h3>
          <span className="pad-total" data-testid="production-active-count"><b>{cards.filter(c=>c.stage!=="Selesai").length}</b> produk aktif</span>
        </div>
        <details className="pad-info" data-testid="production-info">
          <summary>Informasi</summary>
          <p>Setiap divisi menampilkan pekerjaan timnya. Kartu berpindah otomatis ke divisi berikutnya saat tahap diselesaikan. Draft/Dibatalkan tidak tampil. Pembayaran tidak berubah. Angka: produk = item produk aktif, nota = nomor nota unik.</p>
        </details>
      </>
    )}

    {/* Iter 548 — Panel Shopee Sablon/DTF utk tim Sablon/DTF (Ibnu/Fatir).
        Iter 550 — Gunakan TAB utama Offline/Shopee di atas; render Shopee panel
        HANYA di tab Shopee (via early-return di atas). Di tab Offline, tampilkan
        tab bar supaya Ibnu bisa pindah ke Shopee. */}
    {isSablonOp && (
      <nav className="sablon-source-tabs mpb-tabs" role="tablist" aria-label="Sumber antrean Sablon/DTF" data-testid="sablon-source-tabs">
        <SourceTab channel="offline" active={sablonSource==="offline"} onClick={() => { pickSource("sablon", "offline", setSablonSource); }} testid="sablon-source-tab-offline"/>
        <SourceTab channel="shopee" active={tabOf(sablonSource)==="shopee"} onClick={() => pickSource("sablon", "shopee", setSablonSource)} testid="sablon-source-tab-shopee"/>
        <SourceTab channel="tokopedia" active={tabOf(sablonSource)==="tokopedia"} onClick={() => pickSource("sablon", "tokopedia", setSablonSource)} testid="sablon-source-tab-tokopedia"/>
    </nav>
    )}

    {/* Iter 548 — Panel Shopee Press utk tim Mesin Press (Haikal).
        Backend endpoint: /api/shopee/press/queue. Join gate dua jalur.
        Mesin Press tidak punya aliran Offline → tidak pakai tab Offline/Shopee. */}
    {isPress && <PressLiveBar onOfflineChanged={onChanged}/>}
    {isPress && <PressChannelNav ch={tabOf(pressCh)} setCh={(c) => { setPressLate(false); pickSource("press", c, setPressCh); }}/>}
    {isPress && pressCh === "shopee" && <TokopediaBanner/>}
    {isPress && <PressTabs tab={pressUT} setTab={setPressUT} lateOn={pressLate} setLateOn={(x) => { setPressLate(x); if (x && pressCh === "offline") setOpSection(antreanTim.some(c => c.product.deadline_status === "Terlambat") ? "antrean" : "sedang"); }}
      counts={pressCh === "shopee" ? (pressShp && !pressShp.error ? { siap: pressShp.siap, dik: pressShp.dik, wait: pressShp.menunggu, late: pressShp.late, hold: pressShp.ditahan } : {})
        : { siap: kpi.menunggu_nota, dik: kpi.dikerjakan_nota, wait: pressOffWait ?? undefined, late: pressOffLateNota }}/>}
    {isPress && <div style={{ display: pressCh === "shopee" ? "block" : "none" }}>
      <ShopeePressPanel operator={operator} hideTabs tab={pressTab} setTab={setPressTab} lateOnly={pressLate && pressCh === "shopee"} onClearLate={() => setPressLate(false)}/>
    </div>}

    {/* Iter 262 — Page-level toggle utk operator Gudang/Rido: Pesanan Gudang vs Stok Barang
        Iter 287ap — Reskin: underline tabs (teks + garis bawah emas) bukan blok besar. */}
    {isGudangOp && gudangPage === "stok" && !isAccOnly && (
      <button type="button" className="soft-btn rido-back-btn" onClick={()=>setGudangPage("pesanan")} data-testid="op-gudang-back-pesanan">
        ← Kembali ke Pesanan Gudang</button>
    )}
    {isGudangOp && gudangPage === "stok" && !isAccOnly ? (
      <StockModule user={null} operator={operator} onOpenOrder={(no)=>{
        const o = (orders||[]).find(x => x.order_number === no);
        if (o) onOpen(o);
      }}/>
    ) : (<></>)}

    {(!isGudangOp || gudangPage === "pesanan") && <>
    {/* Iter 287ae/398 — Sub-toggle Offline | Shopee untuk tim Desain Bordir
        (desainer DST) DAN Operator Bordir fisik. Keduanya butuh akses ke modul
        Shopee: desainer melihat antrean desain + action; operator fisik melihat
        kartu yang desainnya sudah selesai (bordir_state=design_done). */}
    {/* Iter 609 — Dashboard Performa = milik akun Operator Bordir (gabungan Shopee+Offline): di ATAS toggle sumber,
        satu instance, tidak di-unmount saat pindah Offline↔Shopee (filter & data tetap). */}
    {isBordirOperatorOp && <LiveQueueBar team="Operator Bordir" onOfflineChanged={onChanged} tid="bordir-op-live"
      events={{ shopee: "bordir-op-queue-changed", tasks: "bordir-op-tasks-changed" }}/>}
    {isBordirOperatorOp && <OperatorBordirPerformance compact/>}
    {isBordirOp && <DesainTodayCard operator={operator}/>}
    {(isBordirOp || isBordirOperatorOp) && <nav className="op-gudang-source mpb-tabs" role="tablist" aria-label="Sumber antrean bordir" data-testid="op-bordir-source">
      <SourceTab channel="offline" active={bordirSource==="offline"} onClick={() => { pickSource("bordir", "offline", setBordirSource); }} testid="op-bordir-source-offline"/>
      <SourceTab channel="shopee" active={tabOf(bordirSource)==="shopee"} onClick={() => pickSource("bordir", "shopee", setBordirSource)} testid="op-bordir-source-shopee"/>
      <SourceTab channel="tokopedia" active={tabOf(bordirSource)==="tokopedia"} onClick={() => pickSource("bordir", "tokopedia", setBordirSource)} testid="op-bordir-source-tokopedia"/>
    </nav>}
    {(isBordirOp || isBordirOperatorOp) && bordirSource === "shopee" && <TokopediaBanner/>}

    {isBordirOp && bordirSource === "shopee" ? (
      // Iter 466 — Bungkus seluruh tab Shopee di dalam ErrorBoundary agar
      //   runtime error di salah satu section tidak membuat halaman blank.
      // Iter 539 — Desainer Bordir (team="Bordir", mis. Rizal/Wawan) HANYA
      //   melihat antrean DESAIN BORDIR. Panel Antrean Operator Bordir Shopee
      //   di-UNMOUNT total (judul/counter/tab/kartu/scan + request operator-queue)
      //   supaya tidak keliru melihat pekerjaan tim fisik. Pemisahan berdasarkan
      //   identitas team operator (bukan hardcode nama) — kompatibel dgn Rizal,
      //   Wawan, dan desainer baru yang di-assign team="Bordir".
      <ShopeeErrorBoundary label="antrean Desain Bordir Shopee" testId="bordir-shopee-section-boundary">
        <ShopeeEmbroiderySection user={{ role: "operator", team: "Bordir", name: operator?.name }}/>
      </ShopeeErrorBoundary>
    ) : isBordirOperatorOp && bordirSource === "shopee" ? (
      // Iter 398 — Operator Bordir fisik: HANYA operator-queue, tidak dapat
      // melihat antrean desain (ShopeeEmbroiderySection) atau menjalankan action
      // start-designing / design-done milik desainer.
      <ShopeeErrorBoundary label="antrean Operator Bordir Shopee" testId="bordir-op-shopee-section-boundary">
        <ShopeeBordirOperatorSection user={{ role: "operator", team: "Operator Bordir", name: operator?.name }}/>
      </ShopeeErrorBoundary>
    ) : (<>
    {/* Iter 287h — Sub-toggle Offline | Shopee di dalam Pesanan Gudang (Rido).
        Data Shopee tetap memakai koleksi shopee_jobs — TIDAK dibuat jadi nota offline,
        TIDAK masuk omzet/piutang, TIDAK mengurangi stok.
        Iter 287ap — Reskin: segmented control compact (emas Offline, oranye Shopee).
        Iter 331 — Juga tampil utk Adit (ACC-only) agar dapat memilih sumber ACC;
        filter sub-source di dalam tab Menunggu ACC dihapus. */}
    {isAccOnly && <AccFastScan onOfflineChanged={onChanged}/>}
    {isGudangOp && !isAccOnly && <GlobalFindButton open={opSection === "monitor"} onClick={() => setOpSection(opSection === "monitor" ? "prep" : "monitor")}/>}
    {isGudangOp && !(opSection === "monitor" && !isAccOnly) && <nav className="op-wh-source mpb-tabs" role="tablist" aria-label="Sumber antrean gudang" data-testid="op-gudang-source">
      <SourceTab channel="offline" active={gudangSource==="offline"} onClick={() => { if (opSection==="direct" || opSection==="stock") setOpSection("prep"); pickSource("gudang", "offline", setGudangSource); }} testid="op-gudang-source-offline"/>
      <SourceTab channel="shopee" active={tabOf(gudangSource)==="shopee"} onClick={() => pickSource("gudang", "shopee", setGudangSource)} testid="op-gudang-source-shopee"/>
      <SourceTab channel="tokopedia" active={tabOf(gudangSource)==="tokopedia"} onClick={() => pickSource("gudang", "tokopedia", setGudangSource)} testid="op-gudang-source-tokopedia"/>
    </nav>}
    {isGudangOp && !isAccOnly && opSection !== "monitor" && <div className="rido-ship-recap" data-testid="rido-ship-recap"><ShipTodayPdfButton compact/></div>}

    {/* Iter 287h — Kalau Rido pilih Shopee, seluruh area Offline (KPI, tab, riwayat)
        digantikan oleh antrean Shopee. Semua pencarian/filter/ringkasan/riwayat
        mengikuti sumber ini — tidak digabung dengan Offline.
        Iter 331 — Adit (ACC-only) juga mendapat toggle sumber; saat pilih Shopee,
        langsung masuk ke antrean ACC Shopee (bukan seluruh ShopeeWarehouseSection
        yang mencakup Siapkan Barang, karena Adit tidak berwenang di sana). */}
    {isGudangOp && !isAccOnly && opSection === "monitor" ? (
      <GlobalFind operator={operator} onClose={() => setOpSection("prep")}/>
    ) : isGudangOp && gudangSource === "shopee" ? (
      /* Iter 497 — ENTRY POINT PANTAU PRODUKSI DI TAB SHOPEE.
         Iter 520 — SAMAKAN STRUKTUR DENGAN OFFLINE: dua tile sejajar
         (Cetak Mockup + Pantau Produksi). Reuse komponen existing —
         `ShopeeWarehouseSection` tetap untuk flow cetak mockup Shopee
         (Belum/Sudah Dicetak, Preview, pencarian, proses Gudang) dan
         `ProductionMonitoring source="shopee"` untuk monitoring lintas
         divisi. Hanya satu halaman tampil pada satu waktu: `opSection`
         ∈ {"prep" (Cetak Mockup), "monitor" (Pantau Produksi)}. */
      <>
        {!isAccOnly && (
          <RidoShopeeTiles opSection={opSection} setOpSection={setOpSection}/>
        )}
        {opSection === "stock" && !isAccOnly ? (
          <ShopeeErrorBoundary label="Pengecekan Stok Mockup" testId="stock-check-section-boundary">
            <StockCheckBoard/>
          </ShopeeErrorBoundary>
        ) : opSection === "direct" && !isAccOnly ? (
          <ShopeeErrorBoundary label="antrean Pesanan Tanpa Mockup" testId="direct-shopee-section-boundary">
            <ShopeeDirectWarehouseSection/>
          </ShopeeErrorBoundary>
        ) : isAccOnly ? (
          <ShopeeAccWaitingSection user={{ role: "operator", team: "Gudang", name: operator?.name }} operator={operator}/>
        ) : (
          <ShopeeWarehouseSection user={{ role: "operator", team: "Gudang", name: operator?.name }} hideCancelledToggle/>
        )}
      </>
    ) : (<>
    {/* Prod tabs — hanya tampil di mode admin ATAU operator dengan >1 tim (jarang) */}
    {(!operator || visibleTabs.length > 1) && (() => {
      const tabCounts = visibleTabs.map(t => {
          let count, notaCount;
          if (t.isCompletion) {
            // Iter 248 — Selesai tab scope = order_status="Menunggu Finalisasi" (bucket aktif),
            //   selaras dg dashboard Finalisasi. produk = total item dlm nota, nota = unique.
            const finOrders = (orders || []).filter(o => o?.order_status === "Menunggu Finalisasi");
            notaCount = finOrders.length;
            count = finOrders.reduce((s, o) => s + ((o.products || []).length || 0), 0);
          } else {
            const cardsForTab = cards.filter(c => {
              if (c.stage === t.key) return true;
              const tracks = PARALLEL_STAGE_TRACKS[c.stage];
              if (!tracks) return false;
              const psx = c.product.parallel_status || {};
              return tracks.some(tr => tr.team === t.key && !psx[`${tr.track}_done`]);
            });
            count = cardsForTab.length;
            // Iter 246 v2 — Dual count: X produk · Y nota (unique grouped work-card).
            notaCount = groupTeamCards(cardsForTab, t.key, opName).length;
          }
          return { t, count, notaCount };
      });
      if (!operator) return <AdminDivisionGrid items={tabCounts} active={activeTab} onPick={setActiveTab} loaded={(orders || []).length > 0}/>;
      return <div className="prod-tabs" data-testid="prod-tabs" role="tablist">
        {tabCounts.map(({ t, count, notaCount }) => {
          return <button type="button" key={t.key} role="tab" data-testid={`prod-tab-${t.key.replaceAll(" ","-").replaceAll("/","-")}`} data-stage={t.key.toLowerCase().replaceAll(" ","-").replaceAll("/","-")} className={`prod-tab ${activeTab===t.key?"active":""}`} onClick={()=>setActiveTab(t.key)}>
            <span className="prod-tab-name">{t.label}</span>
            <span className="prod-tab-team muted small">{t.team}</span>
          <span className="prod-tab-count" data-testid={`prod-tab-count-${t.key.replaceAll(" ","-").replaceAll("/","-")}`} title={`${count} produk · ${notaCount} nota`}>
            <strong>{count}</strong> produk · <strong>{notaCount}</strong> nota
          </span>
        </button>;
      })}
      </div>;
    })()}

    {/* Iter 88 — KPI bar operator (labels update: Belum Dikerjakan / Sedang Dikerjakan)
        Iter 142 — Gudang menyembunyikan KPI bar luar untuk hindari kebingungan dengan
        ringkasan Penyiapan Barang yang cakupannya berbeda.
        Iter 247 — Tab Selesai (Shella / admin) pakai dashboard sendiri, sembunyikan KPI umum.
        Iter 287ar — Operator team non-Gudang: tampilan simplified 3-stat (Antrean, Dikerjakan, Selesai Hari Ini)
          + indikator kecil "Hampir deadline X · Terlambat X". Admin tetap pakai KPI bar penuh. */}
    {operator?.team !== "Gudang" && operator?.team !== "Selesai" && operator?.team !== "Mockup" && !isPress && !currentTab?.isCompletion && (
      operator ? (
        <div className="op-stats-row" data-testid="op-stats-row" role="group" aria-label="Statistik antrean">
          <button type="button" className={`op-stat ${opSection==="antrean"?"active":""}`}
            onClick={()=>setOpSection("antrean")} data-testid="op-stat-antrean">
            <span className="op-stat-label">Antrean</span>
            <span className="op-stat-value op-stat-value-plain" data-testid="op-stat-antrean-value">{kpi.menunggu_nota}</span>
            {/* Iter 287ax — Caption "N nota · M produk" hanya saat produk ≠ nota.
                Jumlah pcs tetap terpisah (ditampilkan di body kartu, tidak di sini). */}
            {kpi.menunggu_produk !== kpi.menunggu_nota && (
              <span className="op-stat-sub" data-testid="op-stat-antrean-sub">
                {kpi.menunggu_nota} nota · {kpi.menunggu_produk} produk
              </span>
            )}
          </button>
          <button type="button" className={`op-stat ${opSection==="sedang"?"active":""}`}
            onClick={()=>setOpSection("sedang")} data-testid="op-stat-dikerjakan">
            <span className="op-stat-label">Dikerjakan</span>
            <span className="op-stat-value op-stat-value-active" data-testid="op-stat-dikerjakan-value">{kpi.dikerjakan_nota}</span>
            {kpi.dikerjakan_produk !== kpi.dikerjakan_nota && (
              <span className="op-stat-sub" data-testid="op-stat-dikerjakan-sub">
                {kpi.dikerjakan_nota} nota · {kpi.dikerjakan_produk} produk
              </span>
            )}
          </button>
          <button type="button" className={`op-stat ${opSection==="riwayat"?"active":""}`}
            onClick={()=>setOpSection("riwayat")} data-testid="op-stat-selesai">
            <span className="op-stat-label">Selesai hari ini</span>
            <span className="op-stat-value op-stat-value-done">{kpi.selesai_today}</span>
          </button>
          <div className="op-stats-meta" data-testid="op-stats-meta" aria-live="polite" style={opSection==="riwayat"?{display:"none"}:undefined}>
            {/* Iter 362 — Indikator Hampir deadline / Terlambat hanya relevan untuk
                pekerjaan aktif (Antrean/Dikerjakan). Sembunyikan di tab Riwayat. */}
            <span className={kpi.hampir>0?"op-stats-meta-warn":""}>Hampir deadline <b>{kpi.hampir}</b></span>
            <span className="op-stats-meta-dot" aria-hidden="true">·</span>
            <span className={kpi.terlambat>0?"op-stats-meta-late":""}>Terlambat <b>{kpi.terlambat}</b></span>
          </div>
        </div>
      ) : (
        <div className="op-kpi-bar" data-testid="op-kpi-bar" aria-label="Ringkasan antrean tim">
          <button type="button" className="op-kpi op-kpi-wait" data-testid="op-kpi-menunggu">
            <span className="op-kpi-label">Menunggu</span>
            <span className="op-kpi-value">{kpi.menunggu}</span>
          </button>
          <button type="button" className="op-kpi op-kpi-active" data-testid="op-kpi-dikerjakan">
            <span className="op-kpi-label">Dikerjakan</span>
            <span className="op-kpi-value">{kpi.dikerjakan}</span>
          </button>
          <button type="button" className="op-kpi op-kpi-near" data-testid="op-kpi-hampir">
            <span className="op-kpi-label">Hampir Deadline</span>
            <span className="op-kpi-value">{kpi.hampir}</span>
          </button>
          <button type="button" className="op-kpi op-kpi-late" data-testid="op-kpi-terlambat">
            <span className="op-kpi-label">Terlambat</span>
            <span className="op-kpi-value">{kpi.terlambat}</span>
          </button>
          <button type="button" className="op-kpi op-kpi-done" data-testid="op-kpi-selesai-today">
            <span className="op-kpi-label">Selesai Hari Ini <small>(pekerjaan diserahkan)</small></span>
            <span className="op-kpi-value">{kpi.selesai_today}</span>
          </button>
        </div>
      )
    )}

    {/* Iter 542 — Diagram Produktivitas Harian Desainer Bordir mode OFFLINE.
        Iter 543 — Berevolusi jadi LEADERBOARD; default Gabungan (all). Mode
        Shopee punya instance sendiri di ShopeeEmbroiderySection (warehouse_module.js).
        Operator Bordir mesin tidak terkena (team≠"Bordir"). */}
    {isAdmin && !operator && currentTab?.key === "Bordir" && <OperatorBordirPerformance testIdPrefix="owner-opbordir-performa"/>}

    {/* Iter 313 — Deadline Kalender Mini: 7 hari ke depan + 1 kolom "Terlambat".
        Hanya tampil di dashboard operator (bukan admin view) & bukan tab Selesai (Shella).
        Iter 327 — Dipindah ke tab Pemantauan untuk operator Gudang (fokus layar kerja).
        Iter 382 — Disembunyikan juga untuk team "Bordir" (Desain Bordir) — ringkasan KPI
        di atas sudah cukup untuk fokus kerja desainer DST.
        Iter 510 — Disembunyikan juga untuk team "Operator Bordir" (operator mesin bordir)
        agar antrean/dikerjakan/riwayat langsung terlihat setelah KPI, konsisten dengan
        permintaan user "fokus pada 3 tab tanpa chart besar". */}
    {operator && !currentTab?.isCompletion && operator?.team !== "Gudang" && !isPress
      && operator?.team !== "Bordir" && operator?.team !== "Operator Bordir" && (
      <DeadlineMiniChart teamCards={teamCards} todayISO={todayISO} hidden={operator?.team === "Sablon/DTF"} mobileCollapsed={operator?.team === "Mesin Press"}/>
    )}

    {/* Iter 247 — Tab Selesai (dashboard pickup, bukan antrean kerja).
        Admin/Owner: aktif saat klik tab Selesai. Operator Shella: default tampil. */}
    {currentTab?.isCompletion ? (
      <SelesaiDashboard
        orders={orders} user={user} operator={operator} isAdmin={isAdmin}
        onOpen={onOpen} onChanged={onChanged}
      />
    ) : (isPress && pressCh === "shopee") ? null : operator ? (
      <>
        {/* Iter 371 — Strip aksi utama Rido: 2 tile besar "Cetak Mockup" + "Pantau Produksi".
            Hanya tampil untuk Gudang non-ACC-only (Rido). Tidak mengganggu tab existing;
            ini hanya shortcut navigasi yang memanggil setOpSection() yang sama. Count
            ambil dari kpi.gudang_prep (belum dicetak) agar konsisten dengan tab count. */}
        {operator?.team === "Gudang" && !isAccOnly && (
          <div className="rido-primary-strip rido-primary-strip-single" data-testid="rido-primary-strip" role="group" aria-label="Tugas utama Rido">
            <button
              type="button"
              className={`rido-primary-tile rido-primary-tile-print ${opSection==="prep"?"active":""}`}
              onClick={()=>setOpSection("prep")}
              data-testid="rido-primary-tile-print"
              aria-pressed={opSection==="prep"}
            >
              <span className="rido-primary-icon" aria-hidden="true"><Printer size={22}/></span>
              <span className="rido-primary-text">
                <strong>Cetak Mockup</strong>
                <small>{kpi.gudang_prep || 0} nota belum dicetak</small>
              </span>
              <ChevronRight size={20} className="rido-primary-chev" aria-hidden="true"/>
            </button>
          </div>
        )}
        {/* Iter 372 — Rido/Gudang (non-ACC-only): Pemantauan tab di nav bar DIHAPUS
            karena sudah diakses lewat primary tile "Pantau Produksi". Siapkan Barang
            juga tidak dibuatkan tab karena primary tile "Cetak Mockup" membuka
            halaman yang sama. Sisa tab (ACC / Selesai / Riwayat) di-collapse ke
            dropdown "Lainnya" agar primary tile menjadi akses utama HP yang jelas.
            Adit (isAccOnly) & operator non-Gudang: nav lama dipertahankan. */}
        {operator?.team === "Gudang" && !isAccOnly ? (
          <RidoLainnyaMenu
            opSection={opSection}
            setOpSection={setOpSection}
            kpi={kpi}
          />
        ) : (
        <nav className={`op-section-tabs ${(operator?.team === "Gudang" || operator) ? "op-section-tabs-equal" : ""}`} role="tablist" data-testid="op-section-tabs" hidden={isPress}>
          {/* Iter 142 — Gudang tidak menampilkan tab "Antrean Tim" & "Sedang Dikerjakan"; kedua fungsi
              itu digabung ke tab "Penyiapan Barang" agar Rido fokus pada tugas menyiapkan barang.
              Iter 287ap — Label dipendekkan ("Penyiapan", "Pemantauan", "Riwayat") agar muat 3 kolom di HP.
              Iter 287ar — Non-Gudang operator: label juga dipendekkan ("Antrean", "Dikerjakan", "Riwayat"). */}
          {operator?.team !== "Gudang" && (
            <button type="button" role="tab" className={`op-section-tab ${opSection==="antrean"?"active":""}`} data-testid="op-section-tab-antrean" onClick={()=>setOpSection("antrean")}>
              <span>Antrean</span>
              {/* Iter 287ax — badge pakai nota count (bukan produk) agar konsisten dgn daftar. */}
              <span className="op-section-tab-count" data-testid="op-section-tab-antrean-count">{kpi.menunggu_nota}</span>
            </button>
          )}
          {operator?.team !== "Gudang" && (
            <button type="button" role="tab" className={`op-section-tab ${opSection==="sedang"?"active":""}`} data-testid="op-section-tab-sedang" onClick={()=>setOpSection("sedang")}>
              <span>Dikerjakan</span>
              <span className="op-section-tab-count" data-testid="op-section-tab-sedang-count">{kpi.dikerjakan_nota}</span>
            </button>
          )}
          {operator?.team === "Operator Bordir" && (
            <button type="button" role="tab" className={`op-section-tab ${opSection==="semua"?"active":""}`} data-testid="op-section-tab-semua" onClick={()=>setOpSection("semua")}>
              <span>Semua Pekerjaan</span>
              <span className="op-section-tab-count" data-testid="op-section-tab-semua-count">{semuaTim.length}</span>
            </button>
          )}
          {isPress && (
            <button type="button" role="tab" className={`op-section-tab ${opSection==="menunggu"?"active":""}`} data-testid="op-section-tab-menunggu" onClick={()=>{ setOpSection("menunggu"); setPressLate(false); }}>
              <span>Menunggu</span>
              <span className="op-section-tab-count" data-testid="op-section-tab-menunggu-count">{pressOffWait ?? "…"}</span>
            </button>
          )}
          {operator?.team === "Gudang" && isAccOnly && (
            <button type="button" role="tab" className={`op-section-tab ${opSection==="acc"?"active":""}`} data-testid="op-section-tab-acc" onClick={()=>setOpSection("acc")}>
              <span>Menunggu ACC</span>
              <span className="op-section-tab-count" data-testid="op-section-tab-acc-count">{kpi.gudang_acc || 0}</span>
            </button>
          )}
          <button type="button" role="tab" className={`op-section-tab ${opSection==="riwayat"?"active":""}`} data-testid="op-section-tab-riwayat" onClick={()=>setOpSection("riwayat")}>
            <span>Riwayat</span>
            {/* Iter 332.5 — Badge count untuk tab Riwayat, hanya ditampilkan untuk
                tim Mockup (Mia dkk) sesuai permintaan. Nilai = total `riwayatSaya`.
                Iter 332.6 — Diperluas ke tim Gudang (Rido/Adit) dengan nilai sama. */}
            {(operator?.team === "Mockup" || operator?.team === "Gudang") && (
              <span className="op-section-tab-count" data-testid="op-section-tab-riwayat-count">{
                operator?.team === "Gudang" ? (kpi.gudang_riwayat || 0) : kpi.riwayat_nota
              }</span>
            )}
          </button>
        </nav>
        )}

        {/* Iter 589 — Bordir Multi-Operator: tugas sub-pekerjaan milik operator ini. */}
        {operator?.team === "Operator Bordir" && <ClaimLookup/>}
        {operator?.team === "Operator Bordir" && <MyBordirTasks/>}
        {/* Iter 287ar — Search bar untuk operator team (filter by customer name / order number). */}
        {operator?.team !== "Gudang" && (
          <div className="op-search-wrap" data-testid="op-search-wrap">
            <Search size={16} className="op-search-icon" aria-hidden="true"/>
            <input
              type="search"
              className="op-search-input"
              placeholder="Cari nama / nomor nota"
              value={opSearch}
              onChange={(e)=>setOpSearch(e.target.value)}
              data-testid="op-search-input"
              aria-label="Cari nama pelanggan atau nomor nota"
            />
            {opSearch && <button type="button" className="op-search-clear" onClick={()=>setOpSearch("")}
              aria-label="Bersihkan pencarian" data-testid="op-search-clear">
              <X size={14}/>
            </button>}
            {/* Iter 333 — Scan Barcode untuk Operator Bordir: resolve order_number ke
                stage. Jika stage === "Bordir" → set opSearch (filter kartu). Jika belum
                di stage Bordir → toast info. Pakai endpoint existing /barcode-scan.
                Iter 511b — Tersedia di semua tab (Antrean · Dikerjakan · Riwayat) sesuai
                permintaan Owner, agar operator dapat memfilter kartu di tab manapun. */}
            {operator?.team === "Operator Bordir" && (
              <OperatorBordirScanButton operator={operator} user={user} onFound={(num)=>setOpSearch(num)}/>
            )}
          </div>
        )}

        {/* Iter 332.3 — Strip pasif untuk tim Mockup (Mia dkk): "Hampir deadline X · Terlambat X".
            Pengganti ringkas untuk `op-stats-row` yang disembunyikan agar nav lebih bersih.
            Tidak clickable (hanya informasi), warna muted + highlight warning/danger bila > 0.
            Iter 364 — Sembunyikan di tab Riwayat (indikator hanya relevan utk pekerjaan aktif). */}
        {operator?.team === "Mockup" && !currentTab?.isCompletion && opSection !== "riwayat" && (
          <div className="op-deadline-strip" data-testid="op-deadline-strip" aria-live="polite">
            <span className={kpi.hampir>0 ? "op-deadline-strip-warn" : ""}
              data-testid="op-deadline-strip-near">
              Hampir deadline <b>{kpi.hampir}</b>
            </span>
            <span className="op-deadline-strip-dot" aria-hidden="true">·</span>
            <span className={kpi.terlambat>0 ? "op-deadline-strip-late" : ""}
              data-testid="op-deadline-strip-late">
              Terlambat <b>{kpi.terlambat}</b>
            </span>
          </div>
        )}

        <div className="prod-tab-body" data-testid="prod-tab-body">
          {/* Iter 287ar — Filter in-place berdasar opSearch (nama customer / nomor nota).
              Tidak mengubah state antreanTim/mineActive/riwayatSaya; hanya view-level filter.
              Scroll position & tab aktif tetap preserved saat data diperbarui. */}
          {(() => {
            const q = (opSearch || "").trim().toLowerCase();
            const matchCard = (c) => {
              if (!q) return true;
              const name = String(c?.order?.customer_name || "").toLowerCase();
              const num = String(c?.order?.order_number || "").toLowerCase();
              return name.includes(q) || num.includes(q);
            };
            const matchEvent = (r) => {
              if (!q) return true;
              const name = String(r?.order?.customer_name || "").toLowerCase();
              const num = String(r?.order?.order_number || "").toLowerCase();
              const prod = String(r?.product?.custom_name || r?.product?.name || "").toLowerCase();
              return name.includes(q) || num.includes(q) || prod.includes(q);
            };
            const lateOk = (c) => !(isPress && pressLate) || c.product.deadline_status === "Terlambat";
            const antreanFiltered = antreanTim.filter(matchCard).filter(lateOk);
            const mineFiltered = mineActive.filter(matchCard).filter(lateOk);
            const riwayatFiltered = riwayatSaya.filter(matchEvent);
            return <>
              {isPress && pressLate && (opSection === "antrean" || opSection === "sedang") && <LateBanner count={pressOffLateNota}
                onClear={() => setPressLate(false)} testid="offline-press-late"/>}
              {isPress && opSection === "menunggu" && <OfflinePressWaiting onOpen={(num) => { const o = orders.find(x => x.order_number === num); if (o) onOpen(o); }}/>}
              {opSection === "antrean" && (
                antreanFiltered.length === 0
                  ? <OperatorEmptyState kind="antrean" isOperator searchQuery={q}/>
                  : (operator?.team === "Bordir" ? sortDesignQueue : (g) => g)(groupTeamCards(antreanFiltered, visibleTabs[0]?.key || "", opName)).map(c => (
                      <TeamProductCard key={c.groupKey} card={c} tab={visibleTabs.find(t=>t.key===c.stage) || currentTab} isAdmin={isAdmin} operator={operator} user={user} busy={busyKey===c.key || (c.productCards||[]).some(pc => busyKey===pc.key)} onOpen={()=>onOpen(c.order)} onOpenMockup={()=>openMockupPreview(c.order)} onAdvance={advance} onClaim={claim} onSetRoute={setRoute} onAdminCorrect={adminCorrect} onParallelDone={parallelDone} onParallelClaim={parallelClaim} onChanged={onChanged} sectionMode="antrean" highlightQuery={q}/>
                    ))
              )}
              {opSection === "semua" && (() => { const sf = semuaTim.filter(matchCard);
                return sf.length === 0 ? <OperatorEmptyState kind="sedang" isOperator searchQuery={q}/>
                  : groupTeamCards(sf, visibleTabs[0]?.key || "", "").map(c => (
                      <TeamProductCard key={c.groupKey} card={c} tab={visibleTabs.find(t=>t.key===c.stage) || currentTab} isAdmin={isAdmin} operator={operator} user={user} busy={false} onOpen={()=>onOpen(c.order)} onOpenMockup={()=>openMockupPreview(c.order)} onAdvance={advance} onClaim={claim} onSetRoute={setRoute} onAdminCorrect={adminCorrect} onParallelDone={parallelDone} onParallelClaim={parallelClaim} onChanged={onChanged} sectionMode="semua" highlightQuery={q}/>)); })()}
              {opSection === "sedang" && (
                mineFiltered.length === 0
                  ? <OperatorEmptyState kind="sedang" isOperator searchQuery={q}/>
                  : groupTeamCards(mineFiltered, visibleTabs[0]?.key || "", opName).map(c => (
                      <TeamProductCard key={c.groupKey} card={c} tab={visibleTabs.find(t=>t.key===c.stage) || currentTab} isAdmin={isAdmin} operator={operator} user={user} busy={busyKey===c.key || (c.productCards||[]).some(pc => busyKey===pc.key)} onOpen={()=>onOpen(c.order)} onOpenMockup={()=>openMockupPreview(c.order)} onAdvance={advance} onClaim={claim} onSetRoute={setRoute} onAdminCorrect={adminCorrect} onParallelDone={parallelDone} onParallelClaim={parallelClaim} onRelease={release} onParallelRelease={parallelRelease} onChanged={onChanged} sectionMode="sedang" highlightQuery={q}/>
                    ))
              )}
              {opSection === "prep" && operator?.team === "Gudang" && (
                <PenyiapanBarang
                  operator={operator} user={user} isAdmin={isAdmin} orders={orders}
                  busyKey={busyKey} todayISO={todayISO}
                  onOpen={onOpen} onOpenMockup={openMockupPreview}
                  onClaim={claim} onAdvance={advance}
                  onParallelClaim={parallelClaim} onParallelDone={parallelDone}
                  onMarkMockupPrint={markMockupPrint}
                  onReload={onChanged}
                  printFilter="belum"
                  gudangAccCount={kpi.gudang_acc || 0}
                  onJumpAcc={()=>setOpSection("acc")}
                />
              )}
              {opSection === "selesai" && operator?.team === "Gudang" && !isAccOnly && (
                <GudangSelesaiList
                  operator={operator} orders={orders} todayISO={todayISO}
                  onOpen={onOpen} onOpenMockup={openMockupPreview}
                />
              )}
              {opSection === "acc" && operator?.team === "Gudang" && (
                <AccBarangList
                  operator={operator} orders={orders} busyKey={busyKey}
                  onOpen={onOpen} onOpenMockup={openMockupPreview}
                  onMarkAccReady={markAccReady}
                  onMarkMockupPrint={markMockupPrint}
                  highlightQuery={q}
                  isAccOnly={isAccOnly}
                  gudangPrepCount={kpi.gudang_prep || 0}
                  onJumpPrep={!isAccOnly ? (()=>setOpSection("prep")) : undefined}
                />
              )}
              {opSection === "monitor" && operator?.team === "Gudang" && (
                <div className="rido-monitor-grid rido-monitor-grid--single" data-testid="rido-monitor-grid">
                  {/* Iter 354 — Daftar pesanan ditampilkan langsung. Ringkasan Gudang
                      (tile + grafik 7 hari) dipindah ke fold "Lihat ringkasan Gudang"
                      di bawah agar Rido langsung melihat kartu pesanan. Akses existing
                      tetap (tidak memperluas permission).
                      Iter 496 — Source mengikuti tab aktif (Offline/Shopee). Komponen
                      sama; backend ganti data lewat query param `source`.
                      Iter 518 — Layout 2 kolom (desktop ≥1200px): daftar ±65% di kiri,
                      ringkasan sticky ±35% di kanan. Mobile tetap satu kolom vertikal
                      & ringkasan tetap collapsible. Wrapper hanya mengatur layout
                      visual; TIDAK mengubah data/permission/workflow. */}
                  {/* Iter 578 — Ringkasan Gudang (per produk) + grafik 7 hari dipindah ke panel
                      "Statistik Lengkap" (tertutup default) di dalam ProductionMonitoring. */}
                  <ProductionMonitoring operator={operator} onOpen={onOpen} source={gudangSource} simple
                    extraStats={<GudangOverviewPanel operator={operator} orders={orders} todayISO={todayISO} teamCards={teamCards}/>}/>
                </div>
              )}
              {opSection === "riwayat" && (
                // Iter 361 — Tab Riwayat Mockup Offline: HANYA SATU UI.
                //   Reuse `opSearch` yang sudah ada di header navigasi (TIDAK duplikat).
                //   Header filter periode (Hari Ini/Minggu Ini/Bulan Ini/Semua) + 1 filter
                //   Status dirender inline. Isi list = kartu Riwayat Pekerjaan versi
                //   baru (RiwayatPekerjaanCard) dengan field tracking lengkap.
                //   Tim non-Mockup tetap memakai RiwayatSaya existing (tidak diubah).
                isMockupTeam ? (
                  <MockupRiwayatPane
                    items={riwayatMockupOffline}
                    opSearch={opSearch}
                    orders={orders}
                    onOpen={onOpen}
                    onOpenMockup={openMockupPreview}
                  />
                ) : (
                  <RiwayatSaya items={riwayatFiltered} operator={operator} user={user} onOpen={onOpen} onOpenMockup={openMockupPreview} highlightQuery={q} onRevertMockup={revertMockup} busyKey={busyKey}/>
                )
              )}
            </>;
          })()}
        </div>
      </>
    ) : (
      <div className="prod-tab-body" data-testid="prod-tab-body">
        {cardsInTab.length === 0
          ? <OperatorEmptyState team={currentTab?.team || ""} tabLabel={currentTab?.label || ""} isOperator={false}/>
          : <AdminPagedCards groups={groupTeamCards(cardsInTab, currentTab?.key || "", "")} resetKey={currentTab?.key} render={c => <TeamProductCard key={c.groupKey} card={c} tab={currentTab} isAdmin={isAdmin} operator={operator} user={user} busy={busyKey===c.key || (c.productCards||[]).some(pc => busyKey===pc.key)} onOpen={()=>onOpen(c.order)} onOpenMockup={()=>openMockupPreview(c.order)} onAdvance={advance} onClaim={claim} onSetRoute={setRoute} onAdminCorrect={adminCorrect} onParallelDone={parallelDone} onParallelClaim={parallelClaim} onChanged={onChanged}/>}/>}
      </div>
    )}
    </>)}
    </>)}
    </>}

    {correctCard && <StageCorrectModal card={correctCard} onClose={()=>setCorrectCard(null)} onSubmit={submitCorrect} busy={!!busyKey}/>}
    {mockupPreview && <MockupPreviewModal
      orderNumber={mockupPreview.order_number}
      customerName={mockupPreview.customer_name}
      isRepeatOrder={mockupPreview.is_repeat_order}
      repeatRef={mockupPreview.repeat_ref}
      onClose={()=>setMockupPreview(null)}
    />}
    {/* Iter 307 — Scanner barcode mockup (HP kamera). */}
    <ScanMockupModal
      open={scanOpen}
      onClose={()=>setScanOpen(false)}
      operator={operator}
      user={user}
      onOpenOrder={onOpen}
    />
  </div>;
}

export function DeadlineMiniChart({ teamCards, todayISO, hidden, mobileCollapsed }) {
  // Iter 313 — Mini bar chart 7 hari.
  // Iter 318 — Hitung PER NOTA (unique order_number) alih-alih per produk, agar
  //   nota ber-banyak produk tidak dilipatkan. Judul: "Jumlah deadline 7 hari ke depan".
  // Iter 349 — Collapsible via klik judul. State persist di localStorage agar operator
  //   tidak perlu melipat berulang. Default: expanded. Ringkasan tetap terlihat saat
  //   collapsed (total 7 hari + overdue terpisah) agar akses antrean lebih cepat.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem("dln_mini_collapsed");
      if (v === null && mobileCollapsed) return window.innerWidth < 640;
      return v === "1";
    } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("dln_mini_collapsed", collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);
  const buckets = useMemo(() => {
    const pad = (n) => String(n).padStart(2, "0");
    const fmtISO = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    const parseISO = (s) => {
      try { const [y,m,d]=s.split("-").map(Number); return new Date(y, m-1, d); }
      catch { return null; }
    };
    const today = parseISO(todayISO) || new Date();
    today.setHours(0,0,0,0);
    const overdue = { key: "overdue", count: 0, orders: new Set() };
    const days = [];
    const dayLabels = ["Min","Sen","Sel","Rab","Kam","Jum","Sab"];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today); d.setDate(d.getDate()+i);
      days.push({
        key: fmtISO(d),
        date: fmtISO(d),
        label: i === 0 ? "Hari ini" : dayLabels[d.getDay()],
        dayShort: `${pad(d.getDate())}/${pad(d.getMonth()+1)}`,
        count: 0,
        level: "aman",
        orders: new Set(),       // untuk dedup per nota
        orderStatuses: {},        // order_number → tingkat tertinggi deadline_status di nota
      });
    }
    for (const c of (teamCards || [])) {
      const dl = c?.order?.deadline || "";
      const ordNo = c?.order?.order_number;
      if (!dl || !ordNo) continue;
      const ds = c?.product?.deadline_status || "Aman";
      // Iter 369 — Bucket "Terlambat" chart menggunakan `deadline_status` yang
      //   di-inject caller (SSOT yang sama dengan KPI, mis. `isOrderLate` di
      //   Admin Toko / server-side late flag di operator). Sebelumnya hanya
      //   mengandalkan `dl < todayISO` sehingga nota Selesai/Diambil/Finalisasi
      //   masih terhitung Terlambat → menghasilkan angka berbeda dengan KPI.
      //   Dengan perubahan ini, chart & KPI konsisten.
      if (ds === "Terlambat") {
        if (!overdue.orders.has(ordNo)) {
          overdue.orders.add(ordNo);
          overdue.count += 1;
        }
        continue;
      }
      // Nota ber-deadline lewat tapi TIDAK tergolong Terlambat (mis. Selesai,
      //   Menunggu Finalisasi, Diambil, Dikirim, atau tanpa produk aktif) juga
      //   tidak ditampilkan pada kolom 7 hari ke depan karena deadline-nya sudah
      //   masa lalu — di-skip supaya chart hanya menampilkan muatan kerja nyata.
      if (dl < todayISO) continue;
      const idx = days.findIndex((x) => x.date === dl);
      if (idx >= 0) {
        if (!days[idx].orders.has(ordNo)) {
          days[idx].orders.add(ordNo);
          days[idx].count += 1;
        }
        // Level kolom = tingkat tertinggi di antara produk nota pada hari itu.
        const prev = days[idx].orderStatuses[ordNo] || "Aman";
        const rank = { "Terlambat":3, "Hari Ini":2, "Hampir Deadline":1, "Aman":0 };
        if ((rank[ds]||0) > (rank[prev]||0)) days[idx].orderStatuses[ordNo] = ds;
        const topStatus = days[idx].orderStatuses[ordNo];
        if (topStatus === "Terlambat") days[idx].level = "late";
        else if (topStatus === "Hampir Deadline" && days[idx].level !== "late") days[idx].level = "warn";
      }
    }
    return { overdue, days };
  }, [teamCards, todayISO]);

  const maxCount = Math.max(1, buckets.overdue.count, ...buckets.days.map((d) => d.count));
  // Iter 349 — Badge "N nota" hanya menghitung 7 hari (hari ini..H+6).
  //   Jumlah terlambat ditampilkan di keterangan terpisah agar admin tidak salah baca.
  const sevenDayTotal = buckets.days.reduce((s, d) => s + d.count, 0);
  const totalLoad = buckets.overdue.count + sevenDayTotal;
  if (hidden || totalLoad === 0) return null;

  return (
    <div className={`dln-mini ${collapsed ? "dln-mini-collapsed" : ""}`} data-testid="dln-mini-chart" aria-label="Jumlah deadline 7 hari ke depan">
      <button
        type="button"
        className="dln-mini-head dln-mini-head-toggle"
        onClick={() => setCollapsed(v => !v)}
        aria-expanded={!collapsed}
        aria-controls="dln-mini-bars"
        data-testid="dln-mini-toggle"
      >
        <Calendar size={13} aria-hidden="true" />
        <span className="dln-mini-title">Deadline 7 Hari</span>
        <span className="dln-mini-total" data-testid="dln-mini-total" title="Jumlah nota deadline hari ini s.d. 6 hari ke depan">
          {sevenDayTotal} nota
        </span>
        {buckets.overdue.count > 0 && (
          <span className="dln-mini-overdue" data-testid="dln-mini-overdue" title={`${buckets.overdue.count} nota sudah terlambat`}>
            Terlambat <b>{buckets.overdue.count}</b>
          </span>
        )}
        <span className="dln-mini-chev" aria-hidden="true">{collapsed ? <ChevronDown size={14}/> : <ChevronUp size={14}/>}</span>
      </button>
      {!collapsed && (
      <div className="dln-mini-bars" id="dln-mini-bars" role="group" aria-label="Grafik jumlah nota deadline per hari">
        {buckets.overdue.count > 0 && (
          <div
            className="dln-col dln-col-overdue"
            data-testid="dln-col-overdue"
            title={`${buckets.overdue.count} nota sudah terlambat`}
          >
            <span className="dln-count">{buckets.overdue.count}</span>
            <div className="dln-bar-track">
              <div
                className="dln-bar"
                style={{ height: `${Math.max(14, (buckets.overdue.count / maxCount) * 100)}%` }}
              />
            </div>
            <span className="dln-day">Lambat</span>
            <span className="dln-date">—</span>
          </div>
        )}
        {buckets.days.map((d) => (
          <div
            key={d.key}
            className={`dln-col dln-col-${d.level}${d.count === 0 ? " dln-col-empty" : ""}`}
            data-testid={`dln-col-${d.date}`}
            title={d.count > 0 ? `${d.count} nota deadline ${d.dayShort}` : `Kosong — ${d.dayShort}`}
          >
            <span className="dln-count">{d.count || ""}</span>
            <div className="dln-bar-track">
              <div
                className="dln-bar"
                style={{
                  height: d.count > 0
                    ? `${Math.max(14, (d.count / maxCount) * 100)}%`
                    : "6%",
                }}
              />
            </div>
            <span className="dln-day">{d.label}</span>
            <span className="dln-date">{d.dayShort}</span>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}


function OperatorEmptyState({ team, tabLabel, isOperator, kind, searchQuery }) {
  // Iter 88 — kind: "antrean" | "sedang" | "riwayat" (per-section empty state)
  // Iter 287ar — searchQuery: bila ada, tampilkan pesan "Tidak ada hasil untuk {query}".
  if (searchQuery) {
    return (
      <div className="op-empty" data-testid={`prod-tab-empty-search`}>
        <div className="op-empty-icon" aria-hidden="true">
          <Search size={28}/>
        </div>
        <h4 style={{margin:"8px 0 4px"}}>Tidak ada hasil</h4>
        <p className="muted small" style={{margin:0}}>Tidak ditemukan pesanan dengan kata kunci "<strong>{searchQuery}</strong>".</p>
      </div>
    );
  }
  const meta = {
    antrean: {
      title: "Belum ada pekerjaan baru untuk tim Anda",
      msg: <>Antrean tim <strong>kosong</strong>. Pekerjaan baru akan muncul otomatis saat tahap sebelumnya diselesaikan tim lain. Anda tidak perlu refresh halaman.</>,
    },
    sedang: team === "Operator Bordir" ? {
      // Iter 333.2 — Empty state khusus Operator Bordir mengarah ke scan + Kerjakan.
      title: "Tidak ada pekerjaan yang sedang dikerjakan.",
      msg: <>Ambil pekerjaan baru dari tab <strong>Antrean</strong>.</>,
    } : {
      title: "Tidak ada pekerjaan yang sedang Anda kerjakan",
      msg: <>Ambil pekerjaan baru dari tab <strong>Antrean</strong>. Setelah Anda klik <strong>Kerjakan</strong>, pekerjaan akan muncul di tab <strong>Dikerjakan</strong>.</>,
    },
    riwayat: {
      title: "Belum ada riwayat pekerjaan",
      msg: <>Semua pekerjaan yang Anda selesaikan akan tercatat di sini, termasuk yang sudah pindah ke tahap berikutnya.</>,
    },
  };
  const m = kind && meta[kind];
  const title = m ? m.title : (isOperator ? "Tidak ada pekerjaan saat ini" : `Antrean ${tabLabel || "tahap ini"} kosong`);
  const msg = m ? m.msg : (isOperator
    ? <>Nikmati waktu istirahat singkat. Pekerjaan baru akan <strong>muncul otomatis</strong> di sini begitu tahap sebelumnya diselesaikan tim lain — Anda tidak perlu refresh halaman.</>
    : <>Tidak ada pekerjaan aktif untuk tim <strong>{team || "Anda"}</strong> saat ini. Kartu baru akan muncul otomatis begitu tahap sebelumnya selesai.</>);
  return (
    <div className="op-empty" data-testid={`prod-tab-empty${kind?`-${kind}`:""}`}>
      <div className="op-empty-icon" aria-hidden="true">
        <CheckCircle2 size={32}/>
      </div>
      <h4 className="op-empty-title">{title}</h4>
      <p className="op-empty-msg">{msg}</p>
      {isOperator && (
        <div className="op-empty-hint">
          <Sparkle size={12} aria-hidden="true"/>
          <span>Tip: layar ini refresh otomatis</span>
        </div>
      )}
    </div>
  );
}

// Iter 88 — Riwayat Saya component
// Iter 302 — Pencarian global dilayani oleh op-search di parent. Komponen ini
//   hanya menyediakan filter range (Hari Ini / Minggu Ini / Bulan Ini / Semua).
//   KPI "Selesai hari ini" tetap independen dari filter range (dihitung di parent).
// Iter 361 — Pane Riwayat Mockup Offline (reuse opSearch dari header navigasi
//   di atasnya; TIDAK merender search bar sendiri). Baris chip filter: periode
//   (Hari Ini / Minggu Ini / Bulan Ini / Semua) + Status (Semua / Belum Fix /
//   Revisi / Fix / Batal). Kartu memakai RiwayatPekerjaanCard reusable.
function MockupRiwayatPane({ items, opSearch, orders, onOpen, onOpenMockup }) {
  const [range, setRange] = useState("all");
  const [status, setStatus] = useState("");

  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Jakarta" }));
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek = new Date(startOfDay); startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const qnorm = (opSearch || "").trim().toLowerCase();
  const filtered = (items || []).filter(it => {
    if (status && it.status !== status) return false;
    if (qnorm) {
      const hay = `${it.order_number || ""} ${it.customer || ""} ${it.job_label || ""}`.toLowerCase();
      if (!hay.includes(qnorm)) return false;
    }
    const t = new Date(it.updated_at || it.created_at || 0);
    if (range === "today" && t < startOfDay) return false;
    if (range === "week" && t < startOfWeek) return false;
    if (range === "month" && t < startOfMonth) return false;
    return true;
  });

  // Iter 381 — Dedup & grouping Riwayat Mockup: 1 nota = 1 kartu.
  //   `items` berisi 1 entry per PRODUK (riwayatMockupOffline di parent).
  //   Nota multi-produk ⇒ beberapa entry dgn order_number sama → dulu
  //   menghasilkan kartu duplikat. Group by order_number, agregat produk,
  //   gunakan event paling baru sebagai representasi kartu. Thumbnail/UI
  //   kartu existing dipertahankan (RiwayatPekerjaanCard).
  const grouped = React.useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const key = it.order_number || `__NO_ORDER_${it.id}`;
      const curT = new Date(it.updated_at || it.created_at || 0).getTime();
      const prev = map.get(key);
      if (!prev) {
        map.set(key, {
          ...it,
          // id stabil berbasis nota agar React key tidak collision.
          id: `riwayat-mockup-${key}`,
          _product_count: 1,
          _product_labels: it.job_label ? [it.job_label] : [],
          _revisions_total: it.revision_count || 0,
          _any_fix: it.status === "FIX",
          _any_batal: it.status === "BATAL",
          _latest_t: curT,
        });
      } else {
        prev._product_count += 1;
        if (it.job_label && !prev._product_labels.includes(it.job_label)) {
          prev._product_labels.push(it.job_label);
        }
        prev._revisions_total += it.revision_count || 0;
        prev._any_fix = prev._any_fix || it.status === "FIX";
        prev._any_batal = prev._any_batal || it.status === "BATAL";
        // Entry paling baru menjadi representasi kartu (status / next_label /
        // updated_at). Lebih intuitif krn menampilkan progress terakhir nota.
        if (curT > prev._latest_t) {
          prev._latest_t = curT;
          prev.status = it.status;
          prev.next_label = it.next_label;
          prev.next_cls = it.next_cls;
          prev.updated_at = it.updated_at;
          prev.pic_name = it.pic_name;
          prev.pic_id = it.pic_id;
          prev.is_cancelled = it.is_cancelled;
          prev.cancel_reason = it.cancel_reason;
        }
      }
    }
    // Finalisasi: job_label = gabung ringkas (max 2) + ekor "+ N lainnya"
    for (const g of map.values()) {
      const labels = g._product_labels.filter(Boolean);
      if (labels.length === 0) g.job_label = "—";
      else if (labels.length === 1) g.job_label = labels[0];
      else if (labels.length === 2) g.job_label = labels.join(" · ");
      else g.job_label = `${labels[0]} · ${labels[1]} +${labels.length - 2}`;
      g.revision_count = g._revisions_total;
    }
    // Sort by latest_t desc.
    return [...map.values()].sort((a, b) => b._latest_t - a._latest_t);
  }, [filtered]);

  const openByOrder = (it) => {
    const o = (orders || []).find(x => x.order_number === it.order_number);
    if (o && onOpen) onOpen(o);
  };

  return (
    <div className="riwayat-saya" data-testid="riwayat-saya">
      <div className="riwayat-filter-bar">
        <div className="riwayat-range" data-testid="mockup-riwayat-range">
          {[
            { k: "today", l: "Hari Ini" },
            { k: "week", l: "Minggu Ini" },
            { k: "month", l: "Bulan Ini" },
            { k: "all", l: "Semua" },
          ].map(r => (
            <button key={r.k} type="button"
              className={`riwayat-range-btn ${range === r.k ? "active" : ""}`}
              data-testid={`riwayat-range-${r.k}`}
              onClick={() => setRange(r.k)}>{r.l}</button>
          ))}
        </div>
        <select
          className="riwayat-status-select"
          value={status}
          onChange={e => setStatus(e.target.value)}
          data-testid="mockup-riwayat-status"
          aria-label="Filter status pekerjaan"
        >
          <option value="">Semua Status</option>
          <option value="BELUM_FIX">Belum Fix</option>
          <option value="REVISI">Revisi</option>
          <option value="FIX">Fix</option>
          <option value="BATAL">Batal</option>
        </select>
      </div>
      <style>{`
        .riwayat-saya{max-width:100%;}
        .riwayat-status-select{border:1px solid rgba(203,213,225,.3);background:#171a21;color:#f1f5f9;border-radius:999px;padding:5px 10px;font-size:12.5px;font-weight:600;min-height:32px;margin-left:8px;}
        .riwayat-status-select:focus{outline:none;border-color:#0d9488;}
        /* Iter 381 — List & kartu riwayat tidak boleh melebihi lebar parent. */
        .mockup-riwayat-list{display:flex;flex-direction:column;gap:6px;margin-top:8px;max-width:100%;min-width:0;overflow:hidden;}
        .mockup-riwayat-list > *{max-width:100%;min-width:0;}
        .mockup-riwayat-empty{text-align:center;padding:20px 12px;color:#94a3b8;font-size:13px;border:1px dashed rgba(203,213,225,.22);border-radius:10px;margin-top:8px;}
        @media (max-width: 480px){
          .riwayat-status-select{flex:1 1 100%;margin-left:0;margin-top:6px;}
        }
      `}</style>
      {grouped.length === 0 ? (
        <div className="mockup-riwayat-empty" data-testid="mockup-riwayat-empty">
          Tidak ada pekerjaan pada filter ini.
        </div>
      ) : (
        <div className="mockup-riwayat-list" data-testid="mockup-riwayat-list">
          {grouped.map(it => {
            const order = (orders || []).find(x => x.order_number === it.order_number);
            // Iter 499 — Preview Mockup HARUS tetap bisa dibuka walau order
            //   sudah pindah tahap / tidak lagi di-fetch `orders` aktif. Bangun
            //   payload minimal dari `it` kalau order tidak ketemu.
            const openPreview = () => {
              if (!onOpenMockup) return;
              if (order) { onOpenMockup(order); return; }
              onOpenMockup({
                order_number: it.order_number,
                customer_name: it.customer || "",
                is_repeat_order: !!(it._raw && it._raw.is_repeat_order),
                repeat_ref: (it._raw && it._raw.repeat_ref) || "",
              });
            };
            return (
              <RiwayatPekerjaanCard
                key={it.id}
                item={it}
                flavor="offline"
                testPrefix="mockup-offline-riwayat"
                onOpen={openByOrder}
                thumbSlot={
                  <MonitorThumb
                    orderNumber={it.order_number}
                    onOpen={openPreview}
                  />
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
}



function RiwayatSaya({ items, operator, user, onOpen, onOpenMockup, highlightQuery, onRevertMockup, busyKey }) {
  const [range, setRange] = useState("all");

  // Iter 302 / Iter 378 — Boundary waktu & list ter-filter dihitung via useMemo
  //   agar sinkron deterministik terhadap `range` dan `items`. Sebelumnya
  //   dihitung inline tiap render → bisa menyebabkan Date boundary bergeser
  //   bila render bertepatan dengan pergantian menit (sangat jarang) dan
  //   menyulitkan telusur re-render saat filter diklik. Dengan useMemo, saat
  //   state `range` berubah, filter list dijamin ter-recompute SEKALI dengan
  //   boundary yang sama.
  //   Minggu Ini dinormalkan ke Senin (ISO/Indonesia), bukan Sunday (US).
  //   Boundary dihitung sekali saat mount (deps kosong) — pergantian hari
  //   di-handle oleh refresh halaman / auto-refresh parent yang remount tree.
  const { startOfDay, startOfWeek, startOfMonth } = React.useMemo(() => {
    const nowJakarta = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Jakarta" }));
    const sod = new Date(nowJakarta.getFullYear(), nowJakarta.getMonth(), nowJakarta.getDate());
    const dow = sod.getDay();
    const mondayOffset = dow === 0 ? 6 : dow - 1;
    const sow = new Date(sod); sow.setDate(sow.getDate() - mondayOffset);
    const som = new Date(nowJakarta.getFullYear(), nowJakarta.getMonth(), 1);
    return { startOfDay: sod.getTime(), startOfWeek: sow.getTime(), startOfMonth: som.getTime() };
  }, []);

  const filtered = React.useMemo(() => {
    const arr = Array.isArray(items) ? items : [];
    if (range === "all") return arr;
    return arr.filter(it => {
      const raw = it?.event?.at;
      if (!raw) return false;
      const t = new Date(raw).getTime();
      if (!Number.isFinite(t)) return false;
      if (range === "today") return t >= startOfDay;
      if (range === "week") return t >= startOfWeek;
      if (range === "month") return t >= startOfMonth;
      return true;
    });
  }, [items, range, startOfDay, startOfWeek, startOfMonth]);

  return (
    <div className="riwayat-saya" data-testid="riwayat-saya">
      <div className="riwayat-filter-bar">
        <div className="riwayat-range">
          {[
            {k:"today", l:"Hari Ini"},
            {k:"week",  l:"Minggu Ini"},
            {k:"month", l:"Bulan Ini"},
            {k:"all",   l:"Semua"},
          ].map(r => (
            <button key={r.k} type="button" className={`riwayat-range-btn ${range===r.k?"active":""}`} data-testid={`riwayat-range-${r.k}`} onClick={()=>setRange(r.k)}>{r.l}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <OperatorEmptyState kind="riwayat" isOperator/>
      ) : (
        // Iter 335 — Frontend-only grouping: items dikelompokkan per `order_number` supaya
        //   1 nota = 1 kartu visual. Backend data (riwayatSaya) tidak diubah; hanya
        //   dirender ulang sebagai nested rows di dalam kartu header per nota.
        <ul className="riwayat-list riwayat-list-grouped" data-testid="riwayat-list">
          {(() => {
            const buckets = new Map(); // order_number → { order, items[] }
            for (const it of filtered) {
              const num = it.order.order_number;
              if (!buckets.has(num)) buckets.set(num, { order: it.order, items: [] });
              buckets.get(num).items.push(it);
            }
            const groups = Array.from(buckets.values());
            return groups.map((g, gi) => {
              // Iter 493 — Prod/aktivitas count: respect entry agregat Adit
              //   (accAggregatedProducts). Untuk operator lain, hitung dari items.
              let prodCount, actCount;
              const anyAgg = g.items.find(x => x && x.accAggregatedProducts);
              if (anyAgg) {
                const uniqProd = new Set();
                let acts = 0;
                for (const x of g.items) {
                  if (x.accAggregatedProducts) {
                    for (const ap of x.accAggregatedProducts) uniqProd.add(ap.id || ap.name);
                    acts += x.accAggregatedProducts.length;
                  } else {
                    uniqProd.add(x.product?.id);
                    acts += 1;
                  }
                }
                prodCount = uniqProd.size;
                actCount = acts;
              } else {
                prodCount = new Set(g.items.map(x => x.product.id)).size;
                actCount = g.items.length;
              }
              return (
                <li key={`riw-group-${g.order.order_number}-${gi}`} className="riwayat-group" data-testid={`riwayat-group-${g.order.order_number}`}>
                  <div className="riwayat-group-head">
                    <div className="riwayat-group-head-main">
                      <strong className="riwayat-group-cust">{g.order.customer_name || "—"}</strong>
                      <span className="muted small"> · {g.order.order_number}</span>
                    </div>
                    <div className="riwayat-group-head-meta muted small">
                      {prodCount} produk · {actCount} aktivitas
                    </div>
                  </div>
                  <ul className="riwayat-group-items">
                    {(() => {
                      // Iter 537 — FIX duplikasi kartu produk dalam 1 nota (Sablon Ibnu & lainnya).
                      // Sebelumnya: 1 event = 1 RiwayatItem → produk sama dgn beberapa event muncul berkali-kali.
                      // Sekarang: group per-identitas produk dalam nota ini. 1 produk = 1 kartu dgn timeline aktivitas.
                      // Logic grouping dipisah ke `riwayat_grouping.js` agar BISA DIUJI SECARA PERILAKU.
                      // Hitungan header (prodCount/actCount) di atas TIDAK berubah — tetap mencerminkan data asli.
                      const perProduct = groupRiwayatByProduct(g.items);
                      return perProduct.map(({ identity, primary, all }, i) => (
                        <RiwayatItem
                          key={`${g.order.order_number}-${identity}-${primary.event?.at || i}`}
                          item={{ ...primary, allActivities: all }}
                          idx={`${gi}-${i}`}
                          operator={operator} user={user}
                          onOpen={() => onOpen(primary.order)}
                          onOpenMockup={() => onOpenMockup && onOpenMockup(primary.order)}
                          highlightQuery={highlightQuery}
                          onRevertMockup={onRevertMockup}
                          busyKey={busyKey}
                        />
                      ));
                    })()}
                  </ul>
                </li>
              );
            });
          })()}
        </ul>
      )}
    </div>
  );
}

function RiwayatItem({ item, idx, operator, user, onOpen, onOpenMockup, highlightQuery, onRevertMockup, busyKey }) {
  const { order, product, event, currentStage, currentPic, orderStatus, parallelStatus } = item;
  const route = product.production_route || "";
  const stagesList = ROUTE_STAGES_UI[route] || [];
  const curI = stagesList.indexOf(currentStage);
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      // Iter 302 — Format waktu ikut zona Asia/Jakarta, apapun timezone browser.
      const d = new Date(iso);
      const parts = new Intl.DateTimeFormat("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
      }).formatToParts(d);
      const dd = parts.find(p=>p.type==="day")?.value || "";
      const mm = parts.find(p=>p.type==="month")?.value || "";
      const hh = parts.find(p=>p.type==="hour")?.value || "";
      const mi = parts.find(p=>p.type==="minute")?.value || "";
      return `${dd}/${mm} ${hh}:${mi}`;
    } catch { return iso; }
  };

  // Iter 302 — Pisahkan "pelaksana tahap sebelumnya" (historis, tetap tersimpan)
  //   dari "status & PIC sekarang" (ikut keadaan pesanan terbaru).
  // Iter 311 — Event REVERT: label "Dikembalikan ke Mockup oleh X", bukan "Mockup→Y selesai".
  //            Event CANCELED (completion yg dibatalkan oleh revert berikutnya): beri badge
  //            "Penyelesaian dibatalkan — kembali dikerjakan" untuk tetap audit tanpa menampilkannya
  //            sebagai pekerjaan selesai aktif.
  const isRevert = !!item.isRevert;
  const isCanceled = !!item.isCanceled;
  const stageFrom = event.stage_from || "—";
  const stageTo = event.stage_to || "";
  const pelaksana = event.handed_over_by || event.moved_by || operator?.name || "—";
  const revertReason = (() => {
    if (!isRevert) return "";
    const n = String(event.note || "");
    // Format backend: "Kembalikan ke Mockup (alasan: XXX)"
    const m = n.match(/alasan:\s*([\s\S]*)\)\s*$/i);
    return (m && m[1]) ? m[1].trim() : "";
  })();

  // Iter 302 — Tentukan tampilan stage & PIC saat ini.
  //   - Jika produk masuk stage "Selesai" DAN order_status="Menunggu Finalisasi" →
  //     tampilkan "Menunggu Finalisasi" (antrean finalisasi Shella). PIC = Shella
  //     (team "Selesai"); gunakan "Belum diambil" bila belum ada PIC aktif.
  //   - Jika order_status="Selesai" (sudah difinalisasi) → tampilkan "Selesai"
  //     tanpa PIC aktif.
  //   - Selain itu → ikut production_stage + production_pic terbaru. Jangan pakai
  //     operator ini (Wawan) sebagai fallback setelah pekerjaannya diserahkan.
  let displayStage = currentStage || "—";
  let displayPic = "";
  const stageIsSelesai = currentStage === "Selesai";
  const stageIsParallel = !!PARALLEL_STAGE_TRACKS[currentStage];
  if (stageIsSelesai && orderStatus === "Menunggu Finalisasi") {
    displayStage = "Menunggu Finalisasi";
    displayPic = currentPic && currentPic !== (event.handed_over_by || "") ? currentPic : "Belum diambil";
  } else if (stageIsSelesai && orderStatus === "Selesai") {
    displayStage = "Selesai";
    displayPic = ""; // final, tidak perlu PIC aktif
  } else if (stageIsParallel) {
    // Untuk tahap paralel: tampilkan ringkas progress track.
    const tracks = PARALLEL_STAGE_TRACKS[currentStage] || [];
    const parts = tracks.map(tr => {
      const done = !!(parallelStatus||{})[`${tr.track}_done`];
      const claimedBy = (parallelStatus||{})[`${tr.track}_claimed_by`] || "";
      const picLabel = done ? "selesai" : (claimedBy || "belum diambil");
      return `${tr.label}: ${picLabel}`;
    });
    displayPic = parts.join(" · ");
  } else {
    // Stage normal: PIC aktif = production_pic bila ada; selain itu "Belum diambil".
    displayPic = currentPic ? currentPic : "Belum diambil";
  }

  return (
    <li className={`riwayat-item ${isRevert?"riwayat-item-revert":""} ${isCanceled?"riwayat-item-canceled":""}`} data-testid={`riwayat-item-${idx}`}>
      <div className="riwayat-head">
        <div className="riwayat-head-left">
          {/* Iter 287au — Highlight match pada nomor nota & nama customer. */}
          <span className="riwayat-nota">{highlightMatch(order.order_number, highlightQuery)}</span>
          <strong className="riwayat-customer">{highlightMatch(order.customer_name || "—", highlightQuery)}</strong>
        </div>
        <span className="riwayat-time">{fmtTime(event.at)}</span>
      </div>
      <div className="riwayat-body">
        {/* Iter 332.11 — Thumbnail mockup kecil di tab Riwayat (semua PIC).
            Reuse QueueMockupThumb (endpoint /mockup.preview). Klik → modal besar. */}
        <div className="riwayat-body-row">
          {onOpenMockup && (
            <QueueMockupThumb orderNumber={order.order_number} onOpenMockup={onOpenMockup}
              testId={`riwayat-item-${idx}-thumb`}/>
          )}
          <div className="riwayat-body-main">
            <p className="riwayat-product">
              <strong>{product.custom_name || product.name}</strong> · {product.quantity || 0} pcs
              {product.color && <span className="muted small"> · {product.color}</span>}
              {product.material && <span className="muted small"> · {product.material}</span>}
            </p>
        {/* Iter 302 — Historis: hanya catat pelaksana tahap sebelumnya. Jangan ikut
            memindah/menginterpretasikan state pesanan saat ini.
            Iter 304 — Event ACC Barang Siap (Adit) ditampilkan sebagai "ACC Barang Siap oleh".
            Iter 311 — Event REVERT ditampilkan "Dikembalikan ke Mockup oleh …" (bukan "Sablon Paralel selesai"). */}
        <p className="riwayat-tahap muted small" data-testid={`riwayat-item-${idx}-historis`}>
          {item.isAcc
            ? <><strong>ACC Barang Siap</strong> oleh: <strong>{pelaksana}</strong></>
            : isRevert
              ? <><strong>Dikembalikan ke Mockup</strong> oleh: <strong>{pelaksana}</strong>{stageFrom && stageFrom !== "—" && <> · dari <strong>{stageFrom}</strong></>}</>
              : event.event === "bordir_design_handover"
                ? <><strong>Desain Bordir</strong> diserahkan oleh: <strong>{pelaksana}</strong></>
                : <><strong>{stageFrom}</strong> selesai oleh: <strong>{pelaksana}</strong></>}
        </p>
        {operator?.team === "Bordir" && stageFrom === "Bordir" && !isRevert && (
          <p className="riwayat-tahap small" data-testid={`riwayat-item-${idx}-bordir-status`}>
            {/* Iter 635 — status AKTUAL dari data pesanan, bukan status lokal */}
            Status: <strong>{currentStage !== "Bordir"
              ? (curI > stagesList.indexOf("Bordir") ? "Selesai bordir" : `Tahap ${currentStage || "-"}`)
              : !currentPic ? "Sudah masuk antrean operator"
              : currentPic === pelaksana ? "Belum dikirim ke operator"
              : `Sedang dikerjakan operator · ${currentPic}`}</strong>
          </p>
        )}
        {isRevert && revertReason && (
          <p className="riwayat-reason small" data-testid={`riwayat-item-${idx}-reason`}>
            Alasan: <em>{revertReason}</em>
          </p>
        )}
        {isCanceled && !isRevert && (
          <p className="riwayat-canceled-badge small" data-testid={`riwayat-item-${idx}-canceled-badge`}>
            Penyelesaian dibatalkan — kembali dikerjakan
          </p>
        )}
        {/* Iter 537 — Timeline aktivitas lain pada produk yang SAMA dalam nota ini.
            Grouping di parent sudah menyatukan event per-identitas produk; di sini kami
            tampilkan aktivitas selain primary agar TIDAK ADA aktivitas yang hilang dari UI
            (sesuai mandat Owner: aktivitas riwayat tetap lengkap). */}
        {Array.isArray(item.allActivities) && item.allActivities.length > 1 && (() => {
          const extras = item.allActivities.filter(a => a && a.event && a.event.at !== event.at);
          if (extras.length === 0) return null;
          return (
            <ul className="riwayat-timeline small muted" data-testid={`riwayat-item-${idx}-timeline`}>
              {extras.map((a, i2) => {
                const ev = a.event || {};
                const evFrom = ev.stage_from || "—";
                const evBy = ev.handed_over_by || ev.moved_by || "—";
                const evIsRevert = !!a.isRevert;
                const evIsAcc = !!a.isAcc;
                const evIsCanceled = !!a.isCanceled;
                return (
                  <li
                    key={`${idx}-tl-${i2}-${ev.at || i2}`}
                    className={`riwayat-timeline-item ${evIsRevert?"is-revert":""} ${evIsCanceled?"is-canceled":""}`}
                    data-testid={`riwayat-item-${idx}-timeline-${i2}`}
                  >
                    <span className="riwayat-timeline-time">{fmtTime(ev.at)}</span>
                    <span> · </span>
                    {evIsAcc
                      ? <><strong>ACC Barang Siap</strong> oleh <strong>{evBy}</strong></>
                      : evIsRevert
                        ? <><strong>Dikembalikan ke Mockup</strong> oleh <strong>{evBy}</strong></>
                        : <><strong>{evFrom}</strong> selesai oleh <strong>{evBy}</strong></>}
                    {evIsCanceled && !evIsRevert && (
                      <span className="riwayat-canceled-badge small"> · dibatalkan</span>
                    )}
                  </li>
                );
              })}
            </ul>
          );
        })()}
          </div>{/* /riwayat-body-main */}
        </div>{/* /riwayat-body-row */}
      </div>{/* /riwayat-body */}
      {stagesList.length > 0 && (
        <div className="riwayat-progress" aria-label="Progress produksi terkini">
          {stagesList.map((s, i) => {
            // Jika order sudah "Selesai" (final), semua tahap termasuk Selesai = done.
            const isFinalDone = orderStatus === "Selesai";
            const done = isFinalDone ? true : (i < curI);
            const active = !isFinalDone && i === curI;
            const icon = done ? "✅" : active ? "🔵" : "⏳";
            // Tampilkan label khusus utk stage "Selesai" bila status order masih "Menunggu Finalisasi"
            const label = (s === "Selesai" && orderStatus === "Menunggu Finalisasi") ? "Menunggu Finalisasi" : s;
            return (
              <span key={s} className={`riwayat-stage riwayat-stage-${done?"done":active?"active":"pending"}`}>
                {icon} {label}
                {i < stagesList.length - 1 && <span className="riwayat-arrow"> → </span>}
              </span>
            );
          })}
        </div>
      )}
      <div className="riwayat-foot">
        <span className="muted small" data-testid={`riwayat-item-${idx}-current`}>
          Sekarang di: <strong>{displayStage}</strong>
          {displayPic ? <> · Operator <strong>{displayPic}</strong></> : null}
        </span>
        {/* Iter 309 — Tombol "Kembalikan ke Mockup" khusus event Mockup→next. Backend menegakkan
            otorisasi (pelaksana atau Owner) & guard aktivitas lanjutan. Tombol hanya tampil bila
            event ini adalah transisi dari Mockup, dan user adalah Owner atau operator pelaksana.
            Iter 311 — JANGAN tampilkan tombol pada:
              • event REVERT itu sendiri,
              • event completion yang SUDAH dibatalkan (ada revert menunjuk `at` ini),
              • bila currentStage sudah "Mockup" (idempoten; backend juga menolak). */}
        {!item.isAcc && !isRevert && !isCanceled && currentStage !== "Mockup" && stageFrom === "Mockup" && onRevertMockup && (() => {
          const isOwner = (user?.role || "").toLowerCase() === "owner";
          const isPelaksana = !!operator && operator.name && operator.name === pelaksana;
          if (!isOwner && !isPelaksana) return null;
          const key = `rev::${item.order.order_number}::${item.product.id}`;
          const isBusy = busyKey === key;
          return (
            <button
              type="button"
              className="soft-btn small-btn"
              data-testid={`riwayat-item-${idx}-revert`}
              disabled={isBusy}
              onClick={()=>onRevertMockup(item)}
              style={{color:"#b91c1c"}}
            >
              {isBusy ? "Memproses…" : "Kembalikan ke Mockup"}
            </button>
          );
        })()}
        <button type="button" className="text-btn small-btn" data-testid={`riwayat-item-${idx}-detail`} onClick={onOpen}>Lihat Detail</button>
      </div>
    </li>
  );
}

function StageCorrectModal({ card, onClose, onSubmit, busy }) {
  const { product, stage: currentStage } = card;
  const route = product.production_route || "Perlu Diperiksa";
  const stagesList = ROUTE_STAGES_UI[route] || [];
  const curI = stagesList.indexOf(currentStage);
  const [target, setTarget] = useState(currentStage);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const newI = stagesList.indexOf(target);
  const isBackward = newI >= 0 && curI >= 0 && newI <= curI;
  const noteRequired = isBackward && target !== currentStage;
  const handleSubmit = () => {
    setError("");
    if (target === currentStage) { setError("Pilih tahap yang berbeda"); return; }
    if (!stagesList.includes(target)) { setError("Tahap tidak valid untuk jalur ini"); return; }
    if (noteRequired && !note.trim()) { setError("Catatan alasan wajib diisi untuk koreksi mundur/lintas"); return; }
    onSubmit({ stage: target, note: note.trim() });
  };
  return <div className="modal-overlay" data-testid="stage-correct-overlay" onClick={onClose}>
    <div className="modal" onClick={e=>e.stopPropagation()} data-testid="stage-correct-modal">
      <div className="modal-head"><h3>Koreksi Tahap Produksi</h3><button type="button" className="icon-btn" onClick={onClose} aria-label="Tutup"><X size={18}/></button></div>
      <p className="muted small" style={{marginTop:4}}>Produk: <strong>{product.custom_name || product.name}</strong> · Jalur: <strong>{route}</strong></p>
      <p className="muted small">Tahap saat ini: <strong>{currentStage}</strong></p>
      <label className="field-label" style={{marginTop:14,display:"block"}}>Tahap tujuan</label>
      <select data-testid="stage-correct-target" value={target} onChange={e=>{setTarget(e.target.value);setError("");}} style={{width:"100%",marginTop:6}} disabled={busy}>
        {stagesList.map(s => <option key={s} value={s}>{s}{s===currentStage?" (saat ini)":""}</option>)}
      </select>
      <label className="field-label" style={{marginTop:14,display:"block"}}>Catatan alasan {noteRequired && <span className="required">*</span>}</label>
      <textarea data-testid="stage-correct-note" value={note} onChange={e=>{setNote(e.target.value);setError("");}} style={{width:"100%",marginTop:6}} rows={3} placeholder={noteRequired ? "Wajib diisi untuk koreksi mundur/lintas tahap" : "Opsional untuk maju satu tahap"} disabled={busy}/>
      {error && <div className="alert" data-testid="stage-correct-error" style={{marginTop:12}}>{error}</div>}
      <div className="modal-foot">
        <button type="button" className="soft-btn" data-testid="stage-correct-cancel" onClick={onClose} disabled={busy}>Batal</button>
        <button type="button" className="primary" data-testid="stage-correct-submit" onClick={handleSubmit} disabled={busy}>Simpan Koreksi</button>
      </div>
    </div>
  </div>;
}

// ============================================================================
// Iter 82 — Progress Produksi (Timeline) + Riwayat Proses
// Visual read-only untuk semua operator produksi. Tidak mengubah permission
// atau logic backend — hanya menambah visibility per tahap.
// ============================================================================
function StageTimeline({ route, stagesList, curStage, curI, isLate, parallelStatus, testId }) {
  const isParallelStage = (s) => !!PARALLEL_STAGE_TRACKS[s];
  return (
    <div className="stage-timeline" data-testid={testId} aria-label="Progress produksi">
      <div className="stage-timeline-head">
        <span className="stage-timeline-title">Progress Produksi</span>
        <span className="stage-timeline-route muted small">{route}</span>
      </div>
      <ol className="stage-timeline-list">
        {stagesList.map((s, i) => {
          const done = i < curI;
          const active = i === curI;
          const pending = i > curI;
          const late = active && isLate;
          const StIcon = STAGE_META[s]?.icon;
          let status = "pending";
          let statusIcon = "⏳";
          if (done) { status = "done"; statusIcon = "✅"; }
          else if (late) { status = "late"; statusIcon = "🔴"; }
          else if (active) { status = "active"; statusIcon = "🔵"; }
          const team = STAGE_TEAM_LABEL[s] || "";
          const parallelTracks = PARALLEL_STAGE_TRACKS[s];
          const isThisParallelActive = active && parallelTracks;
          return (
            <li key={s} className={`stage-timeline-node stage-${status}`} data-testid={`${testId}-node-${i}`}>
              <div className="stage-node-dot" aria-hidden="true">
                {done && <CheckCircle2 size={14}/>}
                {active && !late && <span className="stage-node-pulse"/>}
                {late && <AlertTriangle size={14}/>}
                {pending && <span className="stage-node-dot-inner"/>}
              </div>
              <div className="stage-node-info">
                <span className="stage-node-name">
                  {StIcon && <StIcon size={12} aria-hidden="true"/>}
                  <span>{s}</span>
                  <span className="stage-node-status" aria-label={status}>{statusIcon}</span>
                </span>
                {team && !isThisParallelActive && <span className="stage-node-team muted small">{team}</span>}
                {isThisParallelActive && (
                  <span className="stage-node-parallel">
                    {parallelTracks.map((tr) => {
                      const trDone = !!parallelStatus?.[`${tr.track}_done`];
                      const trBy = parallelStatus?.[`${tr.track}_done_by`] || tr.pic;
                      return (
                        <span key={tr.track} className={`stage-node-track ${trDone ? "trk-done" : "trk-wait"}`} title={`${tr.label}: ${trDone ? "selesai" : "sedang dikerjakan"}`}>
                          <span className="trk-pic">{trBy}</span>
                          <span className="trk-status">{trDone ? "✅" : "🔵"}</span>
                        </span>
                      );
                    })}
                  </span>
                )}
              </div>
              {i < stagesList.length - 1 && <div className={`stage-node-link ${done ? "link-done" : ""}`} aria-hidden="true"/>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function HistoryList({ history, testId }) {
  const [open, setOpen] = useState(false);
  const rawItems = [...history].reverse();
  // Iter 312 — Hitung canceledAts dari seluruh history, lalu enrich tiap entry
  //   dengan `isRevert` / `isCanceled` supaya audit trail jelas membedakan
  //   penyelesaian aktif, penyelesaian yang dibatalkan, dan event pengembalian.
  const canceledAts = new Set();
  for (const h of history) {
    if (h?.revert_of_event_at) canceledAts.add(String(h.revert_of_event_at));
  }
  const items = rawItems.map((h) => ({
    ...h,
    isRevert: !!h?.revert_of_event_at && h?.stage_to === "Mockup",
    isCanceled: canceledAts.has(String(h?.at || "")),
    // Parse alasan dari note "Kembalikan ke Mockup (alasan: X)"
    revertReason: (() => {
      if (!(h?.revert_of_event_at && h?.stage_to === "Mockup")) return "";
      const m = String(h?.note || "").match(/alasan:\s*([\s\S]*)\)\s*$/i);
      return (m && m[1]) ? m[1].trim() : "";
    })(),
  }));
  const shown = open ? items : items.slice(0, 3);
  const formatTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth()+1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return iso; }
  };
  return (
    <div className="stage-history" data-testid={testId}>
      <button type="button" className="stage-history-toggle" onClick={()=>setOpen(o=>!o)} data-testid={`${testId}-toggle`}>
        <Clock size={13} aria-hidden="true"/>
        <span>Riwayat Proses ({items.length})</span>
        {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
      </button>
      {(open || items.length <= 3) && (
        <ul className="stage-history-list">
          {shown.map((h, i) => (
            <li key={`${h.at || i}-${i}`} className={`stage-history-item ${h.isRevert?"shi-revert":""} ${h.isCanceled?"shi-canceled":""}`}>
              <div className="stage-history-line">
                <span className="stage-history-arrow">
                  {h.isRevert ? (
                    <strong className="shi-badge-revert">Dikembalikan ke Mockup</strong>
                  ) : (
                    <>
                      <strong>{h.stage_from || "—"}</strong>
                      <span className="stage-history-sep">→</span>
                      <strong>{h.stage_to || "—"}</strong>
                    </>
                  )}
                </span>
                <span className="stage-history-time muted small">{formatTime(h.at)}</span>
              </div>
              {h.isCanceled && !h.isRevert && (
                <div className="shi-canceled-badge small">Penyelesaian dibatalkan — kembali dikerjakan</div>
              )}
              <div className="stage-history-actor muted small">
                <User size={11} aria-hidden="true"/>
                {h.isRevert
                  ? <span>Dikembalikan oleh <b>{h.moved_by || "—"}</b>{h.stage_from && h.stage_from !== "Mockup" && <> · dari <b>{h.stage_from}</b></>}</span>
                  : (h.handed_over_by
                      ? <span>Diserahkan oleh <b>{h.handed_over_by}</b></span>
                      : <span>Dipindahkan oleh <b>{h.moved_by || "—"}</b></span>)}
                {!h.isRevert && h.moved_by && h.moved_by !== h.handed_over_by && <span> · dipindahkan <b>{h.moved_by}</b></span>}
              </div>
              {h.isRevert && h.revertReason
                ? <div className="stage-history-note shi-reason">Alasan: <em>{h.revertReason}</em></div>
                : (h.note && !h.isRevert && <div className="stage-history-note">{h.note}</div>)}
            </li>
          ))}
        </ul>
      )}
      {!open && items.length > 3 && (
        <button type="button" className="stage-history-more" onClick={()=>setOpen(true)} data-testid={`${testId}-showmore`}>
          Lihat semua {items.length} riwayat
        </button>
      )}
    </div>
  );
}

// Iter 332.7 — Compact mockup thumbnail untuk kartu Bordir (Antrean & Dikerjakan).
// Iter 332.8 — Diperluas ke tim Press (Mesin Press), Sublim (Jersey/Sublim), & Selesai.
// Komponen di-rename `BordirMockupThumb` → `QueueMockupThumb` dan menjadi helper
// bersama agar tidak duplikasi kode antar tim.
// Iter 332.10 — Fix endpoint: pakai `/mockup.preview` (file mockup aktual) BUKAN
//   `/nota.thumb.png` (yang merender receipt invoice, bukan file mockup). Konsisten
//   dengan `OrderMockupThumb` di Gudang.
// Klik → onOpenMockup() membuka preview besar (modal existing). Fallback placeholder
// "Mockup belum tersedia" ditampilkan saat <img> gagal dimuat.
function QueueMockupThumb({ orderNumber, onOpenMockup, testId }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [orderNumber]);
  const url = `${API}/orders/${encodeURIComponent(orderNumber || "")}/mockup.preview?_=t`;
  return (
    <button
      type="button"
      className={`queue-bordir-thumb ${failed ? "queue-bordir-thumb-empty" : ""}`}
      onClick={(e)=>{ e.stopPropagation(); if (onOpenMockup) onOpenMockup(); }}
      data-testid={testId || undefined}
      aria-label={`Pratinjau mockup pesanan ${orderNumber}`}>
      {!failed ? (
        <img src={url} alt="" draggable="false" loading="lazy" decoding="async"
          onError={() => setFailed(true)}/>
      ) : (
        <span className="queue-bordir-thumb-placeholder">
          <ImageIcon size={18} aria-hidden="true"/>
          <span>Belum ada mockup</span>
        </span>
      )}
    </button>
  );
}


// Iter 333 — Scan Barcode untuk Operator Bordir (jahit mesin).
// Alur: operator klik tombol → modal input (autofocus, submit on Enter) →
//   POST /api/orders/{number}/barcode-scan (existing endpoint, idempotent) →
//   cek response.context.products ada yg stage === "Bordir" →
//     ✓ panggil onFound(number) supaya opSearch terisi + kartu ter-filter
//     ✗ toast info "pekerjaan belum siap dibordir"
// Pakai HANYA endpoint existing — tidak mengubah workflow/state machine.
// Iter 350 — Refactor: pakai ScanMockupModal (kamera) alih-alih modal ketik-saja.
//   Alur:
//     1. Operator Bordir tap tombol Scan di sebelah input pencarian.
//     2. Buka ScanMockupModal (kamera belakang + izin + manual input fallback).
//     3. Setelah scan sukses (POST /barcode-scan — endpoint existing), hasil
//        order_number dikirim ke onFound(num) → set opSearch → filter kartu di
//        tab Antrean. ScanMockupModal otomatis stop kamera via internal effect.
//   Tidak mengubah workflow/state machine. Validasi stage=Bordir sudah ada di
//   backend; frontend hanya menyaring kartu, user melihat jika nota sedang di
//   Bordir atau tidak via konteks hasil scan.
function OperatorBordirScanButton({ onFound, operator, user }) {
  const [open, setOpen] = useState(false);
  const handleOpenOrder = useCallback((num) => {
    if (num && onFound) onFound(num);
    setOpen(false);
  }, [onFound]);
  return (
    <>
      <button type="button" className="op-scan-barcode-btn" onClick={()=>setOpen(true)}
        data-testid="op-scan-barcode-btn" aria-label="Scan Cari Barang">
        <Camera size={14}/> <span>Scan Cari Barang</span>
      </button>
      <ScanMockupModal
        open={open}
        onClose={()=>setOpen(false)}
        operator={operator}
        user={user}
        onOpenOrder={handleOpenOrder}
        searchOnly
      />
    </>
  );
}


// Iter 287aw — Komponen file panel inline untuk kartu Mockup (Hendar/Een/Mia) di tab
// Dikerjakan. Pakai endpoint `/api/orders/{order}/products/{productId}/files` yang sama
// dengan Detail. Menampilkan jumlah file + Preview Mockup + Ganti File; atau "Belum ada
// mockup" + Unggah Mockup. Preview reuse gaya `pf-preview-modal`.
// Iter 322 — Dukungan "Mockup sudah digabung": tombol opsional untuk menautkan ke
//   mockup produk lain pada nota yang sama (via endpoint link-mockup) tanpa duplikasi file.
function MockupInlineActions({ orderNumber, productId, onFilesChanged, shareableFromProductId, linkedMockupValid: linkedProp, onLinkedChanged, productLabel, productQty, optionalHint }) {
  const [state, setState] = useState({ files: [], loading: true, error: "" });
  const followsPrimary = !linkedProp && state.files.length > 0 && state.files.every(f => f.is_note_primary);
  const linkedMockupValid = linkedProp || followsPrimary;
  const [busyLocal, setBusyLocal] = useState(false);
  const [preview, setPreview] = useState({ url: "", name: "", type: "" });
  const [chooser, setChooser] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: "" }));
    try {
      const r = await axios.get(`${API}/orders/${encodeURIComponent(orderNumber)}/products/${encodeURIComponent(productId)}/files`, { withCredentials:true });
      const files = r.data.files || [];
      setState({ files, loading: false, error: "" });
      if (onFilesChanged) onFilesChanged(files.length);
    } catch (e) {
      const st = e.response?.status;
      const msg = st === 403 ? "Anda tidak berwenang mengakses file mockup." : (e.response?.data?.detail || "Gagal memuat mockup.");
      setState({ files: [], loading: false, error: msg });
    }
  }, [orderNumber, productId, onFilesChanged]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (preview.url) URL.revokeObjectURL(preview.url); }, [preview.url]);

  const uploadFile = useCallback(async (f) => {
    if (!f) return;
    if (f.size > 10*1024*1024) { notify.warning({ title:"Ukuran file terlalu besar", message:"Maksimal 10 MB." }); return; }
    const okTypes = ["image/jpeg","image/png","image/webp","application/pdf"];
    if (!okTypes.includes(f.type) && !/\.(jpg|jpeg|png|webp|pdf)$/i.test(f.name)) {
      notify.warning({ title:"Format tidak didukung", message:"Gunakan JPG, PNG, WebP, atau PDF." }); return;
    }
    try {
      const fd = new FormData(); fd.append("file", f);
      const url = `${API}/orders/${encodeURIComponent(orderNumber)}/products/${encodeURIComponent(productId)}/files`;
      const res = await guardedUpload({ precheckUrl: `${url}/precheck`, file: f, send: (g) => {
        setBusyLocal(true);
        return axios.post(withQuery(url, g), fd, { withCredentials:true, headers:{"Content-Type":"multipart/form-data"} }).finally(() => setBusyLocal(false));
      } });
      if (!res) return;
      notify.success({ title:"Mockup tersimpan", message: f.name });
      await load();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = status === 403 ? "Anda tidak berwenang mengunggah file di tahap ini." : (detail || "Silakan coba lagi.");
      notify.error({ title:"Upload gagal", message: msg });
    } finally { setBusyLocal(false); }
  }, [orderNumber, productId, load]);

  const onPick = (e) => { e?.stopPropagation?.(); fileInputRef.current?.click(); };
  const onFileChange = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (f) await uploadFile(f);
  };

  // Iter 322 — Tautkan/pisahkan mockup gabungan.
  const doLink = useCallback(async () => {
    if (!shareableFromProductId) return;
    setLinkBusy(true);
    try {
      await axios.post(
        `${API}/orders/${encodeURIComponent(orderNumber)}/products/${encodeURIComponent(productId)}/link-mockup`,
        { source_product_id: shareableFromProductId }, { withCredentials: true }
      );
      notify.success({ title: "Mockup digabung", message: "Produk ini sekarang menggunakan mockup pertama." });
      if (onLinkedChanged) onLinkedChanged();
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail || "Silakan coba lagi.";
      notify.error({ title: "Gagal menggabung mockup", message: detail });
    } finally { setLinkBusy(false); }
  }, [orderNumber, productId, shareableFromProductId, onLinkedChanged, load]);

  const doUnlink = useCallback(async () => {
    const ok = await notify.confirm({
      title: "Unggah terpisah untuk produk ini?",
      message: "Produk ini akan dilepas dari mockup gabungan dan membutuhkan file sendiri.",
      primaryLabel: "Ya, Pisahkan",
      secondaryLabel: "Batal",
    });
    if (!ok) return;
    setLinkBusy(true);
    try {
      await axios.post(
        `${API}/orders/${encodeURIComponent(orderNumber)}/products/${encodeURIComponent(productId)}/unlink-mockup`,
        {}, { withCredentials: true }
      );
      notify.success({ title: "Mockup gabungan dipisahkan", message: "Silakan unggah file sendiri." });
      if (onLinkedChanged) onLinkedChanged();
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail || "Silakan coba lagi.";
      notify.error({ title: "Gagal memisahkan mockup", message: detail });
    } finally { setLinkBusy(false); }
  }, [orderNumber, productId, onLinkedChanged, load]);

  const openPreviewOf = async (file) => {
    try {
      const r = await axios.get(`${API}/files/${encodeURIComponent(file.id)}/preview${file.preview_query || ""}`, { withCredentials:true, responseType:"blob" });
      const url = URL.createObjectURL(r.data);
      if (preview.url) URL.revokeObjectURL(preview.url);
      setPreview({ url, name: file.filename, type: file.content_type || "" });
    } catch (e) {
      notify.error({ title:"Gagal memuat pratinjau", message: e.response?.data?.detail || "Silakan coba lagi." });
    }
  };
  const closePreview = () => { if (preview.url) URL.revokeObjectURL(preview.url); setPreview({ url:"", name:"", type:"" }); };

  const files = state.files;
  const activeFile = files[0];

  return <div className="queue-mockup-inline" onClick={e=>e.stopPropagation()} data-testid={`mockup-inline-${productId}`}>
    <input ref={fileInputRef} type="file"
      accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
      style={{display:"none"}} onChange={onFileChange}
      data-testid={`mockup-inline-file-input-${productId}`}/>

    {state.loading && <span className="queue-mockup-status">Memuat file mockup…</span>}

    {!state.loading && state.error && (
      <div className="queue-mockup-row">
        <span className="queue-mockup-status queue-mockup-error">{state.error}</span>
        <button type="button" className="btn-mockup-neutral" onClick={load}
          data-testid={`mockup-inline-retry-${productId}`}>Coba Lagi</button>
      </div>
    )}

    {!state.loading && !state.error && !linkedMockupValid && files.length === 0 && (
      <>
        {/* Iter 494 — COMPACT ROW: hilangkan dua blok besar "Mockup belum tersedia"
            + "Unggah Mockup" (full-width). Ganti dengan satu baris ringkas
            berisi label produk + status + tombol kecil "Unggah". Konsisten
            dengan baris saat mockup sudah ada (`queue-mockup-row-has-compact`). */}
        <div className="queue-mockup-row queue-mockup-row-has queue-mockup-row-has-compact queue-mockup-row-empty-compact"
             data-testid={`mockup-inline-empty-${productId}`}>
          <span className="queue-mockup-thumb queue-mockup-thumb-empty" aria-hidden="true">
            <ImageIcon size={14}/>
          </span>
          <span className="queue-mockup-label">
            {productLabel && (
              <span className="queue-mockup-prodname" data-testid={`mockup-inline-prodname-${productId}`}>
                <strong>{productLabel}</strong>
                {productQty ? <span className="muted small"> · {productQty} pcs</span> : null}
              </span>
            )}
            <span className="queue-mockup-status" data-testid={`mockup-inline-empty-label-${productId}`}>{optionalHint ? "Mockup belum diisi (opsional)" : "Mockup belum tersedia"}</span>
          </span>
          <button type="button" className="btn-mockup-primary btn-mockup-small"
            onClick={onPick} disabled={busyLocal}
            data-testid={`mockup-inline-upload-${productId}`}>
            <ImageIcon size={13}/> <span>{busyLocal ? "Mengunggah…" : "Unggah"}</span>
          </button>
        </div>
        {/* Iter 322 — Aksi "Mockup sudah digabung": kompak, hanya tampil bila eligible. */}
        {shareableFromProductId && (
          <div className="queue-mockup-row queue-mockup-row-link">
            <button type="button" className="btn-mockup-ghost btn-mockup-link btn-mockup-small"
              onClick={doLink} disabled={linkBusy}
              data-testid={`mockup-inline-link-${productId}`}>
              <span>{linkBusy ? "Menautkan…" : "Mockup sudah digabung"}</span>
            </button>
          </div>
        )}
        <UseExistingMockupButton orderNumber={orderNumber} productId={productId}
          onLinked={async () => { if (onLinkedChanged) onLinkedChanged(); await load(); }}/>
      </>
    )}

    {!state.loading && !state.error && linkedMockupValid && (
      <div className="queue-mockup-row queue-mockup-row-linked queue-mockup-row-linked-compact" data-testid={`mockup-inline-linked-${productId}`}>
        {/* Iter 322 — Kondisi linked: pakai file dari produk pertama.
            Iter 332.4 — Compact: hilangkan box dashed besar, label singkat
            "Pakai mockup produk 1", tombol Unggah Terpisah jadi link kecil. */}
        <span className="queue-mockup-thumb" aria-hidden="true">
          <img src={`${API}/orders/${encodeURIComponent(orderNumber)}/mockup.preview?_=t`}
               alt="" draggable="false" loading="lazy" decoding="async"
               onError={(e)=>{ e.currentTarget.style.display = "none"; }}/>
        </span>
        <span className="queue-mockup-label">
          {productLabel && (
            <span className="queue-mockup-prodname" data-testid={`mockup-inline-prodname-${productId}`}>
              <strong>{productLabel}</strong>
              {productQty ? <span className="muted small"> · {productQty} pcs</span> : null}
            </span>
          )}
          <span className="queue-mockup-status" data-testid={`mockup-inline-linked-label-${productId}`}>
            {files.find(f => f.source_order_number) ? `Mockup Bersama dari nota ${files.find(f => f.source_order_number).source_order_number}` : followsPrimary ? "Mengikuti mockup utama" : "Pakai mockup produk 1"}</span>
        </span>
        <button type="button" className="btn-mockup-outline"
          onClick={() => files.length > 1 ? setChooser("preview") : (activeFile ? openPreviewOf(activeFile) : null)}
          disabled={files.length === 0}
          data-testid={`mockup-inline-preview-linked-${productId}`}>
          <ImageIcon size={14}/> <span>Pratinjau</span>
        </button>
        <button type="button" className="btn-mockup-ghost btn-mockup-unlink"
          onClick={followsPrimary ? onPick : doUnlink} disabled={linkBusy || busyLocal}
          data-testid={`mockup-inline-unlink-${productId}`}>
          <span>{linkBusy ? "Memisahkan…" : "Unggah terpisah"}</span>
        </button>
      </div>
    )}

    {!state.loading && !state.error && !linkedMockupValid && files.length > 0 && (
      <div className="queue-mockup-row queue-mockup-row-has queue-mockup-row-has-compact" data-testid={`mockup-inline-has-${productId}`}>
        {/* Iter 314 — Thumbnail kecil (reuse endpoint /orders/{n}/mockup.preview) +
            label "Mockup tersedia" + Preview Mockup outline. Ganti File tetap muncul
            untuk hak akses yang sudah ada (hanya operator Mockup).
            Iter 332.4 — Compact: label produk+qty dipindah ke atas status "Mockup
            tersedia" supaya tiap produk cukup 1 blok tanpa header terpisah.
            Iter 332.10 — Fix endpoint ke /mockup.preview (bukan /nota.thumb.png
            yang merender receipt, bukan file mockup). */}
        <span className="queue-mockup-thumb" aria-hidden="true">
          <img src={`${API}/orders/${encodeURIComponent(orderNumber)}/mockup.preview?_=t`}
               alt="" draggable="false" loading="lazy" decoding="async"
               onError={(e)=>{ e.currentTarget.style.display = "none"; }}
               data-testid={`mockup-inline-thumb-${productId}`}/>
        </span>
        <span className="queue-mockup-label">
          {productLabel && (
            <span className="queue-mockup-prodname" data-testid={`mockup-inline-prodname-${productId}`}>
              <strong>{productLabel}</strong>
              {productQty ? <span className="muted small"> · {productQty} pcs</span> : null}
            </span>
          )}
          <span className="queue-mockup-status">Mockup tersedia</span>
        </span>
        <button type="button" className="btn-mockup-outline"
          onClick={() => files.length > 1 ? setChooser("preview") : openPreviewOf(activeFile)}
          data-testid={`mockup-inline-preview-${productId}`}>
          <ImageIcon size={14}/> <span>Pratinjau</span>
        </button>
        <button type="button" className="btn-mockup-ghost" onClick={onPick} disabled={busyLocal}
          data-testid={`mockup-inline-replace-${productId}`}>
          <span>{busyLocal ? "Mengunggah…" : "Ganti"}</span>
        </button>
      </div>
    )}

    {chooser === "preview" && files.length > 1 && (
      <div className="queue-mockup-chooser" role="dialog" aria-label="Pilih file mockup untuk pratinjau"
        onClick={(e)=>{ if (e.target === e.currentTarget) setChooser(null); }}
        data-testid={`mockup-inline-chooser-${productId}`}>
        <div className="queue-mockup-chooser-inner">
          <div className="queue-mockup-chooser-head">
            <strong>Pilih file untuk Pratinjau</strong>
            <button type="button" className="icon-btn" onClick={()=>setChooser(null)} aria-label="Tutup">
              <X size={16}/>
            </button>
          </div>
          <ul className="queue-mockup-chooser-list">
            {files.map((f, i) => (
              <li key={f.id}>
                <button type="button" className="queue-mockup-chooser-item"
                  onClick={() => { setChooser(null); openPreviewOf(f); }}
                  data-testid={`mockup-inline-chooser-item-${productId}-${i}`}>
                  <strong>{f.filename}</strong>
                  <span className="muted small">
                    {i === 0 ? "Versi aktif · " : ""}
                    {new Date(f.created_at).toLocaleString("id-ID",{timeZone:"Asia/Jakarta"})}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    )}

    {preview.url && (
      <div className="pf-preview-modal" role="dialog" aria-modal="true"
        data-testid={`mockup-inline-preview-modal-${productId}`} onClick={closePreview}>
        <div className="pf-preview-inner" onClick={e=>e.stopPropagation()}>
          <div className="pf-preview-head">
            <strong className="pf-preview-name" title={preview.name}>{preview.name}</strong>
            <button type="button" className="soft-btn small-btn pf-preview-close"
              data-testid={`mockup-inline-preview-close-${productId}`} onClick={closePreview}
              aria-label="Tutup pratinjau">Tutup</button>
          </div>
          <div className="pf-preview-body">
            {preview.type === "application/pdf"
              ? <iframe src={preview.url} title={preview.name} className="pf-preview-frame"/>
              : <img src={preview.url} alt={preview.name} className="pf-preview-img" draggable="false"/>
            }
          </div>
        </div>
      </div>
    )}
  </div>;
}


function MockupSourceTag({ p, noteHasMockup }) {
  const t = mockupSourceLabel(p, noteHasMockup);
  return <span className={`muted small note-mockup-src${t === "Mockup sendiri" ? " is-own" : ""}`} data-testid={`mockup-src-${p.id}`}> · {t}</span>;
}

// Iter 494 — NOTE-LEVEL MOCKUP PANEL (1 nota = 1 mockup gabungan).
//   Mengganti pola "N panel upload per produk" dengan SATU area upload untuk
//   seluruh nota. Produk sumber (`primary`) = produk pertama yang sudah memiliki
//   file sendiri (`has_mockup && !linked_mockup_valid`), atau produk pertama
//   di `mockupTargets` bila belum ada mockup sama sekali.
//   Setelah upload ke primary, produk lain diset `linked_mockup_source_product_id`
//   ke primary via endpoint existing `/link-mockup` (TIDAK mengubah backend).
//   Opsi sekunder "Unggah terpisah per produk" tetap tersedia (fold).
function NoteMockupPanel({ orderNumber, mockupTargets, onChanged }) {
  const primary = useMemo(() => {
    const ownOwner = mockupTargets.find(pc => pc?.product?.has_mockup && !pc?.product?.linked_mockup_valid);
    return ownOwner || mockupTargets[0];
  }, [mockupTargets]);
  const primaryId = primary?.product?.id || "";
  const noteHasMockup = useMemo(() => {
    if (primary?.product?.has_mockup) return true;
    return mockupTargets.some(pc => pc?.product?.linked_mockup_valid || pc?.product?.has_mockup || pc?.product?.follows_note_mockup);
  }, [primary, mockupTargets]);

  const [busy, setBusy] = useState(false);
  const [showSeparate, setShowSeparate] = useState(false);
  const [preview, setPreview] = useState({ url: "", name: "", type: "" });
  const [thumbStamp, setThumbStamp] = useState(() => Date.now());
  const fileInputRef = useRef(null);

  useEffect(() => () => { if (preview.url) URL.revokeObjectURL(preview.url); }, [preview.url]);

  const linkOthersToPrimary = useCallback(async () => {
    if (!primaryId) return;
    const others = mockupTargets.filter(pc => pc?.product?.id && pc.product.id !== primaryId);
    for (const pc of others) {
      const srcPid = pc.product?.linked_mockup_source_product_id || "";
      if (pc.product?.linked_mockup_valid && srcPid === primaryId) continue;
      try {
        await axios.post(
          `${API}/orders/${encodeURIComponent(orderNumber)}/products/${encodeURIComponent(pc.product.id)}/link-mockup`,
          { source_product_id: primaryId }, { withCredentials: true }
        );
      } catch (_e) { /* non-fatal; biarkan operator pakai Unggah terpisah bila gagal */ }
    }
  }, [orderNumber, primaryId, mockupTargets]);

  // onlyPid: Paste Mockup untuk 1 produk saja (tanpa menautkan produk lain). Default = 1 mockup gabungan nota.
  const uploadFile = useCallback(async (f, onlyPid = null) => {
    if (!f || !primaryId) return false;
    if (f.size > 10 * 1024 * 1024) {
      notify.warning({ title:"Ukuran file terlalu besar", message:"Maksimal 10 MB." });
      return false;
    }
    const okTypes = ["image/jpeg","image/png","image/webp","application/pdf"];
    if (!okTypes.includes(f.type) && !/\.(jpg|jpeg|png|webp|pdf)$/i.test(f.name)) {
      notify.warning({ title:"Format tidak didukung", message:"Gunakan JPG, PNG, WebP, atau PDF." });
      return false;
    }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await axios.post(
        `${API}/orders/${encodeURIComponent(orderNumber)}/products/${encodeURIComponent(onlyPid || primaryId)}/files`,
        fd, { withCredentials: true, headers:{"Content-Type":"multipart/form-data"} }
      );
      const only = onlyPid && mockupTargets.find(pc => pc.product.id === onlyPid);
      const msg = only ? `Hanya untuk ${only.product.custom_name || only.product.name || "produk ini"}.`
        : mockupTargets.length > 1 ? `Berlaku untuk ${mockupTargets.length} produk pada nota ini.` : f.name;
      notify.success({ title: "Mockup tersimpan", message: msg });
      if (!onlyPid && mockupTargets.length > 1) await linkOthersToPrimary();
      setThumbStamp(Date.now());
      if (onChanged) onChanged();
      return true;
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const errMsg = status === 403 ? "Anda tidak berwenang mengunggah file di tahap ini." : (detail || "Silakan coba lagi.");
      notify.error({ title: "Upload gagal", message: errMsg });
      return false;
    } finally { setBusy(false); }
  }, [orderNumber, primaryId, mockupTargets, linkOthersToPrimary, onChanged]);
  const [pasteOpen, setPasteOpen] = useState(false);

  const onPick = (e) => { e?.stopPropagation?.(); fileInputRef.current?.click(); };
  const onFileChange = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (f) await uploadFile(f);
  };

  const openPreview = async () => {
    if (!primaryId) return;
    // Produk sumber mockup bisa jadi bukan primary (bila primary pakai link).
    // Ambil daftar file dari produk yang punya file aktif.
    const srcPid = (() => {
      const ownOwner = mockupTargets.find(pc => pc?.product?.has_mockup && !pc?.product?.linked_mockup_valid);
      if (ownOwner) return ownOwner.product.id;
      const linked = mockupTargets.find(pc => pc?.product?.linked_mockup_source_product_id);
      const follower = mockupTargets.find(pc => pc?.product?.note_mockup_source_product_id);
      return linked?.product?.linked_mockup_source_product_id || follower?.product?.note_mockup_source_product_id || primaryId;
    })();
    try {
      const r = await axios.get(
        `${API}/orders/${encodeURIComponent(orderNumber)}/products/${encodeURIComponent(srcPid)}/files`,
        { withCredentials:true }
      );
      const files = r.data.files || [];
      const f = files[0];
      if (!f) { notify.warning({ title:"Mockup belum tersedia", message:"Belum ada file untuk dibuka." }); return; }
      const rb = await axios.get(`${API}/files/${encodeURIComponent(f.id)}/preview${f.preview_query || ""}`, { withCredentials:true, responseType:"blob" });
      const url = URL.createObjectURL(rb.data);
      if (preview.url) URL.revokeObjectURL(preview.url);
      setPreview({ url, name: f.filename, type: f.content_type || "" });
    } catch (e) {
      notify.error({ title:"Gagal membuka mockup", message: e.response?.data?.detail || "Silakan coba lagi." });
    }
  };
  const closePreview = () => { if (preview.url) URL.revokeObjectURL(preview.url); setPreview({ url:"", name:"", type:"" }); };

  return (
    <div className="note-mockup-panel" onClick={e=>e.stopPropagation()} data-testid={`note-mockup-panel-${orderNumber}`}>
      <input ref={fileInputRef} type="file"
        accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
        style={{display:"none"}} onChange={onFileChange}
        data-testid={`note-mockup-file-input-${orderNumber}`}/>

      <DesignFormButton channel="offline" orderNumber={orderNumber} products={mockupTargets.map(pc => pc.product)}/>
      <MockupTemplateButton channel="offline" orderNumber={orderNumber}/>
      {/* Daftar produk compact (hanya tampil bila > 1 produk) */}
      {mockupTargets.length > 1 && (
        <ul className="note-mockup-products" data-testid={`note-mockup-products-${orderNumber}`}>
          {mockupTargets.map((pc, i) => {
            const p = pc.product;
            const pName = p.custom_name || p.name || "Produk";
            const pQty = Number(p.quantity || 0);
            return (
              <li key={p.id||i} className="note-mockup-products-item" data-testid={`note-mockup-prod-${p.id||i}`}>
                <span className="note-mockup-dot" aria-hidden="true">•</span>
                <strong className="note-mockup-prodname">{pName}</strong>
                {pQty ? <span className="muted small"> · {pQty} pcs</span> : null}
                {p.color && <span className="muted small"> · {p.color}</span>}
                <MockupSourceTag p={p} noteHasMockup={noteHasMockup}/>
              </li>
            );
          })}
        </ul>
      )}

      {/* Area mockup level nota: 1 thumbnail + 1 tombol aksi */}
      <div className={`note-mockup-row ${noteHasMockup ? "has" : "empty"}`}
        data-has-mockup={noteHasMockup ? "yes" : "no"}
        data-testid={`note-mockup-row-${orderNumber}`}>
        <span className="note-mockup-thumb" aria-hidden="true">
          {noteHasMockup ? (
            <img src={`${API}/orders/${encodeURIComponent(orderNumber)}/mockup.preview?_=${thumbStamp}`}
              alt="" draggable="false" loading="lazy" decoding="async"
              onError={(e)=>{ e.currentTarget.style.display = "none"; }}
              data-testid={`note-mockup-thumb-${orderNumber}`}/>
          ) : (
            <ImageIcon size={16}/>
          )}
        </span>
        <span className="note-mockup-info">
          <span className="note-mockup-info-title">
            {noteHasMockup ? "Mockup nota tersedia" : "Mockup nota belum tersedia"}
          </span>
          <span className="note-mockup-info-sub muted small">
            {mockupTargets.length === 1
              ? "1 produk pada nota ini"
              : `Berlaku untuk ${mockupTargets.length} produk`}
          </span>
        </span>
        {noteHasMockup && (
          <button type="button" className="btn-mockup-outline btn-mockup-small"
            onClick={openPreview} disabled={busy}
            data-testid={`note-mockup-preview-${orderNumber}`}>
            <ImageIcon size={13}/> <span>Pratinjau</span>
          </button>
        )}
        <button type="button"
          className={noteHasMockup ? "btn-mockup-ghost btn-mockup-small" : "btn-mockup-primary btn-mockup-small"}
          onClick={onPick} disabled={busy || !primaryId}
          data-testid={`note-mockup-upload-${orderNumber}`}>
          {noteHasMockup ? <ImageIcon size={13}/> : <Upload size={17}/>}
          <span>{busy ? "Mengunggah…" : (noteHasMockup ? "Ganti Mockup" : "Unggah Mockup")}</span>
        </button>
        <button type="button" className="btn-mockup-outline btn-mockup-small mo-paste-btn"
          onClick={(e) => { e.stopPropagation(); setPasteOpen(true); }} disabled={busy || !primaryId}
          data-testid={`note-mockup-paste-${orderNumber}`}>
          <ClipboardPaste size={noteHasMockup ? 13 : 17}/><span>Paste Mockup</span>
        </button>
      </div>
      {pasteOpen && <PasteMockupModal orderNumber={orderNumber} targets={mockupTargets}
        onClose={() => setPasteOpen(false)} onSave={(f, pid) => uploadFile(f, pid)}/>}

      {/* Opsi sekunder: unggah terpisah per produk (fold) */}
      {mockupTargets.length > 1 && (
        <div className="note-mockup-separate">
          <button type="button" className="note-mockup-separate-toggle"
            onClick={()=>setShowSeparate(v=>!v)}
            data-testid={`note-mockup-toggle-separate-${orderNumber}`}>
            {showSeparate ? "× Tutup unggah terpisah" : "Butuh unggah berbeda per produk? Buka opsi"}
          </button>
          {showSeparate && (
            <div className="note-mockup-separate-list" data-testid={`note-mockup-separate-list-${orderNumber}`}>
              {mockupTargets.map((pc) => (
                <div key={`sep-${pc.product.id}`} className="queue-mockup-item queue-mockup-item-compact">
                  <MockupInlineActions
                    orderNumber={orderNumber}
                    productId={pc.product.id}
                    productLabel={pc.product.custom_name || pc.product.name || "Produk"}
                    productQty={pc.product.quantity || 0}
                    shareableFromProductId={""}
                    linkedMockupValid={!!pc.product.linked_mockup_valid}
                    optionalHint={mockupTargets.length > 1}
                    onLinkedChanged={() => { if (onChanged) onChanged(); }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {preview.url && (
        <div className="pf-preview-modal" role="dialog" aria-modal="true"
          data-testid={`note-mockup-preview-modal-${orderNumber}`} onClick={closePreview}>
          <div className="pf-preview-inner" onClick={e=>e.stopPropagation()}>
            <div className="pf-preview-head">
              <strong className="pf-preview-name" title={preview.name}>{preview.name}</strong>
              <button type="button" className="soft-btn small-btn pf-preview-close"
                onClick={closePreview} aria-label="Tutup pratinjau">Tutup</button>
            </div>
            <div className="pf-preview-body">
              {preview.type === "application/pdf"
                ? <iframe src={preview.url} title={preview.name} className="pf-preview-frame"/>
                : <img src={preview.url} alt={preview.name} className="pf-preview-img" draggable="false"/>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function SubGroupActions({ subGroup, tab, isAdmin, operator, busy, showLabelPrefix, sectionMode, onAdvance, onClaim, onParallelClaim, onParallelDone, onRelease, onParallelRelease, onChanged, releaseInMenu, releaseSignal }) {
  // Iter 351 — Dialog konfirmasi "Kembalikan ke Antrean".
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const cards = subGroup.cards;
  const primary = subGroup.primary;
  const product = primary.product;
  const stage = primary.stage;
  const route = product.production_route || "";
  const stagesList = ROUTE_STAGES_UI[route] || [];
  const curI = stagesList.indexOf(stage);
  const nextStage = curI >= 0 && curI < stagesList.length - 1 ? stagesList[curI+1] : null;
  const parallelTracks = PARALLEL_STAGE_TRACKS[stage] || null;
  const ps = product.parallel_status || {};
  const myTrackForTab = parallelTracks ? parallelTracks.find(tr => tr.team === (tab?.key || "")) : null;
  const myTrackDone = myTrackForTab ? !!ps[`${myTrackForTab.track}_done`] : false;
  const myTrackClaimedBy = myTrackForTab ? (ps[`${myTrackForTab.track}_claimed_by`] || "") : "";
  const myTrackIsMine = !!(operator && myTrackClaimedBy && myTrackClaimedBy === operator.name);
  const myTrackUnclaimed = !!(myTrackForTab && !myTrackClaimedBy && !myTrackDone);
  const bTeam = product.bordir_team || {};
  // Status Menunggu Dilanjutkan dicek per produk (bukan hanya produk pertama); produk menunggu tidak menghalangi produk lain.
  const isWaitingPc = (pc) => stage === "Bordir" && !!pc.product.bordir_team?.waiting;
  const waitingCards = cards.filter(isWaitingPc);
  const claimCards = cards.filter(pc => !pc.product.production_pic && !isWaitingPc(pc));
  const bWaiting = waitingCards.length > 0;
  const needsClaim = operator && claimCards.length > 0 && !parallelTracks;
  const isMe = operator && product.production_pic === operator.name;
  const showAdvance = !parallelTracks && (isAdmin || (operator && isMe));
  const prefix = showLabelPrefix ? `[${route || "—"}] ` : "";
  const nProd = cards.length;
  const suffix = nProd > 1 ? ` (${nProd} produk)` : "";
  // Iter 594 — SPK Bordir Multi-Operator: tombol "Selesai Tahap Bordir" nonaktif + alasan bila belum lengkap (backend tetap memvalidasi).
  const bordirGate = operator?.team === "Operator Bordir" && stage === "Bordir" && String(primary.order?.source || "").toLowerCase() !== "shopee";
  const [bordirReady, refreshBordirReady] = useBordirReadiness(cards.map(pc => ({ order_number: pc.order.order_number, product_id: pc.product.id })), bordirGate && showAdvance);
  const bordirMulti = Object.values(bordirReady).filter(r => r?.multi);
  const bordirOp = operator?.team === "Operator Bordir" && !isAdmin && stage === "Bordir" && String(primary.order?.source || "").toLowerCase() !== "shopee";
  const bRole = teamRole(bTeam, product.production_pic, operator?.name);
  const claimOn = bordirOp && sectionMode !== "semua" && !bWaiting && (!product.production_pic || bRole === "holder" || bRole === "active");
  const [finishDlg, setFinishDlg] = useState(false);
  const [claimSum, refreshClaimSum] = useClaimSummary(cards.map(pc => ({ order_number: pc.order.order_number, product_id: pc.product.id })), claimOn);
  const bordirBlocked = bordirMulti.filter(r => !r.ready);
  // Iter 351 — Tombol Release tampil HANYA di tab Dikerjakan ("sedang") untuk operator pemegang.
  //   Owner/Admin TIDAK melihat tombol ini (sesuai aturan: no bypass).
  const canReleaseRegular = !!(sectionMode === "sedang" && operator && !isAdmin && !parallelTracks && isMe && onRelease);
  const isBordirStage = product?.production_stage === "Bordir";
  const canReleaseParallel = !!(sectionMode === "sedang" && operator && !isAdmin && parallelTracks && myTrackForTab && myTrackIsMine && !myTrackDone && onParallelRelease);
  // Kartu Mockup: "Kembalikan ke Antrean" dipicu dari menu Lainnya (dialog konfirmasi yang sama).
  useEffect(() => { if (releaseSignal && (canReleaseRegular || canReleaseParallel)) setConfirmRelease(true); }, [releaseSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  const doRelease = () => {
    setConfirmRelease(false);
    if (canReleaseRegular) {
      for (const pc of cards) onRelease(pc);
    } else if (canReleaseParallel && myTrackForTab) {
      for (const pc of cards) onParallelRelease(pc, myTrackForTab.track);
    }
  };

  // Pekerjaan bertim / sudah memakai Catat Hasil → Selesai membuka formulir dua kolom dulu (hasil pemegang tidak hilang).
  const finishNeedsForm = bordirOp && cards.length === 1 && bordirMulti.length === 0
    && needsFinishForm(claimSum[`${primary.order.order_number}::${product.id}`], product.bordir_team, operator?.name);
  if (bordirOp && sectionMode === "semua") return <>
    <TeamStatus team={bTeam} holderName={product.production_pic} testid={`prod-team-status-${product.id}`}/>
  </>;
  return <>
    {bordirOp && (product.production_pic || bWaiting) && <TeamStatus team={bTeam} holderName={product.production_pic} testid={`prod-team-status-${product.id}`}/>}
    {bordirOp && bWaiting && <div className="queue-card-foot">{waitingCards.map(pc => <ResumeScanButton key={pc.product.id} operator={operator} onChanged={onChanged} testid={`prod-team-resume-${pc.product.id}`}/>)}</div>}
    {parallelTracks && myTrackForTab && !myTrackDone && myTrackUnclaimed && operator && !isAdmin && <div className="queue-card-foot">
      <button type="button" data-testid={`prod-parallel-claim-${product.id}-${myTrackForTab.track}`} className="soft-btn small-btn" disabled={busy} onClick={()=>{ for (const pc of cards) onParallelClaim(pc, myTrackForTab.track); }}>
        {prefix}Mulai — {myTrackForTab.label}{suffix}
      </button>
    </div>}
    {parallelTracks && myTrackForTab && !myTrackDone && myTrackIsMine && <div className="queue-card-foot">
      <button type="button" data-testid={`prod-parallel-done-${product.id}-${myTrackForTab.track}`} className="primary small-btn" disabled={busy} onClick={()=>{ for (const pc of cards) onParallelDone(pc, myTrackForTab.track); }}>
        {prefix}Selesai — {myTrackForTab.label}{suffix}
      </button>
    </div>}
    {parallelTracks && myTrackForTab && !myTrackDone && !myTrackUnclaimed && !myTrackIsMine && operator && !isAdmin && <div className="queue-card-foot">
      <span className="queue-status-label" data-testid={`prod-parallel-taken-${product.id}`}>{prefix}Sedang dikerjakan oleh {myTrackClaimedBy}</span>
    </div>}
    {parallelTracks && myTrackForTab && !myTrackDone && isAdmin && <div className="queue-card-foot">
      <button type="button" data-testid={`prod-parallel-done-${product.id}-${myTrackForTab.track}`} className="primary small-btn" disabled={busy} onClick={()=>{ for (const pc of cards) onParallelDone(pc, myTrackForTab.track); }}>
        {prefix}Selesai — {myTrackForTab.label}{suffix}
      </button>
    </div>}
    {parallelTracks && myTrackForTab && myTrackDone && <div className="queue-card-foot">
      <span className="queue-status-label" data-testid={`prod-parallel-mine-done-${product.id}`}>{prefix}✓ Track {myTrackForTab.label} sudah selesai. Menunggu track lain…</span>
    </div>}
    {needsClaim && !isAdmin && <div className="queue-card-foot">
      <button type="button" data-testid={`prod-claim-${product.id}`} className="soft-btn small-btn" disabled={busy} onClick={()=>{ for (const pc of claimCards) onClaim(pc); }}>
        {/* Iter 287aw — Label khusus stage Mockup (tim Mia/Een/Hendar): "Mulai Mockup".
            Iter 332.3 — Diringkas jadi "Kerjakan" di Antrean untuk konsistensi UX operator.
            Iter 333.2 — Operator Bordir juga pakai label "Kerjakan". */}
        {operator?.team === "Mesin Press" ? `${prefix}MULAI PRESS${suffix}` : `${prefix}Kerjakan${suffix}`}
      </button>
    </div>}
    {showAdvance && nextStage && (() => {
      const isMp = ["shopee", "tokopedia"].includes(String(primary.order?.source || "").toLowerCase());
      const allHaveMockup = cards.every(pc => !!pc?.product?.has_mockup || (!isMp && !!pc?.product?.follows_note_mockup));
      const needMockupFile = stage === "Mockup" && !allHaveMockup;
      if (needMockupFile) {
        return isMp ? null : <div className="queue-card-foot"><span className="queue-status-label" data-testid={`prod-need-note-mockup-${product.id}`}>Unggah minimal 1 mockup untuk nota ini</span></div>;
      }
      return <div className="queue-card-foot">
        <button type="button" data-testid={`prod-advance-${product.id}`} className={`primary ${bordirOp ? "btm-main" : "small-btn"}`}
          disabled={busy || bordirBlocked.length > 0}
          onClick={()=>{ if (finishNeedsForm) { setFinishDlg(true); return; } for (const pc of cards) onAdvance(pc); }}>
          {prefix}{bordirMulti.length ? `Selesai Tahap Bordir${suffix}` : operator?.team === "Mesin Press" ? `SELESAI PRESS${suffix}` : (stage === "Mockup" || operator?.team === "Operator Bordir") ? "Selesai" : `Selesai Tahap → ${nextStage}${suffix}`}
        </button>
        {bordirBlocked.map((r, i) => <ReadinessList key={i} r={r} testid={`prod-bordir-reasons-${product.id}-${i}`}/>)}
        {(stage === "Mockup" || operator?.team === "Operator Bordir") && nProd === 1 && (
          <span className="queue-stage-next-caption" data-testid={`prod-next-stage-${product.id}`}>
            Berikutnya: {nextStage}
          </span>
        )}
      </div>;
    })()}
    {bordirOp && sectionMode === "sedang" && cards.map(pc => <BordirTeamActions key={`team-${pc.product.id}`} orderNumber={pc.order.order_number} productId={pc.product.id}
      team={pc.product.bordir_team} holderName={pc.product.production_pic} meName={operator?.name} label={`${pc.order.order_number} · ${pc.product.name || ""}`}
      onChanged={() => { onChanged && onChanged(); refreshClaimSum(); refreshBordirReady(); }} testid={pc.product.id}/>)}
    {finishDlg && <FinishWithClaim orderNumber={primary.order.order_number} productId={product.id} label={`${primary.order.order_number} · ${product.name || ""}`}
      onFinish={() => onAdvance(primary)} onClose={() => { setFinishDlg(false); onChanged && onChanged(); }}/>}
    {/* Iter 589 — Bagi Pekerjaan Bordir lama: hanya untuk pekerjaan yang SUDAH memakai pembagian (pekerjaan baru memakai Catat Hasil Saya). */}
    {sectionMode === "sedang" && (operator?.team === "Operator Bordir" || isAdmin) && stage === "Bordir" && !!product.production_pic && bordirMulti.length > 0
      && String(primary.order?.source || "").toLowerCase() !== "shopee" && (
      <div className="queue-card-foot" style={{ flexDirection: "column", alignItems: "stretch" }}>
        <button type="button" className={`soft-btn small-btn ${showSplit ? "bt-close-btn" : ""}`} onClick={()=>setShowSplit(v=>!v)} aria-expanded={showSplit} data-testid={`prod-bordir-split-${product.id}`}>
          {isMe ? (showSplit ? "Tutup Bagi Pekerjaan" : "Bagi Pekerjaan") : (showSplit ? "Tutup Tim Bordir" : "Lihat Tim Bordir")}
        </button>
        {showSplit && cards.map(pc => <BordirSplitPanel key={pc.product.id} orderNumber={pc.order.order_number} productId={pc.product.id} onChanged={refreshBordirReady}/>)}
      </div>
    )}
    {isBordirStage && product.bordir_prior_progress && <div className="queue-card-foot" style={{color:"#b45309", fontWeight:700, fontSize:12}}
      data-testid={`prod-bordir-prior-${product.id}`}>{priorProgressText(product.bordir_prior_progress)}</div>}
    {/* Iter 351 — Tombol sekunder "Kembalikan ke Antrean", terpisah jelas dari tombol Selesai. */}
    {(canReleaseRegular || canReleaseParallel) && !releaseInMenu && (
      <div className="queue-card-foot queue-card-foot-release">
        <button
          type="button"
          data-testid={`prod-release-${product.id}${canReleaseParallel ? `-${myTrackForTab?.track}` : ""}`}
          className="soft-btn small-btn queue-release-btn"
          disabled={busy}
          onClick={()=>{ if (isBordirStage && canReleaseRegular) { for (const pc of cards) onRelease(pc); } else setConfirmRelease(true); }}
          title={isBordirStage && canReleaseRegular ? "Batalkan pekerjaan bordir ini (wajib alasan)" : "Kembalikan pekerjaan ini ke antrean"}
        >
          {isBordirStage && canReleaseRegular ? "✕ Batalkan Pekerjaan" : "↶ Kembalikan ke Antrean"}
        </button>
      </div>
    )}
    {confirmRelease && (
      <div className="queue-release-overlay" onClick={()=>!busy && setConfirmRelease(false)}>
        <div className="queue-release-modal" onClick={e=>e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="release-title">
          <h3 id="release-title" className="queue-release-title">Kembalikan ke Antrean</h3>
          <p className="queue-release-msg">
            Kembalikan pekerjaan ini ke antrean agar bisa diambil operator lain?
          </p>
          {nProd > 1 && (
            <p className="queue-release-meta muted small">
              {nProd} produk sejenis akan dikembalikan sekaligus.
            </p>
          )}
          <div className="queue-release-foot">
            <button type="button" className="soft-btn small-btn" onClick={()=>setConfirmRelease(false)} disabled={busy} data-testid={`release-cancel-${product.id}`}>Batal</button>
            <button type="button" className="primary small-btn" onClick={doRelease} disabled={busy} data-testid={`release-confirm-${product.id}`}>Kembalikan</button>
          </div>
        </div>
      </div>
    )}
    {!parallelTracks && !nextStage && <div className="queue-card-foot"><span className="queue-status-label" data-testid={`prod-final-${product.id}`}>{prefix}Tahap akhir</span></div>}
  </>;
}

export function TeamProductCard({ card, tab, isAdmin, operator, user, busy, onOpen, onOpenMockup, onAdvance, onClaim, onSetRoute, onAdminCorrect, onParallelDone, onParallelClaim, onRelease, onParallelRelease, onChanged, sectionMode, highlightQuery }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [moOpen, setMoOpen] = useState(false);
  const [relSig, setRelSig] = useState(0);
  const { order, product, stage } = card;
  const productCards = Array.isArray(card.productCards) && card.productCards.length > 0 ? card.productCards : [card];
  const subGroups = Array.isArray(card.subGroups) && card.subGroups.length > 0
    ? card.subGroups
    : [{ key: "single", cards: productCards, primary: productCards[0] }];
  const isGroup = productCards.length > 1;
  const totalPcs = productCards.reduce((s, pc) => {
    const p = pc.product;
    const sizes = Array.isArray(p.sizes) ? p.sizes : [];
    const n = Number(p.quantity || 0) || sizes.reduce((ss, x) => ss + Number(x.quantity || 0), 0);
    return s + n;
  }, 0);
  const route = product.production_route || "Perlu Diperiksa";
  const stagesList = ROUTE_STAGES_UI[route] || [];
  const curI = stagesList.indexOf(stage);
  const productLabel = product.custom_name || product.name || "Produk";
  const isPerluDiperiksa = tab?.key === "Perlu Diperiksa";
  const sablonTypeBadge = (stage === "Sablon Paralel" && tab?.key === "Sablon/DTF") ? (product.sablon_type || "") : "";
  const StageIcon = STAGE_META[stage]?.icon;
  const dlStatus = String(product.deadline_status || "Aman");
  const isLate = dlStatus === "Terlambat";
  const isNearDl = dlStatus === "Hampir Deadline";
  const isDesainBordir = operator?.team === "Bordir" || operator?.team === "Operator Bordir";
  const dlLabel = (d) => !isDesainBordir ? `Deadline ${fmtDLShort(d)}` : (d ? `Target Selesai ${fmtDLShort(d)}` : "Target Selesai belum tersedia");
  const uniformStage = subGroups.every(sg => sg.primary.stage === subGroups[0].primary.stage);
  const uniformRoute = subGroups.every(sg => (sg.primary.product.production_route||"") === (subGroups[0].primary.product.production_route||""));
  // Iter 334 — Multi-route summary: bila subGroups > 1, hitung tally produk per
  //   next-stage supaya operator langsung paham "ini masih 1 nota, cuma punya N cabang".
  //   Tidak mengubah grouping logic — hanya tampilan ringkas.
  const subGroupSummary = subGroups.length > 1 ? subGroups.map(sg => {
    const sp = sg.primary;
    const r = sp.product.production_route || "";
    const sl = ROUTE_STAGES_UI[r] || [];
    const i = sl.indexOf(sp.stage);
    const next = (i >= 0 && i < sl.length - 1) ? sl[i+1] : (sp.stage === "Persiapan Paralel" ? "Bordir" : "Tahap akhir");
    const prodCount = sg.cards.length;
    return { route: r, next, prodCount };
  }).reduce((acc, cur) => {
    // Group by (route, next): sum prodCount.
    const key = `${cur.route}::${cur.next}`;
    const found = acc.find(x => x.key === key);
    if (found) found.prodCount += cur.prodCount;
    else acc.push({ ...cur, key });
    return acc;
  }, []) : [];
  // Iter 287ar — Simplified card mode untuk operator team (non-Gudang, non-admin viewing).
  //   Sembunyikan timeline, history, chip Aman, Jalur/Tahap line, dan ProductFilesPanel.
  //   Semua detail tetap bisa diakses via tombol Detail.
  const simplified = !!operator && operator.team !== "Gudang" && !operator.is_admin_toko;
  // Iter 287aw — Panel inline mockup khusus tim Mockup saat tab Dikerjakan (sudah claim)
  // atau Antrean belum (tidak ditampilkan di antrean — hanya setelah mulai). File-sumber
  // sama dengan Detail (endpoint products/{productId}/files).
  // Iter 316 — Diperluas ke kartu GABUNGAN (isGroup): render 1 panel inline per produk,
  //   plus ringkasan "Mockup tersedia: X dari Y produk" di atasnya.
  const showMockupInline = simplified
    && operator?.team === "Mockup"
    && stage === "Mockup"
    && sectionMode === "sedang";
  // Daftar produk target untuk panel mockup gabungan: hanya produk di tahap Mockup saja.
  //   Produk yang sudah di stage lain tidak perlu panel upload mockup di sini.
  const mockupTargets = showMockupInline
    ? productCards.filter(pc => pc?.product?.id && pc?.stage === "Mockup")
    : [];
  const mockupHaveCount = mockupTargets.filter(pc => !!pc?.product?.has_mockup).length;
  const mockupTotalCount = mockupTargets.length;
  const mockupAllReady = mockupTotalCount > 0 && mockupHaveCount === mockupTotalCount;
  // Format sizes compact: "M×1, L×1" bila ada; "lihat detail" bila kompleks; "" bila kosong/N/A.
  // Iter 287at — Pisahkan jumlah dari ukuran dengan "×" (bukan spasi) agar tidak terbaca
  // "N/A 1" sebagai "ukuran N/A sebanyak 1". Entry dengan ukuran kosong atau "N/A" diskip —
  // tidak mengarang ukuran. Jika semua skip, kembalikan "" → caller boleh tampilkan
  // "Ukuran belum diisi".
  const fmtSizes = (p) => {
    const raw = Array.isArray(p.sizes) ? p.sizes.filter(s => Number(s.quantity||0) > 0) : [];
    const sizes = raw.filter(s => {
      const sz = String(s.size || "").trim();
      return sz && sz.toUpperCase() !== "N/A";
    });
    if (!sizes.length) return "";
    if (sizes.length > 4) return "lihat detail";
    return sizes.map(s => `${s.size}×${s.quantity}`).join(", ");
  };
  const sizesTxt = !isGroup ? fmtSizes(product) : "";
  // Kartu Mockup Offline (tim Mockup): ringkas produk + status mockup + Detail/Lainnya. Hanya tampilan.
  const moUi = simplified && operator?.team === "Mockup";
  const moTargets = moUi ? productCards.filter(pc => pc?.product?.id && pc?.stage === "Mockup") : [];
  const moCanRelease = moUi && sectionMode === "sedang" && !!onRelease && !isAdmin
    && productCards.some(pc => pc?.product?.production_pic === operator?.name);
  // Iter 287at — Deteksi: ada entry size tapi semua label kosong/N/A → "Ukuran belum diisi".
  const sizesEmptyLabel = !isGroup && !sizesTxt && Array.isArray(product.sizes)
    && product.sizes.some(s => Number(s.quantity||0) > 0)
    && product.sizes.every(s => {
      const sz = String(s.size || "").trim();
      return !sz || sz.toUpperCase() === "N/A";
    });
  return <article className="queue-card" data-stage={stageKey(stage)}
    data-accent={product?.deadline_status === "Terlambat" ? "late" : product?.production_pic ? "work" : "queue"} data-has-mockup={(() => {
    // Iter 316 — Atribut `data-has-mockup` kini merepresentasikan status LEVEL KARTU:
    //   "yes" bila semua produk tahap Mockup pada kartu ini sudah punya file;
    //   "no" bila ada minimal 1 produk tanpa file. CSS combo menyesuaikan grid layout.
    if (showMockupInline && mockupTargets.length > 0) return mockupAllReady ? "yes" : "no";
    return product?.has_mockup ? "yes" : "no";
  })()} data-dl={isLate ? "late" : isNearDl ? "near" : "ok"} data-testid={`prod-card-${order.order_number}-${product.id}`}>
    <div className="queue-card-head" onClick={onOpen} data-testid={`prod-card-open-${product.id}`}>
      <div className="queue-card-head-main">
        {/* Iter 287au — Highlight match untuk search bar operator. */}
        <strong className="queue-card-customer">{highlightMatch(order.customer_name || "—", highlightQuery)}</strong>
        <span className="card-kicker queue-card-order">{highlightMatch(order.order_number, highlightQuery)}</span>
        {moUi && <MockupWaLine wa={order.whatsapp} testid={`prod-card-wa-${product.id}`}/>}
        {order.is_repeat_order && <RepeatOrderBadge active repeatRef={order.repeat_ref} context="offline" size="compact"
          testid={`prod-card-repeat-${product.id}`}/>}
      </div>
      <div className="queue-card-head-right">
        {/* Iter 287ar — Mode simplified: hanya tampilkan tanggal "Deadline X Okt" + chip khusus
            bila Hampir Deadline / Terlambat. Chip "Aman" dihilangkan. */}
        {moUi ? (
          <span className={`queue-deadline-plain mo-dl ${isLate?"is-late":isNearDl?"is-near":""}`}
            title={isLate ? "Terlambat" : isNearDl ? "Hampir deadline" : undefined}
            data-testid={`prod-card-deadline-${product.id}`}><Calendar size={13} aria-hidden="true"/>{isLate ? "Terlambat · " : ""}{fmtDLShort(order.deadline)}</span>
        ) : simplified ? (
          <>
            <span className={`queue-deadline-plain ${isLate?"is-late":isNearDl?"is-near":""}`}
              data-testid={`prod-card-deadline-${product.id}`}>{dlLabel(order.deadline)}</span>
            {(isLate || isNearDl) && (
              <span className={`deadline-pill-lite ${isLate?"is-late":"is-near"}`}
                data-testid={`prod-card-deadline-status-${product.id}`}>{isLate?"Terlambat":"Hampir deadline"}</span>
            )}
          </>
        ) : (
          <>
            <span className="queue-deadline" data-testid={`prod-card-deadline-${product.id}`}>{isDesainBordir ? dlLabel(order.deadline) : `DL ${order.deadline || "—"}`}</span>
            <span className={`deadline-pill dl-${(product.deadline_status||"Aman").replaceAll(" ","-")}`}
              data-testid={`prod-card-deadline-status-${product.id}`}>{product.deadline_status || "Aman"}</span>
          </>
        )}
      </div>
    </div>
    {moUi ? <div className="queue-card-body mo-body" onClick={onOpen}>
      <MockupSummaryBox productCards={productCards} totalPcs={totalPcs} fmtSizes={fmtSizes} testid={`prod-card-summary-${product.id}`}/>
      {isGroup && moOpen && <MockupProductList productCards={productCards} fmtSizes={fmtSizes} testid={`prod-card-group-${order.order_number}`}/>}
      {!isGroup && product.note && <p className="queue-note">{product.note}</p>}
    </div> : <div className="queue-card-body" onClick={onOpen}>
      {isGroup ? (
        <>
          <p className="queue-products queue-products-group" data-testid={`prod-card-group-${order.order_number}`}>
            <strong>{productCards.length} produk</strong> · Total <strong>{totalPcs} pcs</strong>
            {sablonTypeBadge && <span className={`sablon-type-badge sablon-type-${sablonTypeBadge.toLowerCase()}`} data-testid={`prod-card-sablon-type-${product.id}`}>{sablonTypeBadge}</span>}
          </p>
          <ul className="queue-group-list" style={{margin:"4px 0 6px",padding:"0 0 0 18px"}}>
            {productCards.map((pc, i) => {
              const p = pc.product;
              const pName = p.custom_name || p.name || "Produk";
              const pQty = Number(p.quantity || 0) || (Array.isArray(p.sizes) ? p.sizes.reduce((s,x)=>s+Number(x.quantity||0),0) : 0);
              const pSizes = fmtSizes(p);
              return <li key={p.id||i} style={{margin:"2px 0"}} data-testid={`prod-card-group-item-${p.id||i}`}>
                <strong>{pName}</strong> · {pQty} pcs
                {p.color && <span className="muted small"> · {p.color}</span>}
                {p.material && <span className="muted small"> · {p.material}</span>}
                {pSizes && <span className="muted small"> · {pSizes}</span>}
                {p.follows_note_mockup && <span className="muted small" data-testid={`prod-card-follows-mockup-${p.id}`}> · Mengikuti mockup utama</span>}
              </li>;
            })}
          </ul>
        </>
      ) : (
        <>
          <p className="queue-products" data-testid={`prod-card-name-${product.id}`}>
            <strong>{productLabel}</strong> · {product.quantity||0} pcs
            {sablonTypeBadge && <span className={`sablon-type-badge sablon-type-${sablonTypeBadge.toLowerCase()}`} data-testid={`prod-card-sablon-type-${product.id}`}>{sablonTypeBadge}</span>}
          </p>
          {simplified ? (
            (product.color || product.material || sizesTxt || sizesEmptyLabel) && (
              <p className="queue-meta queue-variant-line" data-testid={`prod-card-variant-${product.id}`}>
                {[product.color, product.material].filter(Boolean).join(" · ")}
                {(product.color || product.material) && (sizesTxt || sizesEmptyLabel) && " · "}
                {sizesTxt && <>Ukuran: <span>{sizesTxt}</span></>}
                {!sizesTxt && sizesEmptyLabel && <span className="queue-pic-empty">Ukuran belum diisi</span>}
              </p>
            )
          ) : (
            (product.material || product.color) && (
              <p className="queue-meta queue-material-line" data-testid={`prod-card-material-${product.id}`}>
                {product.material && <span className="queue-chip queue-chip-material">Bahan: <strong>{product.material}</strong></span>}
                {product.color && <span className="queue-chip queue-chip-color">Warna: <strong>{product.color}</strong></span>}
              </p>
            )
          )}
        </>
      )}
      {!simplified && <p className="queue-meta queue-stage-line">
        {StageIcon && <StageIcon size={13} className="queue-stage-icon" aria-hidden="true"/>}
        Jalur: <strong data-testid={`prod-card-route-${product.id}`}>{uniformRoute ? route : "Beragam per produk"}</strong> · Tahap: <strong>{uniformStage ? stage : "Beragam per produk"}</strong>
      </p>}
      {!isGroup && !PARALLEL_STAGE_TRACKS[stage] && <p className="queue-meta queue-pic-line">
        {/* Iter 287ar — Fallback "Belum diambil operator" ketika PIC kosong.
            Iter 509 — Mode `simplified` dipakai Operator Bordir (Offline) → label
            "Operator:" (bukan "PIC:") agar identitas operator lebih langsung
            terbaca. Mode non-simplified tetap "PIC saat ini:" (generic). */}
        {simplified ? (
          product.production_pic
            ? <>Operator: <strong data-testid={`prod-card-pic-${product.id}`}>{product.production_pic}</strong></>
            : <span className="queue-pic-empty" data-testid={`prod-card-pic-${product.id}`}>Belum diambil operator</span>
        ) : (
          <>Operator saat ini: <strong data-testid={`prod-card-pic-${product.id}`}>{product.production_pic || "—"}</strong></>
        )}
      </p>}
      {!isGroup && PARALLEL_STAGE_TRACKS[stage] && (
        /* Iter 287at — Status paralel: 2 indikator kecil terpisah.
           - "done" (tr._done=true) → "Selesai · {nama}"
           - "claimed_by" dan belum done → "Dikerjakan · {nama}"
           - belum keduanya → "Belum dimulai"
           Nama PIC saja TIDAK dianggap sebagai bukti selesai — hanya flag `_done`. */
        simplified ? (
          <div className="queue-parallel-row" data-testid={`prod-card-parallel-status-${product.id}`}>
            {(PARALLEL_STAGE_TRACKS[stage]||[]).map((tr) => {
              const ps = product.parallel_status || {};
              const done = !!ps[`${tr.track}_done`];
              const claimedBy = ps[`${tr.track}_claimed_by`] || "";
              const doneBy = ps[`${tr.track}_done_by`] || "";
              let label, cls;
              if (done) { label = `Selesai · ${doneBy || "—"}`; cls = "parallel-done"; }
              else if (claimedBy) { label = `Dikerjakan · ${claimedBy}`; cls = "parallel-inprogress"; }
              else { label = "Belum dimulai"; cls = "parallel-wait"; }
              return <span key={tr.track} className={`queue-parallel-chip ${cls}`}
                data-testid={`prod-card-parallel-${tr.track}-${product.id}`}>
                <strong>{tr.label}:</strong> {label}
              </span>;
            })}
          </div>
        ) : (
          <p className="queue-meta parallel-status-line" data-testid={`prod-card-parallel-status-${product.id}`}>
            Status paralel: {(PARALLEL_STAGE_TRACKS[stage]||[]).map(tr => ({
              ...tr,
              done: !!(product.parallel_status||{})[`${tr.track}_done`],
              claimed_by: (product.parallel_status||{})[`${tr.track}_claimed_by`] || "",
            })).map((tr,i,arr) => {
              const ps = product.parallel_status || {};
              const label = tr.done ? `✓ ${ps[`${tr.track}_done_by`]||"selesai"}` : (tr.claimed_by ? `${tr.claimed_by} · dikerjakan` : "belum dikerjakan");
              const cls = tr.done ? "parallel-done" : (tr.claimed_by ? "parallel-inprogress" : "parallel-wait");
              return <span key={tr.track} className={`parallel-chip ${cls}`}>{tr.label}: {label}{i<arr.length-1?" · ":""}</span>;
            })}
          </p>
        )
      )}
      {!isGroup && product.note && <p className="queue-note">{product.note}</p>}
    </div>}
    {moUi && moTargets.length > 0 && <MockupStatusRow targets={moTargets} isGroup={isGroup} open={moOpen}
      onToggle={() => setMoOpen(v => !v)} testid={`prod-card-mockup-${product.id}`}/>}

    {/* Iter 82 — Progress Produksi (Timeline). Tampilkan hanya utk kartu tunggal.
        Iter 287ar — Mode simplified (operator team): timeline disembunyikan — akses via Detail. */}
    {!simplified && !isGroup && !isPerluDiperiksa && stagesList.length > 1 && (
      <StageTimeline route={route} stagesList={stagesList} curStage={stage} curI={curI} isLate={isLate} parallelStatus={product.parallel_status || {}} testId={`prod-card-timeline-${product.id}`}/>
    )}
    {/* Iter 82 — Riwayat Proses (collapsible).
        Iter 287ar — Mode simplified: history disembunyikan — akses via Detail. */}
    {!simplified && !isGroup && !isPerluDiperiksa && Array.isArray(product.production_pic_history) && product.production_pic_history.length > 0 && (
      <HistoryList history={product.production_pic_history} testId={`prod-card-history-${product.id}`}/>
    )}

    {/* Iter 287ar — Mode simplified: hilangkan ProductFilesPanel di kartu — akses via Detail. */}
    {!simplified && !isGroup && product.id && <div className="queue-card-files" onClick={e=>e.stopPropagation()}>
      <ProductFilesPanel orderNumber={order.order_number} productId={product.id} compact/>
    </div>}
    {isPerluDiperiksa && isAdmin && <div className="queue-card-foot prod-route-picker" data-testid={`prod-route-picker-${product.id}`}>
      <span className="queue-status-label">Tentukan jalur:</span>
      <div className="route-btn-grid">
        {ROUTE_CHOICES.map(r => <button key={r} type="button" disabled={busy} data-testid={`prod-set-route-${product.id}-${r.replaceAll(" ","-").replaceAll("/","-")}`} className="soft-btn small-btn" onClick={()=>{ for (const pc of productCards) onSetRoute(pc, r); }}>{r}</button>)}
      </div>
    </div>}
    {!isPerluDiperiksa && (
      /* Iter 334 — Ringkasan multi-route di atas aksi: "3 produk → Bordir · 2 produk → Sablon/DTF".
         Hanya saat subGroups.length > 1 (artinya nota punya >1 cabang next-stage).
         Tujuan: operator tahu ini SATU nota dengan beberapa cabang, bukan beberapa nota. */
      subGroupSummary.length > 1 && (
        <div className="queue-multi-route-summary" data-testid={`prod-multi-route-${product.id}`}>
          <span className="queue-multi-route-badge">1 nota · {subGroupSummary.reduce((s,x)=>s+x.prodCount,0)} produk</span>
          <ul className="queue-multi-route-list">
            {subGroupSummary.map((sg,i)=>(
              <li key={`${sg.key}-${i}`} className="queue-multi-route-item" data-testid={`prod-multi-route-item-${product.id}-${i}`}>
                <strong>{sg.prodCount}</strong> produk <span className="muted">→</span> <strong>{sg.next}</strong>
                {sg.route ? <span className="muted small"> ({sg.route})</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )
    )}
    {!isPerluDiperiksa && (
      /* Iter 287at — Simplified mode: wrap primary (SubGroupActions) + Detail dalam satu
         baris via display:contents trick. Non-simplified tetap urutan asli.
         Iter 287av — Preview Mockup button DI ATAS baris aksi (sebelum combo). */
      simplified ? (
        <>
          {operator && onOpenMockup && (MOCKUP_PREVIEW_TEAMS.has(operator.team) || MOCKUP_THUMB_TEAMS.has(operator.team)) && (
            MOCKUP_THUMB_TEAMS.has(operator.team) ? (
              /* Iter 332.7/332.8 — Pola compact: thumbnail 80px + Preview Mockup kecil.
                 Berlaku untuk Bordir, Press, Sublim, Selesai (via MOCKUP_THUMB_TEAMS).
                 Thumbnail clickable → onOpenMockup (modal besar). */
              <div className="queue-actions-preview queue-actions-preview-bordir" onClick={e=>e.stopPropagation()}>
                <QueueMockupThumb orderNumber={order.order_number} onOpenMockup={onOpenMockup}
                  testId={`prod-mockup-thumb-${product.id}`}/>
                <button type="button"
                  className="btn-preview-mockup btn-preview-mockup-compact"
                  data-testid={`prod-preview-mockup-${product.id}`}
                  onClick={(e)=>{ e.stopPropagation(); onOpenMockup(); }}
                  aria-label={`Pratinjau mockup pesanan ${order.order_number}`}>
                  <ImageIcon size={14} aria-hidden="true"/> <span>Pratinjau Mockup</span>
                </button>
              </div>
            ) : (
              <div className="queue-actions-preview" onClick={e=>e.stopPropagation()}>
                <button type="button"
                  className="btn-preview-mockup"
                  data-testid={`prod-preview-mockup-${product.id}`}
                  onClick={(e)=>{ e.stopPropagation(); onOpenMockup(); }}
                  aria-label={`Pratinjau mockup pesanan ${order.order_number}`}>
                  <ImageIcon size={15} aria-hidden="true"/> <span>Pratinjau Mockup</span>
                </button>
              </div>
            )
          )}
          {showMockupInline && mockupTargets.length > 0 && (
            <>
              {/* Iter 494 — 1 NOTA = 1 MOCKUP GABUNGAN.
                  Mengganti N panel upload per produk dengan SATU panel level nota.
                  Daftar produk ditampilkan compact; satu area upload+preview+ganti.
                  Opsi "Unggah terpisah per produk" tersedia sebagai fold sekunder. */}
              <NoteMockupPanel
                orderNumber={order.order_number}
                mockupTargets={mockupTargets}
                onChanged={() => { if (onChanged) onChanged(); }}
              />
            </>
          )}
          {/* Iter 316 — Caption jumlah produk di LUAR tombol, tepat di atas combo, bila kartu
              gabungan. Hindari teks menumpuk "Selesaikan Mockup (N produk)". */}
          {showMockupInline && mockupTargets.length > 1 && mockupAllReady && (
            <div className="queue-mockup-count-caption" data-testid={`prod-mockup-count-${product.id}`}>
              {mockupTargets.length} produk siap diselesaikan
            </div>
          )}
          {moUi ? <>
            <div className="mo-primary" onClick={e=>e.stopPropagation()} data-testid={`prod-actions-combo-${product.id}`}>
              {subGroups.map(sg => (
                <SubGroupActions key={sg.key} subGroup={sg} tab={tab} isAdmin={isAdmin} operator={operator} busy={busy}
                  showLabelPrefix={subGroups.length > 1} sectionMode={sectionMode}
                  onAdvance={(pc)=>onAdvance(pc)} onClaim={(pc)=>onClaim(pc)}
                  onParallelClaim={(pc, track)=>onParallelClaim(pc, track)} onParallelDone={(pc, track)=>onParallelDone(pc, track)}
                  onRelease={onRelease ? ((pc)=>onRelease(pc)) : undefined}
                  onParallelRelease={onParallelRelease ? ((pc, track)=>onParallelRelease(pc, track)) : undefined}
                  onChanged={onChanged} releaseInMenu releaseSignal={relSig}/>
              ))}
            </div>
            <div className="mo-actions-row" onClick={e=>e.stopPropagation()}>
              <button type="button" className="soft-btn small-btn op-btn-detail queue-action-detail"
                data-testid={`prod-detail-${product.id}`} onClick={onOpen}>Detail</button>
              <MockupMoreMenu canRelease={moCanRelease} onRelease={() => setRelSig(n => n + 1)}
                onReport={() => setReportOpen(true)} testid={`prod-card-${product.id}`}/>
            </div>
          </> : <div className="queue-actions-combo" onClick={e=>e.stopPropagation()} data-testid={`prod-actions-combo-${product.id}`}>
            {subGroups.map(sg => (
              <SubGroupActions
                key={sg.key}
                subGroup={sg}
                tab={tab}
                isAdmin={isAdmin}
                operator={operator}
                busy={busy}
                showLabelPrefix={subGroups.length > 1}
                sectionMode={sectionMode}
                onAdvance={(pc)=>onAdvance(pc)}
                onClaim={(pc)=>onClaim(pc)}
                onParallelClaim={(pc, track)=>onParallelClaim(pc, track)}
                onParallelDone={(pc, track)=>onParallelDone(pc, track)}
                onRelease={onRelease ? ((pc)=>onRelease(pc)) : undefined}
                onParallelRelease={onParallelRelease ? ((pc, track)=>onParallelRelease(pc, track)) : undefined}
                onChanged={onChanged}
              />
            ))}
            <button type="button" className="soft-btn small-btn op-btn-detail queue-action-detail"
              data-testid={`prod-detail-${product.id}`} onClick={onOpen}>
              Detail
            </button>
          </div>}
        </>
      ) : (
        <>
          {subGroups.map(sg => (
            <SubGroupActions
              key={sg.key}
              subGroup={sg}
              tab={tab}
              isAdmin={isAdmin}
              operator={operator}
              busy={busy}
              showLabelPrefix={subGroups.length > 1}
              sectionMode={sectionMode}
              onAdvance={(pc)=>onAdvance(pc)}
              onClaim={(pc)=>onClaim(pc)}
              onParallelClaim={(pc, track)=>onParallelClaim(pc, track)}
              onParallelDone={(pc, track)=>onParallelDone(pc, track)}
              onRelease={onRelease ? ((pc)=>onRelease(pc)) : undefined}
              onParallelRelease={onParallelRelease ? ((pc, track)=>onParallelRelease(pc, track)) : undefined}
                onChanged={onChanged}
            />
          ))}
        </>
      )
    )}

    {/* Iter 83 — Common actions: Laporkan Kendala. Preview Mockup sudah dipindah di atas combo.
        Iter 287av — Secondary row operator simplified: hanya Laporkan Kendala
        (Lihat Mockup tidak digandakan). Non-simplified tetap 3-tombol legacy. */}
    {!isPerluDiperiksa && !moUi && (
      simplified ? (
        <div className="queue-actions-secondary" data-testid={`prod-common-actions-${product.id}`}>
          <button type="button" className="btn-report-link"
            data-testid={`prod-report-issue-${product.id}`} onClick={()=>setReportOpen(true)}>
            <AlertTriangle size={14}/> Laporkan Kendala
          </button>
        </div>
      ) : (
        <div className="queue-card-foot op-common-actions" data-testid={`prod-common-actions-${product.id}`}>
          <button type="button" className="soft-btn small-btn op-btn-detail" data-testid={`prod-detail-${product.id}`} onClick={onOpen}>
            {operator && !operator?.is_admin_toko ? "Detail" : "Lihat Detail"}
          </button>
          {operator && MOCKUP_PREVIEW_TEAMS.has(operator.team) && onOpenMockup && (
            <button type="button" className="soft-btn small-btn op-btn-mockup" data-testid={`prod-view-mockup-${product.id}`} onClick={onOpenMockup}>
              <ImageIcon size={14}/> Lihat Mockup
            </button>
          )}
          <button type="button" className="soft-btn small-btn op-btn-report" data-testid={`prod-report-issue-${product.id}`} onClick={()=>setReportOpen(true)}>
            <AlertTriangle size={14}/> Laporkan Kendala
          </button>
        </div>
      )
    )}

    {isAdmin && !isPerluDiperiksa && uniformStage && !PARALLEL_STAGE_TRACKS[stage] && <div className="queue-card-foot">
      <button type="button" data-testid={`prod-admin-correct-${product.id}`} className="text-btn small-btn" disabled={busy} onClick={()=>onAdminCorrect(productCards[0])}>Koreksi tahap (Owner/Admin)</button>
    </div>}

    {reportOpen && (
      <ReportKendalaModal
        card={card}
        actorName={operator?.name || user?.name || "—"}
        onClose={()=>setReportOpen(false)}
      />
    )}
  </article>;
}


// ============================================================================
// Iter 83 — Report Kendala Modal (UI-only, no backend endpoint yet)
// Operator dapat mencatat kendala per produk. Data hanya ditampilkan sebagai
// konfirmasi lokal (notify.success). Nanti dapat dihubungkan ke endpoint
// backend jika diperlukan (mis. POST /api/orders/{no}/products/{pid}/issues).
// ============================================================================
function ReportKendalaModal({ card, actorName, onClose }) {
  const { order, product, stage } = card;
  const [category, setCategory] = useState("Bahan");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!note.trim()) {
      notify.warning({ title: "Catatan wajib diisi", message: "Mohon jelaskan detail kendala yang ditemukan." });
      return;
    }
    setBusy(true);
    // Iter 84 — POST ke backend + fallback localStorage jika gagal
    let backendOk = false;
    try {
      await axios.post(
        `${API}/orders/${order.order_number}/products/${product.id}/issues`,
        { category, note: note.trim(), stage },
        { withCredentials: true }
      );
      backendOk = true;
    } catch (e) {
      console.warn("Report kendala backend gagal, fallback ke localStorage:", e?.message);
    }
    // Selalu backup di localStorage supaya tidak hilang bila offline
    try {
      const key = "tam_issue_reports_v1";
      const now = new Date().toISOString();
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      list.push({
        at: now,
        order_number: order.order_number,
        product_id: product.id,
        product_name: product.custom_name || product.name,
        stage, category, note: note.trim(),
        reported_by: actorName,
        synced: backendOk,
      });
      localStorage.setItem(key, JSON.stringify(list.slice(-200)));
    } catch { /* ignore storage errors */ }
    setBusy(false);
    onClose();
    if (backendOk) {
      notify.success({
        title: "Kendala tercatat",
        message: `Laporan "${category}" untuk ${product.custom_name || product.name} pada tahap ${stage} sudah dicatat ke server dan akan diteruskan ke admin.`,
      });
    } else {
      notify.warning({
        title: "Tersimpan offline",
        message: `Server tidak dapat dijangkau. Laporan disimpan lokal dan akan disinkron nanti.`,
      });
    }
  };
  return (
    <div className="modal-overlay" data-testid="report-kendala-overlay" onClick={onClose}>
      <div className="modal op-report-modal" onClick={e=>e.stopPropagation()} data-testid="report-kendala-modal">
        <div className="modal-head">
          <h3><AlertTriangle size={18} style={{verticalAlign:"-3px",marginRight:6,color:"#b91c1c"}}/>Laporkan Kendala</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Tutup"><X size={18}/></button>
        </div>
        <p className="muted small" style={{marginTop:4}}>
          Nota: <strong>{order.order_number}</strong> · Produk: <strong>{product.custom_name || product.name}</strong> · Tahap: <strong>{stage}</strong>
        </p>
        <label className="field-label" style={{marginTop:14,display:"block"}}>Kategori kendala</label>
        <select data-testid="report-kendala-category" value={category} onChange={e=>setCategory(e.target.value)} style={{width:"100%",marginTop:6}} disabled={busy}>
          <option>Bahan</option>
          <option>Warna</option>
          <option>Ukuran</option>
          <option>Desain / Mockup</option>
          <option>Mesin / Alat</option>
          <option>Kekurangan Barang</option>
          <option>Lainnya</option>
        </select>
        <label className="field-label" style={{marginTop:14,display:"block"}}>Detail kendala <span className="required">*</span></label>
        <textarea
          data-testid="report-kendala-note"
          value={note}
          onChange={e=>setNote(e.target.value)}
          style={{width:"100%",marginTop:6,minHeight:88}}
          rows={4}
          placeholder="Jelaskan kendala secara singkat, mis. warna tidak sesuai, mesin macet, dst."
          disabled={busy}
        />
        <div className="modal-foot" style={{marginTop:16}}>
          <button type="button" className="soft-btn" data-testid="report-kendala-cancel" onClick={onClose} disabled={busy}>Batal</button>
          <button type="button" className="primary" data-testid="report-kendala-submit" onClick={submit} disabled={busy}>
            {busy ? "Menyimpan…" : "Kirim Laporan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Iter 91 — Kebutuhan Barang (READ-ONLY visibility untuk Rido/Gudang)
// Tidak mengubah production_stage / workflow produksi. Hanya UI + status internal.
// ============================================================================
function KebutuhanBarang({ operator }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, belum: 0, sedang: 0, sudah: 0 });
  const [grouped, setGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | Belum Disiapkan | Sedang Disiapkan | Sudah Siap
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: f });
      const r = await axios.get(`${API}/warehouse/prep?${params.toString()}`, { withCredentials: true });
      setRows(r.data?.items || []);
      // Summary + grouped selalu ambil "all" untuk KPI atas + ringkasan agregat
      if (f !== "all") {
        const r2 = await axios.get(`${API}/warehouse/prep?status=all`, { withCredentials: true });
        setSummary(r2.data?.summary || { total: 0, belum: 0, sedang: 0, sudah: 0 });
        setGrouped(r2.data?.grouped || []);
      } else {
        setSummary(r.data?.summary || { total: 0, belum: 0, sedang: 0, sudah: 0 });
        setGrouped(r.data?.grouped || []);
      }
    } catch (e) {
      notify.error({ title: "Gagal memuat kebutuhan barang", message: e?.response?.data?.detail || e.message });
    }
    setLoading(false);
  };
  React.useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (item, newStatus) => {
    setBusyId(item.id);
    try {
      await axios.patch(`${API}/warehouse/prep/${encodeURIComponent(item.id)}`, { status: newStatus }, { withCredentials: true });
      notify.success({ title: "Status persiapan tersimpan", message: `${item.product_name}${item.color?` (${item.color})`:""} → ${newStatus}. Ini TIDAK mengubah tahap produksi.` });
      await load(filter);
    } catch (e) {
      notify.error({ title: "Gagal update", message: e?.response?.data?.detail || e.message });
    }
    setBusyId("");
  };

  const filtered = rows.filter(r => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (r.order_number || "").toLowerCase().includes(q) ||
      (r.customer_name || "").toLowerCase().includes(q) ||
      (r.product_name || "").toLowerCase().includes(q) ||
      (r.color || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="prep-section" data-testid="kebutuhan-barang">
      <div className="prep-note-top" data-testid="prep-note-top">
        <span>📦</span>
        <span>Tab ini hanya untuk <strong>visibility & persiapan</strong>. Perubahan status di sini <strong>TIDAK mengubah tahap produksi utama</strong>.</span>
      </div>

      <div className="prep-summary-grid" data-testid="prep-summary">
        <div className="prep-summary-tile"><span className="prep-summary-label">Total</span><span className="prep-summary-value">{summary.total}</span></div>
        <div className="prep-summary-tile prep-tone-belum"><span className="prep-summary-label">Belum Disiapkan</span><span className="prep-summary-value">{summary.belum}</span></div>
        <div className="prep-summary-tile prep-tone-sedang"><span className="prep-summary-label">Sedang Disiapkan</span><span className="prep-summary-value">{summary.sedang}</span></div>
        <div className="prep-summary-tile prep-tone-sudah"><span className="prep-summary-label">Sudah Siap</span><span className="prep-summary-value">{summary.sudah}</span></div>
      </div>

      {grouped.length > 0 && (
        <section className="prep-aggregate" data-testid="prep-aggregate">
          <header className="prep-aggregate-head">
            <div>
              <p className="eyebrow">Ringkasan Kebutuhan</p>
              <h3 style={{margin:"2px 0 4px",fontSize:16}}>Barang yang perlu disiapkan (agregat)</h3>
              <p className="muted small">Ringkasan per produk + bahan + warna dari SEMUA pesanan yang belum siap. Rido fokus ke kebutuhan barang tanpa perlu buka nomor pesanan.</p>
            </div>
          </header>
          <ul className="prep-aggregate-list">
            {grouped.map((g, i) => (
              <li key={`${g.product_name}|${g.material}|${g.color}`} className="prep-aggregate-item" data-testid={`prep-aggregate-item-${i}`}>
                <div className="prep-aggregate-title">
                  <strong data-testid={`prep-aggregate-name-${i}`}>{g.product_name}</strong>
                  {g.sleeve && g.sleeve !== "Tidak Berlaku" && <span className="muted"> · lengan <strong data-testid={`prep-aggregate-sleeve-${i}`}>{g.sleeve}</strong></span>}
                  {g.material && <span className="muted"> · bahan <strong>{g.material}</strong></span>}
                  {g.color && <span className="muted"> · warna <strong>{g.color}</strong></span>}
                </div>
                <div className="prep-aggregate-body">
                  <div className="prep-aggregate-sizes" data-testid={`prep-aggregate-sizes-${i}`}>
                    {g.sizes.map((s, sidx) => (
                      <span key={sidx} className="prep-size-chip">
                        <span className="prep-size-chip-label">{s.size}</span>
                        <strong>{s.quantity}</strong>
                      </span>
                    ))}
                  </div>
                  <div className="prep-aggregate-total">
                    <span className="muted small">Total</span>
                    <strong data-testid={`prep-aggregate-total-${i}`}>{g.total_qty} pcs</strong>
                    <span className="muted small">dari {g.orders_count} pesanan</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="prep-filter-bar">
        <div className="prep-filter-btns">
          {[
            {k:"all", l:"Semua"},
            {k:"Belum Disiapkan", l:"Belum Disiapkan"},
            {k:"Sedang Disiapkan", l:"Sedang Disiapkan"},
            {k:"Sudah Siap", l:"Sudah Siap"},
          ].map(f => (
            <button key={f.k} type="button" className={`prep-filter-btn ${filter===f.k?"active":""}`} data-testid={`prep-filter-${f.k.replaceAll(" ","-")}`} onClick={()=>setFilter(f.k)}>{f.l}</button>
          ))}
        </div>
        <input type="text" className="prep-search" data-testid="prep-search" placeholder="Cari nota / pelanggan / produk / warna…" value={query} onChange={e=>setQuery(e.target.value)}/>
      </div>

      {loading ? (
        <p className="muted small">Memuat kebutuhan barang…</p>
      ) : filtered.length === 0 ? (
        <div className="op-empty" data-testid="prep-empty">
          <div className="op-empty-icon"><CheckCircle2 size={32}/></div>
          <h4 className="op-empty-title">Tidak ada kebutuhan barang</h4>
          <p className="op-empty-msg">Untuk filter <strong>{filter === "all" ? "Semua" : filter}</strong> — belum ada item yang cocok. Kebutuhan baru akan otomatis muncul saat Nota dibuat.</p>
        </div>
      ) : (
        <ul className="prep-list" data-testid="prep-list">
          {filtered.map((it, i) => (
            <PrepItem key={it.id} item={it} idx={i} busy={busyId === it.id} onSet={setStatus} canReread={!operator}/>
          ))}
        </ul>
      )}
    </div>
  );
}

function PrepItem({ item, idx, busy, onSet, canReread }) {
  const statusToneClass = item.prep_status === "Sudah Siap" ? "prep-tone-sudah"
    : item.prep_status === "Sedang Disiapkan" ? "prep-tone-sedang" : "prep-tone-belum";
  return (
    <li className={`prep-item ${statusToneClass}`} data-testid={`prep-item-${idx}`}>
      <div className="prep-item-head">
        <div className="prep-item-head-left">
          <span className="prep-nota">{item.order_number}</span>
          <strong className="prep-customer">{item.customer_name || "—"}</strong>
        </div>
        <span className={`prep-status-chip prep-chip-${item.prep_status.replaceAll(" ","-")}`} data-testid={`prep-item-${idx}-status`}>{item.prep_status}</span>
      </div>
      <div className="prep-item-body">
        <p className="prep-product">
          <strong>{item.product_name}</strong> · {item.quantity} pcs
        </p>
        {item.manual_check && <p className="prep-manual-check" data-testid={`prep-item-${idx}-manual-check`}><strong>{item.manual_check}</strong></p>}
        {item.category_check && <p className="prep-manual-check" data-testid={`prep-item-${idx}-category-check`}>{item.category_check}</p>}
        {item.ai_read && <AiMockupSummary read={item.ai_read} tid={`prep-${idx}`} canReread={canReread} />}
        <div className="prep-chips">
          {item.material && <span className="queue-chip queue-chip-material">Bahan: <strong>{item.material}</strong></span>}
          {item.color && <span className="queue-chip queue-chip-color">Warna: <strong>{item.color}</strong></span>}
          {item.sleeve && item.sleeve !== "Tidak Berlaku" && <span className="queue-chip queue-chip-sleeve">Lengan: <strong>{item.sleeve}</strong></span>}
          {item.sizes && item.sizes.length > 0
            ? item.sizes.map((s, idx) => {
                // Iter 147 — Chip variasi kini sertakan warna & lengan bila ada (fallback ke
                // product-level bila per-baris kosong). Key pakai index karena size bisa berulang.
                const c = (s.color || item.color || "").trim();
                const v = (s.variant || (item.sleeve && item.sleeve !== "Tidak Berlaku" ? item.sleeve : "") || "").trim();
                const label = `${c ? c + " · " : ""}${s.size}${v ? ` (${v})` : ""}`;
                return (
                  <span key={idx} className="queue-chip queue-chip-size">
                    <strong>{label}</strong>&nbsp;{s.quantity}
                  </span>
                );
              })
            : (item.size && <span className="queue-chip">Size: <strong>{item.size}</strong></span>)
          }
        </div>
        <div className="prep-meta muted small">
          <span>Deadline: <strong>{item.deadline || "—"}</strong></span>
          <span> · Tahap saat ini: <strong>{item.production_stage || "—"}</strong></span>
          <span> · Mockup: <strong>{item.mockup_status || "—"}</strong></span>
        </div>
      </div>
      <div className="prep-item-actions">
        <button type="button" className={`prep-action-btn ${item.prep_status==="Belum Disiapkan"?"active":""}`} data-testid={`prep-item-${idx}-set-belum`} onClick={()=>onSet(item, "Belum Disiapkan")} disabled={busy || item.prep_status==="Belum Disiapkan"}>Belum</button>
        <button type="button" className={`prep-action-btn prep-action-sedang ${item.prep_status==="Sedang Disiapkan"?"active":""}`} data-testid={`prep-item-${idx}-set-sedang`} onClick={()=>onSet(item, "Sedang Disiapkan")} disabled={busy || item.prep_status==="Sedang Disiapkan"}>Sedang</button>
        <button type="button" className={`prep-action-btn prep-action-sudah ${item.prep_status==="Sudah Siap"?"active":""}`} data-testid={`prep-item-${idx}-set-sudah`} onClick={()=>onSet(item, "Sudah Siap")} disabled={busy || item.prep_status==="Sudah Siap"}>Sudah Siap</button>
      </div>
    </li>
  );
}

// ============================================================================
// Iter 142 — Penyiapan Barang (tab utama untuk operator Gudang / Rido)
//
// Sumber data: `orders` prop (state global App.js), sama dengan Antrean/Sedang
// sehingga ringkasan angka dan label deadline pada kartu SELALU konsisten.
//
// Cakupan: produk yang stage == "Gudang" (single-track) ATAU stage paralel
// (Sablon Paralel / Persiapan Paralel) dengan track "gudang" belum selesai.
//
// Aksi Rido:
//   - "Mulai Siapkan"  → single: claim(card); paralel: parallelClaim(card, "gudang")
//   - "Selesai Siapkan"→ single: advance(card); paralel: parallelDone(card, "gudang")
// Prasyarat/error dari backend (mis. Sablon Paralel butuh Press setelah kedua
// track selesai) tetap dikelola oleh handler yang sama (notify.error).
// ============================================================================
export function PenyiapanBarang({ operator, user, isAdmin, orders, busyKey, todayISO, onOpen, onOpenMockup, onClaim, onAdvance, onParallelClaim, onParallelDone, onMarkMockupPrint, onReload, printFilter, gudangAccCount, onJumpAcc }) {
  const opName = operator?.name || "";
  const [statusFilter, setStatusFilter] = useState("all"); // all | belum | sedang | overdue | near | selesai_today
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  // Iter 394 — Bulk Print mode (Rido): pilih beberapa nota lalu Cetak Terpilih
  //   satu print job. Grouping key = order_number (stabil, bukan product/variant/size).
  const selectMode = false; // Gudang memakai kartu compact dengan checkbox permanen
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { printed: groupCards[], missing: [] }
  const BULK_MAX = 25; // Batas wajar supaya browser tidak freeze.
  const lastPick = useRef(null);
  const toggleOrderSelected = (orderNumber, e) => setSelectedOrders(prev => {
    const n = new Set(prev), on = !n.has(orderNumber);
    const ids = groupedCardsRef.current.map(c => c.order.order_number);
    const a = ids.indexOf(lastPick.current), b = ids.indexOf(orderNumber);
    const range = e?.shiftKey && a >= 0 && b >= 0 ? ids.slice(Math.min(a, b), Math.max(a, b) + 1) : [orderNumber];
    range.forEach(x => (on ? n.add(x) : n.delete(x)));
    lastPick.current = orderNumber;
    return n;
  });
  const groupedCardsRef = useRef([]);

  // Kumpulkan kartu Gudang: single stage=Gudang ATAU paralel dg track "gudang" belum done.
  // Iter 329 — Eksklusi kartu yg mockup_print_at sudah ada (sudah ditangani Rido → ke tab Menunggu ACC).
  const gudangCards = useMemo(() => {
    const list = [];
    for (const o of (orders || [])) {
      if (!o || o.order_status === "Draft" || o.order_status === "Dibatalkan") continue;
      for (const p of (o.products || [])) {
        // Iter 329: skip produk yang sudah mockup_print_at (muncul di tab Menunggu ACC saja).
        if (p.mockup_print_at) continue;
        const stage = p.production_stage || "";
        if (stage === "Gudang") {
          list.push({ order: o, product: p, key: `${o.order_number}::${p.id || p.name}`, mode: "single", stage });
          continue;
        }
        const tracks = PARALLEL_STAGE_TRACKS[stage];
        if (tracks) {
          const gTrack = tracks.find(tr => tr.team === "Gudang");
          if (gTrack) {
            const ps = p.parallel_status || {};
            if (!ps[`${gTrack.track}_done`]) {
              list.push({ order: o, product: p, key: `${o.order_number}::${p.id || p.name}::${gTrack.track}`, mode: "parallel", stage, track: gTrack });
            }
          }
        }
      }
    }
    return sortByDeadlinePriority(list);
  }, [orders]);

  // Status helpers per kartu
  const isMineSedang = (c) => c.mode === "parallel"
    ? c.product.parallel_status?.[`${c.track.track}_claimed_by`] === opName
    : c.product.production_pic === opName;
  const isClaimed = (c) => c.mode === "parallel"
    ? !!c.product.parallel_status?.[`${c.track.track}_claimed_by`]
    : !!c.product.production_pic;
  const isMineDone = (c) => c.mode === "parallel"
    ? !!c.product.parallel_status?.[`${c.track.track}_done`]
    : false; // single: kartu keluar dari list saat stage berubah

  // Hitung ringkasan DARI kartu yang sama supaya konsisten dengan daftar.
  const belumCards = gudangCards.filter(c => !isClaimed(c));
  const sedangCards = gudangCards.filter(c => isMineSedang(c));
  let terlambatCount = 0, hampirCount = 0;
  for (const c of gudangCards) {
    const ds = c.product.deadline_status;
    if (ds === "Terlambat") terlambatCount++;
    else if (ds === "Hampir Deadline") hampirCount++;
  }
  // Selesai Hari Ini: entri history hari ini dengan handed_over_by=operator dan
  // event mewakili keluar dari Gudang (single) atau track_done gudang (paralel).
  let selesaiTodayCount = 0;
  for (const o of (orders || [])) {
    for (const p of (o.products || [])) {
      for (const h of (p.production_pic_history || [])) {
        if (!h?.at || !h.at.startsWith(todayISO || "")) continue;
        if (h.handed_over_by !== opName) continue;
        if (h.stage_from === "Gudang" || h.track === "gudang") selesaiTodayCount++;
      }
    }
  }

  // Iter 252 — Synthetic card list untuk filter "Selesai Hari Ini". Sumber = history entries
  //   (produk sudah pindah stage sehingga tidak ada di gudangCards). Dedupe per order+product+mode
  //   supaya multi-hand-over sehari tidak dobel. Flag `selesaiTodayView` dipakai PenyiapanCard
  //   untuk sembunyikan tombol aksi & tampilkan chip "Sudah selesai hari ini".
  const selesaiTodayCards = useMemo(() => {
    const list = [];
    const seen = new Set();
    for (const o of (orders || [])) {
      for (const p of (o.products || [])) {
        for (const h of (p.production_pic_history || [])) {
          if (!h?.at || !h.at.startsWith(todayISO || "")) continue;
          if (h.handed_over_by !== opName) continue;
          const single = h.stage_from === "Gudang";
          const paralel = h.track === "gudang";
          if (!single && !paralel) continue;
          const mode = paralel ? "parallel" : "single";
          const dedupeKey = `${o.order_number}::${p.id || p.name}::${mode}`;
          if (seen.has(dedupeKey)) continue;
          seen.add(dedupeKey);
          const track = paralel ? { track: "gudang", team: "Gudang", label: "Gudang" } : null;
          list.push({
            order: o, product: p, mode, stage: single ? "Gudang" : (p.production_stage || ""),
            track, key: `${dedupeKey}::selesai-today`, selesaiTodayView: true, doneAt: h.at,
          });
        }
      }
    }
    return sortByDeadlinePriority(list);
  }, [orders, opName, todayISO]);

  const sourceCards = statusFilter === "selesai_today" ? selesaiTodayCards : gudangCards;

  const filtered = sourceCards.filter(c => {
    if (statusFilter === "belum" && isClaimed(c)) return false;
    if (statusFilter === "sedang" && !isMineSedang(c)) return false;
    if (statusFilter === "overdue" && c.product.deadline_status !== "Terlambat") return false;
    if (statusFilter === "near" && c.product.deadline_status !== "Hampir Deadline") return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (c.order.order_number || "").toLowerCase().includes(q) ||
      (c.order.customer_name || "").toLowerCase().includes(q) ||
      (c.product.name || "").toLowerCase().includes(q) ||
      (c.product.color || "").toLowerCase().includes(q)
    );
  });

  // Iter 246 — Group card per nota per track/divisi. 1 nota multi-produk yg berada
  //   di tab yg sama = 1 kartu (bukan pecah per produk). Kartu berbeda tetap
  //   dipertahankan bila mode/track berbeda (mis. nota paralel aktif di Gudang +
  //   Sablon → tetap 2 kartu, satu per divisi).
  const groupedCards = useMemo(() => {
    // Iter 260 — Group HANYA per `order_number`. Semua produk 1 nota di tab Gudang
    //   tampil di 1 kartu. Mode/track/claim per-produk boleh berbeda → sub-action
    //   di dalam kartu ditangani PenyiapanCard supaya tiap produk tetap route-aware.
    const groups = new Map();
    for (const c of filtered) {
      const gk = `${c.order.order_number}`;
      if (!groups.has(gk)) {
        groups.set(gk, { ...c, groupKey: gk, productCards: [c] });
      } else {
        groups.get(gk).productCards.push(c);
      }
    }
    // Precompute subGroups per group (mode + track + claim signature) untuk render aksi.
    const subKey = (c) => {
      const trackKey = c.mode === "parallel" ? c.track.track : "single";
      let claimKey = "unclaimed";
      if (c.mode === "parallel") {
        const cb = c.product.parallel_status?.[`${c.track.track}_claimed_by`];
        const done = !!c.product.parallel_status?.[`${c.track.track}_done`];
        if (done) claimKey = cb === opName ? "mine_done" : "other_done";
        else if (cb) claimKey = cb === opName ? "mine" : `other:${cb}`;
      } else {
        const cb = c.product.production_pic;
        if (cb) claimKey = cb === opName ? "mine" : `other:${cb}`;
      }
      return `${c.mode}::${trackKey}::${claimKey}`;
    };
    for (const g of groups.values()) {
      const subMap = new Map();
      for (const pc of g.productCards) {
        const sk = subKey(pc);
        if (!subMap.has(sk)) subMap.set(sk, { key: sk, cards: [pc], primary: pc });
        else subMap.get(sk).cards.push(pc);
      }
      g.subGroups = Array.from(subMap.values());
    }
    return Array.from(groups.values());
  }, [filtered, opName]);
  groupedCardsRef.current = groupedCards;
  const compact = operator?.team === "Gudang";
  const allPicked = groupedCards.length > 0 && groupedCards.every(c => selectedOrders.has(c.order.order_number));

  const toggleExpand = (key) => setExpanded(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  return (
    <div className="wh-prep-tab" data-testid="wh-prep-tab">
      {/* Iter 371 — Chip filter "Belum Dicetak / Sudah Dicetak" sebagai shortcut
          navigasi: Belum = tab Siapkan Barang (ini), Sudah = tab Menunggu ACC.
          Sumber data = catatan cetak sebenarnya (`mockup_print_at`); chip hanya
          memanggil setOpSection existing — tidak menduplikasi daftar. */}
      {operator?.team === "Gudang" && (printFilter === "belum" || printFilter === "sudah") && (
        <div className="rido-print-filter" data-testid="rido-print-filter" role="tablist" aria-label="Filter status cetak mockup">
          <button
            type="button"
            role="tab"
            aria-selected={printFilter === "belum"}
            className={`rido-print-chip ${printFilter === "belum" ? "active" : ""}`}
            onClick={()=>{ /* Sudah di tab prep — no-op bila klik "Belum" */ }}
            data-testid="rido-print-chip-belum"
          >
            Belum Dicetak <span className="rido-print-chip-count">{gudangCards.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={printFilter === "sudah"}
            className={`rido-print-chip ${printFilter === "sudah" ? "active" : ""}`}
            onClick={()=>{ if (typeof onJumpAcc === "function") onJumpAcc(); }}
            data-testid="rido-print-chip-sudah"
          >
            Sudah Dicetak <span className="rido-print-chip-count">{gudangAccCount || 0}</span>
          </button>
        </div>
      )}
      {/* Iter 353 — Hilangkan judul berulang (eyebrow+h2+paragraf). Langsung ke
          pencarian + daftar kartu agar pekerjaan tampak lebih cepat. */}
      {/* Iter 394 — Toolbar Bulk Print (hanya tim Gudang/Rido). */}
      <div className="wh-prep-filter-bar">
        <input type="search" className="wh-prep-search" data-testid="wh-prep-search" placeholder="Cari nota / pelanggan / produk / warna…" value={query}
          onChange={e => setQuery(e.target.value)} onKeyDown={e => { if (e.key === "Escape") setQuery(""); if (e.key === "Enter") e.preventDefault(); }}/>
      </div>
      {compact && (
        <div className="shopee-batch-bar rido-offline-batch" data-testid="rido-bulk-toolbar">
          <label className="shopee-batch-all">
            <input type="checkbox" checked={allPicked} disabled={bulkBusy || groupedCards.length === 0}
              ref={el => { if (el) el.indeterminate = selectedOrders.size > 0 && !allPicked; }}
              onChange={() => setSelectedOrders(allPicked ? new Set() : new Set(groupedCards.map(c => c.order.order_number)))}
              data-testid="rido-bulk-select-all"/>
            {allPicked ? "Batal Pilih Semua" : "Pilih Semua"}
          </label>
          <span className="shopee-batch-sep" aria-hidden="true">|</span>
          <span className="shopee-batch-count" data-testid="rido-bulk-count"><b>{selectedOrders.size}/{groupedCards.length}</b> dipilih
            {selectedOrders.size > BULK_MAX && <span style={{color:"#b91c1c", marginLeft:4}}>· max {BULK_MAX}</span>}</span>
          <span className="shopee-batch-sep" aria-hidden="true">|</span>
          <button type="button" className="primary small-btn"
            disabled={bulkBusy || selectedOrders.size === 0 || selectedOrders.size > BULK_MAX}
            onClick={async ()=>{
              if (selectedOrders.size === 0) return;
              setBulkBusy(true);
              try {
                // Urutan sesuai daftar yang tampil (groupedCards).
                const chosenCards = groupedCards.filter(c => selectedOrders.has(c.order.order_number));
                const items = chosenCards
                  .map(c => ({ orderNumber: c.order.order_number, label: `${c.order.customer_name || "—"} · ${c.order.order_number}` }));
                const res = await cetakPdfA5P(items);
                const miss = new Set((res?.missing || []).map(m => m.orderNumber));
                setBulkConfirm({ printed: chosenCards.filter(c => !miss.has(c.order.order_number)), missing: res?.missing || [] });
                if (res?.missing?.length > 0) {
                  toast.warning(`Dilewati ${res.missing.length} nota tanpa mockup: ${res.missing.slice(0,3).map(m=>m.orderNumber).join(", ")}${res.missing.length>3?"…":""}`, { duration: 7000 });
                }
                toast.success(`PDF A5 Potret dibuka: ${res.printed} mockup, 1 mockup per halaman.`);
              } catch (e) {
                toast.error(e?.message || "Gagal membuka cetak batch.");
              } finally {
                setBulkBusy(false);
              }
            }}
            data-testid="rido-bulk-print" aria-label={`Cetak Terpilih (${selectedOrders.size})`}>
            <Printer size={14} aria-hidden="true"/> {bulkBusy ? "Memuat…" : `Cetak (${selectedOrders.size})`}
          </button>
        </div>
      )}
      {bulkConfirm && <OfflineBulkConfirm data={bulkConfirm} onClose={() => setBulkConfirm(null)}
        onDone={async (failed) => { setBulkConfirm(null); setSelectedOrders(new Set(failed)); if (onReload) await onReload(); }}/>}
      {groupedCards.length === 0 ? (
        <div className="op-empty" data-testid="wh-prep-empty">
          <div className="op-empty-icon"><CheckCircle2 size={32}/></div>
          <h4 className="op-empty-title">
            {gudangCards.length === 0 ? "Semua barang sudah disiapkan" : "Tidak ada kartu yang cocok filter"}
          </h4>
          <p className="op-empty-msg">
            {gudangCards.length === 0
              ? "Belum ada tugas penyiapan Gudang saat ini. Tugas baru muncul otomatis dari nota aktif."
              : `Coba ubah filter ${statusFilter === "all" ? "" : `"${statusFilter}"`} atau kosongkan pencarian.`}
          </p>
        </div>
      ) : (
        <ul className={`wh-prep-list${compact ? " rido-desk-grid rpc-grid" : ""}`} data-testid="wh-prep-list">
          {groupedCards.map((c, i) => compact ? (
            <PenyiapanCardCompact key={c.groupKey} card={c} idx={i}
              busy={busyKey === c.key || (c.productCards||[]).some(pc => busyKey === pc.key)}
              expanded={expanded.has(c.groupKey)} onToggleExpand={() => toggleExpand(c.groupKey)}
              onOpenDetail={() => onOpen(c.order)} onOpenMockup={() => onOpenMockup(c.order)}
              selected={selectedOrders.has(c.order.order_number)}
              onToggleSelected={(e) => toggleOrderSelected(c.order.order_number, e)}
              onMarkMockupPrint={onMarkMockupPrint}/>
          ) : (
            <PenyiapanCard
              key={c.groupKey}
              card={c}
              idx={i}
              opName={opName}
              user={user}
              isAdmin={isAdmin}
              isMine={isMineSedang(c)}
              isClaimed={isClaimed(c)}
              isDone={isMineDone(c)}
              busy={busyKey === c.key || (c.productCards||[]).some(pc => busyKey === pc.key)}
              expanded={expanded.has(c.groupKey)}
              onToggleExpand={() => toggleExpand(c.groupKey)}
              onOpenDetail={() => onOpen(c.order)}
              onOpenMockup={() => onOpenMockup(c.order)}
              selectMode={selectMode}
              selected={selectedOrders.has(c.order.order_number)}
              onToggleSelected={() => toggleOrderSelected(c.order.order_number)}
              onClaim={() => {
                // Iter 246 — Claim SEMUA produk dalam group dgn 1 klik.
                for (const pc of (c.productCards || [c])) {
                  pc.mode === "parallel" ? onParallelClaim(pc, pc.track.track) : onClaim(pc);
                }
              }}
              onAdvance={() => {
                for (const pc of (c.productCards || [c])) {
                  pc.mode === "parallel" ? onParallelDone(pc, pc.track.track) : onAdvance(pc);
                }
              }}
              onClaimRaw={onClaim}
              onAdvanceRaw={onAdvance}
              onParallelClaimRaw={onParallelClaim}
              onParallelDoneRaw={onParallelDone}
              onMarkMockupPrint={onMarkMockupPrint}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// Iter 327 — Overview Gudang di tab Pemantauan.
// Memindahkan 5 ringkasan (Belum Disiapkan, Sedang Disiapkan, Selesai Hari Ini,
// Hampir Deadline, Terlambat) + grafik deadline 7 hari dari halaman kerja utama.
// Dibungkus sebagai panel header di atas ProductionMonitoring supaya tab Pemantauan
// jadi satu halaman ringkas untuk Rido. Tidak memengaruhi tab lain atau alur Offline.
// ============================================================================

// ============================================================================
// Iter 372 — RidoLainnyaMenu
// Dropdown "Lainnya" sebagai akses pendukung untuk Rido. Hanya memuat tab yang
// berbeda dari primary tiles (Cetak Mockup = prep, Pantau Produksi = monitor):
//   - Menunggu ACC (opSection=acc)
//   - Selesai (opSection=selesai)
//   - Riwayat (opSection=riwayat)
// Tidak menambahkan Siapkan Barang/Penyiapan karena membuka halaman yang sama
// dengan primary tile Cetak Mockup. Permission existing dipertahankan — komponen
// ini hanya shortcut navigasi ke setOpSection yang sudah ada.
// ============================================================================
function RidoLainnyaMenu({ opSection, setOpSection, kpi }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const items = [
    { key: "acc", label: "Menunggu ACC", count: kpi.gudang_acc || 0 },
    { key: "selesai", label: "Selesai", count: kpi.gudang_selesai || 0 },
    { key: "riwayat", label: "Riwayat", count: kpi.gudang_riwayat || 0 },
  ];
  const activeItem = items.find(it => it.key === opSection);
  // Iter 578 — Badge hanya pekerjaan yang perlu tindakan (Menunggu ACC); Selesai/Riwayat
  //   (kumulatif) tidak dijumlahkan karena satuannya berbeda.
  const totalBadge = kpi.gudang_acc || 0;
  return (
    <div className="rido-lainnya-wrap" ref={ref} data-testid="rido-lainnya-wrap">
      <button
        type="button"
        className={`rido-lainnya-trigger ${activeItem ? "has-active" : ""}`}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="rido-lainnya-trigger"
      >
        <span className="rido-lainnya-label">
          {activeItem ? activeItem.label : "Lainnya"}
        </span>
        {!activeItem && totalBadge > 0 && (
          <span className="rido-lainnya-badge" data-testid="rido-lainnya-badge">{totalBadge}</span>
        )}
        <ChevronDown size={16} className={`rido-lainnya-chev ${open ? "open" : ""}`} aria-hidden="true"/>
      </button>
      {open && (
        <div className="rido-lainnya-menu" role="menu" data-testid="rido-lainnya-menu">
          {items.map(it => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              className={`rido-lainnya-item ${opSection === it.key ? "active" : ""}`}
              onClick={() => { setOpSection(it.key); setOpen(false); }}
              data-testid={`rido-lainnya-item-${it.key}`}
            >
              <span className="rido-lainnya-item-label">{it.label}</span>
              <span className="rido-lainnya-item-count">{it.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// Iter 354 — Gudang Summary Fold: ringkasan Pekerjaan Gudang + Deadline chart,
//   default tertutup. Dipanggil di tab Pemantauan setelah daftar ProductionMonitoring.
// Iter 518 — Prop `forceOpenDesktop`: pada layar ≥1200px dipaksa terbuka & toggle
//   disembunyikan agar bisa berfungsi sebagai sidebar sticky di layout 2 kolom.
//   Mobile tetap collapsible sebagaimana aslinya; state `open` localStorage dihormati.
function GudangSummaryFold({ operator, orders, todayISO, teamCards, forceOpenDesktop = false }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("gudang_summary_fold_open") === "1"; } catch { return false; }
  });
  const [isDesktop, setIsDesktop] = useState(() => {
    try { return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 1200px)").matches; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("gudang_summary_fold_open", open ? "1" : "0"); } catch {}
  }, [open]);
  useEffect(() => {
    if (!forceOpenDesktop) return;
    try {
      const mq = window.matchMedia("(min-width: 1200px)");
      const handler = (e) => setIsDesktop(!!e.matches);
      // Beberapa browser lama tidak punya addEventListener di MediaQueryList.
      if (mq.addEventListener) mq.addEventListener("change", handler);
      else if (mq.addListener) mq.addListener(handler);
      setIsDesktop(!!mq.matches);
      return () => {
        if (mq.removeEventListener) mq.removeEventListener("change", handler);
        else if (mq.removeListener) mq.removeListener(handler);
      };
    } catch {}
  }, [forceOpenDesktop]);
  const asSidebar = forceOpenDesktop && isDesktop;
  const effectiveOpen = asSidebar ? true : open;
  return (
    <section className={`cross-monitor-fold gudang-summary-fold ${forceOpenDesktop ? "gudang-summary-fold--sidebar" : ""} ${asSidebar ? "gudang-summary-fold--sidebar-active" : ""}`} data-testid="gudang-summary-fold">
      {!asSidebar && (
        <button
          type="button"
          className="cross-monitor-fold-head"
          onClick={()=>setOpen(v=>!v)}
          aria-expanded={open}
          data-testid="gudang-summary-fold-toggle"
        >
          <div className="cross-monitor-fold-text">
            <p className="eyebrow">Lihat ringkasan Gudang</p>
            <p className="muted small">Statistik pekerjaan divisi Gudang + grafik deadline 7 hari.</p>
          </div>
          <span className="cross-monitor-fold-chev" aria-hidden="true">{open ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}</span>
        </button>
      )}
      {effectiveOpen && (
        <div className="cross-monitor-fold-body">
          <GudangOverviewPanel operator={operator} orders={orders} todayISO={todayISO} teamCards={teamCards}/>
        </div>
      )}
    </section>
  );
}

// Iter 352 — Wrapper collapsible untuk Pantauan Seluruh Produksi.
//   Default: TERTUTUP agar tidak menumpuk dua dashboard panjang.
//   State persist localStorage. Memakai akses existing (operator pass-through); TIDAK
//   memperluas permission. Pantauan hanya diisi ProductionMonitoring existing.
function CrossProductionMonitorFold({ operator, onOpen }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("cross_monitor_open") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("cross_monitor_open", open ? "1" : "0"); } catch {}
  }, [open]);
  return (
    <section className="cross-monitor-fold" data-testid="cross-monitor-fold">
      <button
        type="button"
        className="cross-monitor-fold-head"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-controls="cross-monitor-body"
        data-testid="cross-monitor-fold-toggle"
      >
        <div className="cross-monitor-fold-text">
          <p className="eyebrow">Pantauan Seluruh Produksi</p>
          <p className="muted small">Pantau progres lintas divisi. Untuk menyiapkan barang, buka tab <b>Siapkan Barang</b>.</p>
        </div>
        <span className="cross-monitor-fold-chev" aria-hidden="true">
          {open ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
        </span>
      </button>
      {open && (
        <div id="cross-monitor-body" className="cross-monitor-fold-body">
          <ProductionMonitoring operator={operator} onOpen={onOpen}/>
        </div>
      )}
    </section>
  );
}

function GudangOverviewPanel({ operator, orders, todayISO, teamCards }) {  const opName = operator?.name || "";
  const metrics = useMemo(() => {
    let belum = 0, sedang = 0, hampir = 0, terlambat = 0, selesaiToday = 0;
    for (const o of (orders || [])) {
      if (!o || o.order_status === "Draft" || o.order_status === "Dibatalkan") continue;
      for (const p of (o.products || [])) {
        const stage = p.production_stage || "";
        const tracks = PARALLEL_STAGE_TRACKS[stage];
        let inGudang = false, mineSedang = false, claimed = false;
        if (stage === "Gudang") {
          inGudang = true;
          claimed = !!p.production_pic;
          mineSedang = p.production_pic === opName;
        } else if (tracks) {
          const gt = tracks.find(tr => tr.team === "Gudang");
          if (gt && !p.parallel_status?.[`${gt.track}_done`]) {
            inGudang = true;
            claimed = !!p.parallel_status?.[`${gt.track}_claimed_by`];
            mineSedang = p.parallel_status?.[`${gt.track}_claimed_by`] === opName;
          }
        }
        if (inGudang) {
          if (!claimed) belum++;
          if (mineSedang) sedang++;
          const ds = p.deadline_status;
          if (ds === "Terlambat") terlambat++;
          else if (ds === "Hampir Deadline") hampir++;
        }
        for (const h of (p.production_pic_history || [])) {
          if (!h?.at || !h.at.startsWith(todayISO || "")) continue;
          if (h.handed_over_by !== opName) continue;
          if (h.stage_from === "Gudang" || h.track === "gudang") selesaiToday++;
        }
      }
    }
    return { belum, sedang, selesaiToday, hampir, terlambat };
  }, [orders, opName, todayISO]);

  return (
    <section className="wh-prep-tab" data-testid="wh-overview-panel" style={{marginBottom:16}}>
      <header className="wh-prep-head">
        <div>
          <p className="eyebrow">Pekerjaan Gudang</p>
          {/* Iter 352 — Hapus judul "Pemantauan" karena menduplikasi judul tab + judul
              bagian Pantauan Seluruh Produksi di bawah. Satuan angka diklarifikasi (produk). */}
          <p className="muted small">Satuan: <b>produk</b> yang sedang di tahap Gudang (Offline). Grafik: <b>nota</b> dengan kartu Gudang, deadline hari ini s.d. 6 hari ke depan. Cakupan berbeda dengan ringkasan nota di atas, jadi angkanya tidak dapat dibandingkan langsung.</p>
        </div>
      </header>
      <div className="wh-prep-summary" data-testid="wh-overview-summary">
        <SummaryTile label="Belum Disiapkan" sub="produk" value={metrics.belum} tone="belum" testId="wh-overview-belum" active={false} onClick={null}/>
        <SummaryTile label="Sedang Disiapkan" sub="produk" value={metrics.sedang} tone="sedang" testId="wh-overview-sedang" active={false} onClick={null}/>
        <SummaryTile label="Selesai di Gudang Hari Ini" sub="produk" value={metrics.selesaiToday} tone="sudah" testId="wh-overview-selesai" active={false} onClick={null}/>
        <SummaryTile label="Hampir Deadline" sub="produk · Gudang" value={metrics.hampir} tone="hampir" testId="wh-overview-hampir" active={false} onClick={null}/>
        <SummaryTile label="Terlambat" sub="produk · Gudang" value={metrics.terlambat} tone="terlambat" testId="wh-overview-terlambat" active={false} onClick={null}/>
      </div>
      <DeadlineMiniChart teamCards={teamCards} todayISO={todayISO}/>
    </section>
  );
}

// ============================================================================
// Iter 327 — Tab Selesai Gudang.
// Menampilkan daftar produk Gudang yang handed-over oleh operator ini hari ini.
// Kartu minimalis via PenyiapanCard dengan selesaiTodayView=true (tombol tersembunyi).
// ============================================================================
function GudangSelesaiList({ operator, orders, todayISO, onOpen, onOpenMockup }) {
  const opName = operator?.name || "";
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const cards = useMemo(() => {
    const list = [];
    const seen = new Set();
    for (const o of (orders || [])) {
      if (!o) continue;
      for (const p of (o.products || [])) {
        for (const h of (p.production_pic_history || [])) {
          if (!h?.at || !h.at.startsWith(todayISO || "")) continue;
          if (h.handed_over_by !== opName) continue;
          const single = h.stage_from === "Gudang";
          const paralel = h.track === "gudang";
          if (!single && !paralel) continue;
          const mode = paralel ? "parallel" : "single";
          const dedupeKey = `${o.order_number}::${p.id || p.name}::${mode}`;
          if (seen.has(dedupeKey)) continue;
          seen.add(dedupeKey);
          const track = paralel ? { track: "gudang", team: "Gudang", label: "Gudang" } : null;
          list.push({
            order: o, product: p, mode, stage: single ? "Gudang" : (p.production_stage || ""),
            track, key: `${dedupeKey}::selesai-today`, selesaiTodayView: true, doneAt: h.at,
          });
        }
      }
    }
    return sortByDeadlinePriority(list);
  }, [orders, opName, todayISO]);

  const filtered = cards.filter(c => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (c.order.order_number || "").toLowerCase().includes(q) ||
      (c.order.customer_name || "").toLowerCase().includes(q) ||
      (c.product.name || "").toLowerCase().includes(q) ||
      (c.product.color || "").toLowerCase().includes(q)
    );
  });

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const c of filtered) {
      const gk = `${c.order.order_number}`;
      if (!groups.has(gk)) groups.set(gk, { ...c, groupKey: gk, productCards: [c] });
      else groups.get(gk).productCards.push(c);
    }
    for (const g of groups.values()) {
      g.subGroups = [{ key: "selesai", cards: g.productCards, primary: g }];
    }
    return Array.from(groups.values());
  }, [filtered]);

  const toggleExpand = (key) => setExpanded(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  return (
    <div className="wh-prep-tab" data-testid="wh-selesai-tab">
      <header className="wh-prep-head">
        <div>
          <p className="eyebrow">Hari Ini</p>
          <h2 className="wh-prep-title">Selesai Hari Ini</h2>
          <p className="muted small">Pekerjaan Gudang yang sudah Anda selesaikan hari ini. Reset otomatis setiap tanggal baru.</p>
        </div>
      </header>
      <div className="wh-prep-filter-bar">
        <input type="text" className="wh-prep-search" data-testid="wh-selesai-search" placeholder="Cari nota / pelanggan / produk…" value={query} onChange={e => setQuery(e.target.value)}/>
      </div>
      {grouped.length === 0 ? (
        <div className="op-empty" data-testid="wh-selesai-empty">
          <div className="op-empty-icon"><CheckCircle2 size={32}/></div>
          <h4 className="op-empty-title">Belum ada yang diselesaikan hari ini</h4>
          <p className="op-empty-msg">Pekerjaan yang Anda handover hari ini akan muncul di sini.</p>
        </div>
      ) : (
        <ul className="wh-prep-list" data-testid="wh-selesai-list">
          {grouped.map((c, i) => (
            <PenyiapanCard
              key={c.groupKey}
              card={c}
              idx={i}
              opName={opName}
              user={null}
              isAdmin={false}
              isMine={false}
              isClaimed={true}
              isDone={true}
              busy={false}
              expanded={expanded.has(c.groupKey)}
              onToggleExpand={() => toggleExpand(c.groupKey)}
              onOpenDetail={() => onOpen(c.order)}
              onOpenMockup={() => onOpenMockup(c.order)}
              onClaim={() => {}}
              onAdvance={() => {}}
              onClaimRaw={() => {}}
              onAdvanceRaw={() => {}}
              onParallelClaimRaw={() => {}}
              onParallelDoneRaw={() => {}}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// Iter 303 — "Menunggu ACC Barang" (section terpisah untuk Adit / Rido / Owner).
// List produk yang mockup-nya sudah DICETAK oleh Rido tapi BELUM di-ACC. Tombol
// "ACC Barang Siap" hanya aktif bagi operator tim Gudang; backend akan menolak
// pengguna lain. Tidak mengurangi stok. ACC per-item (nota multi-produk boleh
// parsial). Setelah semua item di-ACC, tahap Gudang (atau track gudang paralel)
// otomatis dimajukan oleh backend.
// ============================================================================
function AccBarangList({ operator, orders, busyKey, onOpen, onOpenMockup, onMarkAccReady, onMarkMockupPrint, highlightQuery, isAccOnly, gudangPrepCount, onJumpPrep }) {
  const [query, setQuery] = useState("");
  // Iter 329 — Tab Menunggu ACC HANYA berisi kartu yg mockup sudah dicetak Rido (`mockup_print_at`)
  //   dan belum di-ACC. Sub-tab "Belum Dicetak" dihapus — tombol "Tandai Mockup Dicetak" pindah ke
  //   tab Siapkan Barang (`PenyiapanCard`). Satu kartu hanya muncul di satu tempat.
  // Hanya nota Offline (bukan Shopee) dalam kondisi aktif.
  // Iter 393 — Grouping fix: 1 nota = 1 kartu. Variasi/size/warna berbeda TIDAK memecah kartu.
  //   Produk yang memenuhi syarat (mockup dicetak & belum ACC, scope Gudang) dikumpulkan di
  //   level order_number; nota multi-produk menjadi satu kartu dgn produk sebagai sections.
  const items = useMemo(() => {
    const byOrder = new Map();
    for (const o of (orders || [])) {
      if (!o) continue;
      if (o.order_status === "Draft" || o.order_status === "Dibatalkan") continue;
      if (o.order_status === "Selesai" || o.order_status === "Menunggu Finalisasi") continue;
      if (String(o.source || "").trim().toLowerCase() === "shopee") continue;
      const qualifyingProducts = [];
      for (const p of (o.products || [])) {
        const stage = p.production_stage || "";
        const tracks = PARALLEL_STAGE_TRACKS[stage];
        const inGudangScope = stage === "Gudang" || (tracks && tracks.some(t => t.team === "Gudang"));
        if (!inGudangScope) continue;
        if (p.mockup_print_at && !p.barang_acc_at) qualifyingProducts.push(p);
      }
      if (qualifyingProducts.length === 0) continue;
      // Grouping key: order_number (stabil & bukan product/variant/size/activity id).
      byOrder.set(o.order_number, { order: o, products: qualifyingProducts });
    }
    const waiting = Array.from(byOrder.values());
    return { waiting };
  }, [orders]);

  const filtered = items.waiting.filter(entry => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    const name = (entry.order.customer_name || "").toLowerCase();
    const num = (entry.order.order_number || "").toLowerCase();
    const prodMatch = entry.products.some(p => (p.custom_name || p.name || "").toLowerCase().includes(q));
    return name.includes(q) || num.includes(q) || prodMatch;
  });

  const canAct = String(operator?.team || "") === "Gudang";

  return (
    <div className="wh-acc-tab" data-testid="wh-acc-tab" style={{display:"flex", flexDirection:"column", gap:10}}>
      {/* Iter 371 — Chip filter cetak sejajar dengan tab Siapkan Barang. Chip
          "Belum Dicetak" membawa Rido kembali ke tab prep; chip "Sudah Dicetak"
          aktif di tab ini. Untuk Adit (isAccOnly) chip "Belum Dicetak" disembunyikan
          karena tidak punya akses ke tab Siapkan Barang. */}
      {operator?.team === "Gudang" && typeof onJumpPrep === "function" && (
        <div className="rido-print-filter" data-testid="rido-print-filter-acc" role="tablist" aria-label="Filter status cetak mockup">
          <button
            type="button"
            role="tab"
            aria-selected={false}
            className="rido-print-chip"
            onClick={()=>onJumpPrep()}
            data-testid="rido-print-chip-belum-acc"
          >
            Belum Dicetak <span className="rido-print-chip-count">{gudangPrepCount || 0}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={true}
            className="rido-print-chip active"
            data-testid="rido-print-chip-sudah-acc"
          >
            Sudah Dicetak <span className="rido-print-chip-count">{items.waiting.length}</span>
          </button>
        </div>
      )}
      <header className="wh-prep-head">
        <div>
          <p className="eyebrow">{isAccOnly ? "Tugas Adit" : "Tugas Gudang"}</p>
          <h2 className="wh-prep-title" data-testid="wh-acc-title">Menunggu ACC</h2>
          <p className="muted small">
            {isAccOnly
              ? "Periksa fisik barang yang mockup-nya sudah dicetak Rido, lalu tekan ACC Barang Siap. Anda tidak dapat memindahkan tahap, mengakses stok, atau mencetak mockup."
              : "Barang yang mockup-nya sudah Anda cetak, menunggu Adit memeriksa fisik dan tekan ACC Barang Siap. Tahap otomatis maju setelah semua item di nota di-ACC."}
          </p>
        </div>
      </header>
      <div className="wh-prep-filter-bar">
        <input type="text" className="wh-prep-search" data-testid="wh-acc-search" placeholder="Cari nota / pelanggan / produk…" value={query} onChange={e=>setQuery(e.target.value)}/>
      </div>
      {filtered.length === 0 ? (
        <div className="op-empty" data-testid="wh-acc-empty">
          <div className="op-empty-icon"><CheckCircle2 size={32}/></div>
          <h4 className="op-empty-title">Tidak ada barang menunggu ACC</h4>
          <p className="op-empty-msg">Barang yang mockup-nya sudah dicetak Rido akan muncul di sini.</p>
        </div>
      ) : (
        <ul className="wh-prep-list" data-testid="wh-acc-list" style={{listStyle:"none", padding:0, margin:0, display:"flex", flexDirection:"column", gap:8}}>
          {filtered.map((entry, i) => (
            <AccBarangCard
              key={entry.order.order_number}
              order={entry.order}
              products={entry.products}
              idx={i}
              canAct={canAct && isAccOnly}
              isAccOnly={isAccOnly}
              busyKey={busyKey}
              highlightQuery={highlightQuery || query}
              onOpenMockup={()=>onOpenMockup(entry.order)}
              onMarkAccReady={onMarkAccReady}
              onOpenCard={()=>onOpen(entry.order)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// Iter 329 — Kartu Menunggu ACC: sederhana, hanya 1 tombol utama (ACC Barang Siap).
//   "Buka Nota" dihapus demi konsistensi dgn PenyiapanCard; klik header kartu membuka nota.
// Iter 331 — Rido (non-ACC-only) TIDAK menampilkan tombol ACC. Ganti dengan
//   status pasif "Menunggu pemeriksaan Adit".
// Iter 393 — Grouping 1 nota = 1 kartu. Produk ditampilkan sebagai sections di dalam kartu.
//   Variasi/size/warna berbeda tidak lagi memecah kartu. Setiap produk tetap punya tombol
//   ACC terpisah (backend acc-ready per-product tidak berubah).
function AccBarangCard({ order, products, idx, canAct, isAccOnly, busyKey, highlightQuery, onOpenMockup, onMarkAccReady, onOpenCard }) {
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const parts = new Intl.DateTimeFormat("id-ID", { timeZone:"Asia/Jakarta", day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit", hour12:false }).formatToParts(d);
      const dd = parts.find(p=>p.type==="day")?.value || "";
      const mm = parts.find(p=>p.type==="month")?.value || "";
      const hh = parts.find(p=>p.type==="hour")?.value || "";
      const mi = parts.find(p=>p.type==="minute")?.value || "";
      return `${dd}/${mm} ${hh}:${mi}`;
    } catch { return iso; }
  };
  // Total gabungan pcs semua produk dalam nota ini.
  const grandTotalPcs = products.reduce((acc, p) => {
    const sizes = Array.isArray(p.sizes) ? p.sizes : [];
    const t = Number(p.quantity || 0) || sizes.reduce((s, x) => s + Number(x.quantity || 0), 0);
    return acc + t;
  }, 0);
  const isMultiProd = products.length > 1;
  return (
    <li className="wh-prep-card wh-prep-normal" data-testid={`wh-acc-card-${idx}`}
      data-order-number={order.order_number}>
      <div className="wh-prep-card-head" role="button" tabIndex={0}
        onClick={(e) => { if (!e.target.closest("button")) onOpenCard?.(); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenCard?.(); } }}
        style={{cursor:"pointer"}}
        title="Klik untuk buka nota">
        <div className="wh-prep-card-cust">
          <strong className="wh-prep-cust-name" data-testid={`wh-acc-cust-${idx}`}>{highlightMatch(order.customer_name || "—", highlightQuery)}</strong>
          <span className="muted small wh-prep-nota" data-testid={`wh-acc-nota-${idx}`}>Nota {highlightMatch(order.order_number, highlightQuery)}</span>
          {isMultiProd && <span className="muted small" data-testid={`wh-acc-prod-count-${idx}`}>· {products.length} produk · Total {grandTotalPcs} pcs</span>}
        </div>
        <span className="chip chip-muted" title={`Deadline ${order.deadline || "—"}`}>
          {order.deadline || "—"}
        </span>
      </div>
      <NotaGroupGudangInfo orderNumber={order.order_number}/>
      <div className="wh-prep-card-body-row">
        <OrderMockupThumb orderNumber={order.order_number}
          onOpen={onOpenMockup}
          testId={`wh-acc-thumb-${idx}`}/>
        <div className="wh-prep-card-info" data-testid={`wh-acc-info-${idx}`} style={{display:"flex", flexDirection:"column", gap:10}}>
          {products.map((product, pi) => {
            const sizes = Array.isArray(product.sizes) ? product.sizes : [];
            const totalPcs = Number(product.quantity || 0) || sizes.reduce((s, x) => s + Number(x.quantity || 0), 0);
            const prodBusy = busyKey === `acc::${order.order_number}::${product.id}`;
            return (
              <div key={product.id || pi}
                className="wh-acc-prod-section"
                data-testid={`wh-acc-prod-${idx}-${pi}`}
                style={{paddingTop: pi === 0 ? 0 : 8, borderTop: pi === 0 ? "none" : "1px dashed rgba(100,116,139,.25)"}}>
                <div className="wh-prep-card-product" data-testid={`wh-acc-product-${idx}-${pi}`}>
                  <strong>{highlightMatch(product.custom_name || product.name || "—", highlightQuery)}</strong>
                  {product.color && <span className="muted"> · {product.color}</span>}
                  {product.sleeve && product.sleeve !== "Tidak Berlaku" && <span className="muted"> · {product.sleeve}</span>}
                </div>
                <div className="wh-prep-card-sizes" data-testid={`wh-acc-sizes-${idx}-${pi}`}>
                  {sizes.length === 0
                    ? <span className="muted small">Tidak ada rincian ukuran.</span>
                    : sizes.map((s, si) => {
                        const c = (s.color || product.color || "").trim();
                        const v = (s.variant || (product.sleeve && product.sleeve !== "Tidak Berlaku" ? product.sleeve : "") || "").trim();
                        const label = `${c ? c + " · " : ""}${s.size}${v ? ` (${v})` : ""}`;
                        return (
                          <span key={si} className="prep-size-chip" data-testid={`wh-acc-size-${idx}-${pi}-${si}`}>
                            <span className="prep-size-chip-label">{label}</span>
                            <strong>{s.quantity}</strong>
                          </span>
                        );
                      })}
                  <span className="wh-prep-total">Total: <strong>{totalPcs} pcs</strong></span>
                </div>
                <div className="muted small" data-testid={`wh-acc-meta-${idx}-${pi}`} style={{marginTop:4}}>
                  🖨 Mockup dicetak oleh <strong>{product.mockup_print_by || "—"}</strong> · {fmtTime(product.mockup_print_at)}
                </div>
                <div className="wh-prep-actions" style={{marginTop:6}}>
                  <span className="wh-prep-actions-spacer"/>
                  {isAccOnly ? (
                    <button type="button" className="primary small-btn" data-testid={`wh-acc-mark-${idx}-${pi}`}
                      onClick={()=>onMarkAccReady({ order, product, key: `acc::${order.order_number}::${product.id}` })}
                      disabled={!canAct || prodBusy}
                      title={!canAct ? "Hanya operator tim Gudang (Adit) atau Owner" : ""}>
                      {prodBusy ? "Memproses…" : "✅ ACC Barang Siap"}
                    </button>
                  ) : (
                    <span className="wh-prep-status-passive" data-testid={`wh-acc-passive-${idx}-${pi}`}
                      title="ACC Barang hanya dilakukan oleh Adit">
                      Menunggu pemeriksaan Adit
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </li>
  );
}

function SummaryTile({ label, value, tone, testId, active, onClick, sub }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      className={`wh-sum-tile wh-sum-${tone} ${active ? "active" : ""} ${onClick ? "wh-sum-clickable" : ""}`}
      data-testid={testId}
      onClick={onClick || undefined}
    >
      <span className="wh-sum-label">{label}</span>
      <strong className="wh-sum-value" data-testid={`${testId}-value`}>{value}</strong>
      {/* Iter 352 — Keterangan satuan (produk/nota/pcs) untuk memperjelas cakupan angka. */}
      {sub && <span className="wh-sum-sub" data-testid={`${testId}-sub`}>{sub}</span>}
    </Tag>
  );
}

// ============================================================================
// Iter 330.1 — Thumbnail mockup kecil di kartu Gudang.
// Pakai endpoint existing /api/orders/{number}/mockup.preview (default pertama).
// 72–96px compact; onError → placeholder "Belum ada mockup".
// Klik thumbnail = buka modal preview besar (sama seperti tombol Lihat Mockup).
// Tidak mengubah backend, tidak menambah tinggi kartu secara berlebihan.
// Indikator "+N" = jumlah produk TAMBAHAN di nota (approx materi mockup tambahan);
// angka akurat tersedia saat user tekan "Lihat Mockup" (modal navigator).
// ============================================================================
function OrderMockupThumb({ orderNumber, extraCount = 0, onOpen, testId }) {
  // Iter 353 — Perjelas 3 state: loading, empty (404 = memang belum ada),
  //   dan network-error (gagal muat → tombol "Coba lagi" naikkan versi URL).
  const [state, setState] = useState({ phase: "loading", version: 0 });
  const url = `${API}/orders/${encodeURIComponent(orderNumber)}/mockup.preview?v=${state.version}`;
  const handleClick = (e) => {
    e.stopPropagation();
    if (state.phase === "error") {
      // "Coba lagi" di state error: naikkan versi untuk force reload.
      setState({ phase: "loading", version: state.version + 1 });
      return;
    }
    if (onOpen) onOpen();
  };
  const handleError = (e) => {
    // 404 (empty) dibedakan dari network/5xx: fetch HEAD cek status cepat.
    // Shortcut: pakai e.currentTarget.naturalWidth === 0; cek via fetch untuk status.
    fetch(url, { credentials: "include" }).then(r => {
      if (r.status === 404) setState(s => ({ phase: "empty", version: s.version }));
      else setState(s => ({ phase: "error", version: s.version }));
    }).catch(() => setState(s => ({ phase: "error", version: s.version })));
  };
  return (
    <button type="button" className={`wh-prep-thumb wh-prep-thumb-${state.phase}`} onClick={handleClick}
      data-testid={testId}
      aria-label={state.phase === "error" ? `Coba muat ulang mockup nota ${orderNumber}` : `Pratinjau mockup nota ${orderNumber}`}
      title={state.phase === "error" ? "Klik untuk coba lagi" : "Klik untuk preview mockup besar"}>
      {state.phase === "empty" ? (
        <span className="wh-prep-thumb-empty" data-testid={`${testId}-empty`}>
          <ImageIcon size={18}/>
          <span>Belum ada<br/>mockup</span>
        </span>
      ) : state.phase === "error" ? (
        <span className="wh-prep-thumb-empty wh-prep-thumb-error" data-testid={`${testId}-error`}>
          <ImageIcon size={18}/>
          <span>Gagal memuat<br/><b>Coba lagi</b></span>
        </span>
      ) : (
        <>
          <img key={state.version} src={url} alt="" draggable="false" loading="lazy" decoding="async"
            onLoad={() => setState(s => ({ phase: "ok", version: s.version }))}
            onError={handleError}
            style={{ objectFit: "contain" }}/>
          {state.phase === "loading" && (
            <span className="wh-prep-thumb-loading" data-testid={`${testId}-loading`} aria-hidden="true">
              <span className="wh-prep-thumb-spinner"/>
            </span>
          )}
        </>
      )}
      {extraCount > 0 && state.phase === "ok" && (
        <span className="wh-prep-thumb-badge" data-testid={`${testId}-plus`}>+{extraCount}</span>
      )}
    </button>
  );
}

function PenyiapanCard({ card, idx, opName, user, isAdmin, isMine, isClaimed, isDone, busy, expanded, onToggleExpand, onOpenDetail, onOpenMockup, onClaim, onAdvance, onClaimRaw, onAdvanceRaw, onParallelClaimRaw, onParallelDoneRaw, onMarkMockupPrint, selectMode, selected, onToggleSelected }) {
  const { order, product, mode, stage, track } = card;
  const isSelesaiTodayView = !!card.selesaiTodayView;
  // Iter 246 — Group produk 1 nota di tab sama. Semua produk di `card.productCards`.
  const productList = Array.isArray(card.productCards) && card.productCards.length > 0
    ? card.productCards.map(pc => pc.product)
    : [product];
  const ds = product.deadline_status || "Aman";
  const chipCls = ds === "Terlambat" ? "chip-danger" : ds === "Hampir Deadline" ? "chip-amber" : ds === "Aman" ? "chip-safe" : "chip-muted";
  // Total pcs = gabungan semua produk di group
  const totalPcs = productList.reduce((sum, p) => {
    const sizes = Array.isArray(p.sizes) ? p.sizes : [];
    const pcs = Number(p.quantity || 0) || sizes.reduce((s, x) => s + Number(x.quantity || 0), 0);
    return sum + pcs;
  }, 0);
  const otherClaimer = !isMine && isClaimed
    ? (mode === "parallel" ? (product.parallel_status?.[`${track.track}_claimed_by`] || "") : (product.production_pic || ""))
    : "";
  const otherTracksStatus = mode === "parallel"
    ? (PARALLEL_STAGE_TRACKS[stage] || []).filter(t => t.track !== track.track).map(t => {
        const ps = product.parallel_status || {};
        return { label: t.label, done: !!ps[`${t.track}_done`], by: ps[`${t.track}_done_by`], claimedBy: ps[`${t.track}_claimed_by`] };
      })
    : [];
  const rowCls = ds === "Terlambat" ? "wh-prep-overdue" : ds === "Hampir Deadline" ? "wh-prep-near" : "wh-prep-normal";
  return (
    <li className={`wh-prep-card ${rowCls}${selectMode && selected ? " wh-prep-selected" : ""}`} data-testid={`wh-prep-card-${idx}`}>
      <div className="wh-prep-card-head" role="button" tabIndex={0}
        onClick={(e) => {
          if (e.target.closest("button") || e.target.closest("input[type=checkbox]")) return;
          if (selectMode) { onToggleSelected?.(); return; }
          onOpenDetail?.();
        }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (selectMode) onToggleSelected?.(); else onOpenDetail?.(); } }}
        style={{cursor:"pointer"}}
        data-testid={`wh-prep-card-head-${idx}`}
        title={selectMode ? "Klik untuk pilih/batalkan" : "Klik untuk buka nota"}>
        {selectMode && <input type="checkbox"
          checked={!!selected}
          onChange={() => onToggleSelected?.()}
          onClick={(e)=>e.stopPropagation()}
          data-testid={`wh-prep-select-${idx}`}
          aria-label={`Pilih nota ${order.order_number} untuk cetak`}
          style={{width:18, height:18, accentColor:"#D4AF37", marginRight:8, flexShrink:0}}/>}
        <div className="wh-prep-card-cust">
          <strong className="wh-prep-cust-name" data-testid={`wh-prep-cust-${idx}`}>{order.customer_name || "—"}</strong>
          <span className="muted small wh-prep-nota" data-testid={`wh-prep-nota-${idx}`}>Nota {order.order_number}</span>
        </div>
        <span className={`chip ${chipCls}`} data-testid={`wh-prep-deadline-${idx}`} title={`Deadline ${order.deadline || "—"}`}>
          {ds} · {order.deadline || "—"}
        </span>
      </div>
      <div className="wh-prep-card-body-row">
        <OrderMockupThumb orderNumber={order.order_number}
          extraCount={productList.length > 1 ? productList.length - 1 : 0}
          onOpen={onOpenMockup}
          testId={`wh-prep-thumb-${idx}`}/>
        <div className="wh-prep-card-info" data-testid={`wh-prep-info-${idx}`}>
          <div className="wh-prep-card-product" data-testid={`wh-prep-product-${idx}`}>
            {productList.length === 1 ? (
              <>
                <strong>{productList[0].name}</strong>
                {/* Iter 536 — Jenis Pengerjaan per-item (shared badge) */}
                <JenisPengerjaanBadge product={productList[0]} order={order} size="xs" className="jp-badge-inline" testIdPrefix={`wh-prep-jenis-${idx}`}/>
                {productList[0].color && <span className="muted"> · {productList[0].color}</span>}
                {productList[0].sleeve && productList[0].sleeve !== "Tidak Berlaku" && <span className="muted"> · {productList[0].sleeve}</span>}
              </>
            ) : (
              // Iter 246 — Multi-produk 1 nota: daftar ringkas semua produk.
              <ul className="wh-prep-product-list" style={{margin:0, padding:"0 0 0 14px"}}>
                {productList.map((p, i) => <li key={i} style={{margin:"1px 0"}}>
                  <strong>{p.name}</strong>
                  {/* Iter 536 — Jenis Pengerjaan per-item di multi-produk juga */}
                  <JenisPengerjaanBadge product={p} order={order} size="xs" className="jp-badge-inline" testIdPrefix={`wh-prep-jenis-${idx}-${i}`}/>
                  {p.color && <span className="muted"> · {p.color}</span>}
                </li>)}
              </ul>
            )}
          </div>
          <div className="wh-prep-card-sizes" data-testid={`wh-prep-sizes-${idx}`}>
            {productList.flatMap((p, pi) => {
              const sz = Array.isArray(p.sizes) ? p.sizes : [];
              return sz.length === 0
                ? []
                : sz.map((s, sidx) => {
                    const c = (s.color || p.color || "").trim();
                    const v = (s.variant || (p.sleeve && p.sleeve !== "Tidak Berlaku" ? p.sleeve : "") || "").trim();
                    const label = `${c ? c + " · " : ""}${s.size}${v ? ` (${v})` : ""}`;
                    return (
                      <span key={`${pi}-${sidx}`} className="prep-size-chip" data-testid={`wh-prep-size-${idx}-${pi}-${sidx}`}>
                        <span className="prep-size-chip-label">{label}</span>
                        <strong>{s.quantity}</strong>
                      </span>
                    );
                  });
            })}
            <span className="wh-prep-total" data-testid={`wh-prep-total-${idx}`}>Total: <strong>{totalPcs} pcs</strong>{productList.length > 1 && ` · ${productList.length} produk`}</span>
          </div>
        </div>
      </div>
      <NotaGroupGudangInfo orderNumber={order.order_number}/>
      {product.note && <div className="wh-prep-note" data-testid={`wh-prep-note-${idx}`}><span className="muted small">Catatan:</span> {product.note}</div>}
      <div className="wh-prep-actions">
        <button type="button" className="text-btn small-btn wh-prep-expand-btn" data-testid={`wh-prep-expand-${idx}`} onClick={onToggleExpand} aria-expanded={expanded}>
          {expanded ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
          {expanded ? " Tutup" : " Detail"}
        </button>
        <span className="wh-prep-actions-spacer"/>
        {isSelesaiTodayView ? (
          <span className="chip chip-safe wh-prep-done-chip" data-testid={`wh-prep-done-today-${idx}`} title="Sudah Anda selesaikan hari ini">
            <CheckCircle2 size={12}/> Sudah selesai hari ini
          </span>
        ) : (
          /* Iter 373 — Aksi Cetak Mockup Rido:
             - Thumbnail (atas) → preview (tidak ubah status).
             - Tombol Cetak Mockup → modal A5/A4 → jendela cetak.
             - Tombol Unduh Mockup → download file (tidak ubah status).
             - Tombol Tandai Mockup Dicetak → konfirmasi manual → POST mockup-print.
             Klaim ("Mulai Siapkan") DIHAPUS dari alur cetak — tidak lagi prasyarat.
             Multi-produk: aksi cetak/unduh pakai preview nota (file paling baru);
             tandai mencakup SEMUA produk belum ditandai di kartu — bila ada file
             berbeda per produk, buka Detail nota untuk aksi per-file. */
          <PenyiapanCetakActions
            card={card}
            idx={idx}
            busy={busy}
            onMarkMockupPrint={onMarkMockupPrint}
          />
        )}
      </div>
      {expanded && (
        <div className="wh-prep-expand" data-testid={`wh-prep-expand-body-${idx}`}>
          <div className="wh-prep-expand-info">
            <p className="muted small">
              Tahap saat ini: <strong>{stage}</strong>
              {product.production_pic ? <> · Operator <strong>{product.production_pic}</strong></> : null}
              {" · Route: "}<strong>{product.production_route || "—"}</strong>
            </p>
            {mode === "parallel" && (
              <p className="muted small">
                <span>Jalur paralel — Gudang & tim lain berjalan bersamaan.</span>
                {otherTracksStatus.length > 0 && <>
                  {" Status: "}
                  {otherTracksStatus.map((t, ti) => (
                    <span key={ti}>
                      {t.label}: <strong className={t.done ? "wh-track-done" : "wh-track-wait"}>
                        {t.done ? `selesai${t.by ? ` (${t.by})` : ""}` : (t.claimedBy ? `dikerjakan ${t.claimedBy}` : "menunggu")}
                      </strong>{ti < otherTracksStatus.length - 1 ? " · " : ""}
                    </span>
                  ))}
                </>}
                {" · Mesin Press hanya tersedia setelah SEMUA track selesai."}
              </p>
            )}
          </div>
          {Array.isArray(product.production_pic_history) && product.production_pic_history.length > 0 && (
            <HistoryList history={product.production_pic_history} testId={`wh-prep-history-${idx}`}/>
          )}
          {product.id && <ProductFilesPanel orderNumber={order.order_number} productId={product.id} compact/>}
        </div>
      )}
    </li>
  );
}


// Gudang Rido · kartu Cetak Mockup Offline compact (sama dengan kartu Shopee .rdc).
function PenyiapanCardCompact({ card, idx, busy, expanded, onToggleExpand, onOpenDetail, onOpenMockup, selected, onToggleSelected, onMarkMockupPrint }) {
  const { order, product } = card;
  const productList = (card.productCards || [card]).map(pc => pc.product);
  const ds = product.deadline_status || "Aman";
  const dlCls = ds === "Terlambat" ? "late" : ds === "Hampir Deadline" ? "near" : "ok";
  const totalPcs = productList.reduce((sum, p) => sum + (Number(p.quantity || 0) || (p.sizes || []).reduce((t, x) => t + Number(x.quantity || 0), 0)), 0);
  const names = productList.map(p => [p.name, p.color].filter(Boolean).join(" · ")).join(" + ");
  const note = productList.map(p => p.note).filter(Boolean).join(" · ");
  return (
    <li className={`shopee-batch-cell rpc-cell${selected ? " is-picked" : ""}`} data-testid={`wh-prep-card-${idx}`}>
      <label className="shopee-batch-pick" title="Pilih untuk cetak massal (Shift+klik = pilih rentang)">
        <input type="checkbox" checked={!!selected} onClick={(e) => onToggleSelected(e)} onChange={() => {}}
          aria-label={`Pilih nota ${order.order_number} untuk cetak`} data-testid={`wh-prep-select-${idx}`}/>
      </label>
      <div className="card shopee-wh-card rdc rpc" role="button" tabIndex={0} data-testid={`wh-prep-card-head-${idx}`}
        onClick={(e) => { if (!e.target.closest("button, input, a, [role=menu]")) onOpenDetail(); }}
        onKeyDown={(e) => { if (e.target === e.currentTarget && e.key === "Enter") { e.preventDefault(); onOpenDetail(); } }}
        title="Klik untuk buka nota">
        <div className="shopee-wh-top">
          <span className="rpc-thumb"><OrderMockupThumb orderNumber={order.order_number} extraCount={productList.length > 1 ? productList.length - 1 : 0}
            onOpen={onOpenMockup} testId={`wh-prep-thumb-${idx}`}/></span>
          <div className="shopee-wh-info rdc-info">
            <div className="rdc-account" title={order.customer_name || ""} data-testid={`wh-prep-cust-${idx}`}>{order.customer_name || "—"}</div>
            <div className="rdc-order">
              <span className="shopee-order-number" data-testid={`wh-prep-nota-${idx}`}>Nota {order.order_number}</span>
              <RpcCopy text={order.order_number} testId={`wh-prep-copy-${idx}`}/>
            </div>
            <div className="rdc-line">
              <span className={`rpc-dl rpc-dl-${dlCls}`} data-testid={`wh-prep-deadline-${idx}`} title={`Deadline ${order.deadline || "—"}`}>{ds} · {order.deadline || "—"}</span>
              {productList.map((p, i) => <JenisPengerjaanBadge key={i} product={p} order={order} size="xs" className="jp-badge-inline" testIdPrefix={`wh-prep-jenis-${idx}-${i}`}/>)}
            </div>
            <div className="rpc-prod" title={names} data-testid={`wh-prep-product-${idx}`}>{names} · <b data-testid={`wh-prep-total-${idx}`}>{totalPcs} pcs</b></div>
          </div>
        </div>
        <div className="shopee-wh-actions" onClick={e => e.stopPropagation()}>
          <PenyiapanCetakActions card={card} idx={idx} busy={busy} onMarkMockupPrint={onMarkMockupPrint} compact
            onPreview={onOpenMockup} onToggleExpand={onToggleExpand} expanded={expanded} onOpenDetail={onOpenDetail}/>
        </div>
        {note && <div className="rpc-note" title={note} data-testid={`wh-prep-note-${idx}`}>Catatan: {note}</div>}
        <NotaGroupGudangInfo orderNumber={order.order_number}/>
        {expanded && <div className="wh-prep-expand rpc-expand" onClick={e => e.stopPropagation()} data-testid={`wh-prep-expand-body-${idx}`}>
          <div className="wh-prep-card-sizes">
            {productList.flatMap((p, pi) => (p.sizes || []).map((sz, si) => (
              <span key={`${pi}-${si}`} className="prep-size-chip" data-testid={`wh-prep-size-${idx}-${pi}-${si}`}>
                <span className="prep-size-chip-label">{`${(sz.color || p.color || "").trim() ? (sz.color || p.color).trim() + " · " : ""}${sz.size}${sz.variant ? ` (${sz.variant})` : ""}`}</span>
                <strong>{sz.quantity}</strong></span>)))}
          </div>
          {product.id && <ProductFilesPanel orderNumber={order.order_number} productId={product.id} compact/>}
        </div>}
      </div>
    </li>
  );
}

function RpcCopy({ text, testId }) {
  const [ok, setOk] = useState(false);
  return <button type="button" className="rdc-copy" title="Salin nomor nota" aria-label={`Salin nomor nota ${text}`} data-testid={testId}
    onClick={async (e) => { e.stopPropagation(); try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); } catch { /* izin ditolak */ } }}>
    {ok ? <Check size={13}/> : <Copy size={13}/>}</button>;
}

// ============================================================================
// Iter 373 — PenyiapanCetakActions (Rido).
// Baris aksi: [Cetak Mockup] [Unduh Mockup] [Tandai Mockup Dicetak | Progress].
// - Tidak memerlukan klaim ("Mulai Siapkan"). Produk belum diklaim tetap bisa dicetak.
// - Preview/unduh/dialog cetak TIDAK memanggil mockup-print.
// - Tandai Mockup Dicetak membuka modal konfirmasi; hanya setelah "Ya, sudah dicetak"
//   endpoint POST mockup-print dipanggil untuk SEMUA produk kartu yang belum ditandai.
// - Progress badge saat ada produk sudah ditandai sebelumnya (parsial).
// - Busy flag mencegah pencatatan ganda saat satu permintaan masih in-flight.
// ============================================================================
// Konfirmasi cetak massal Offline (pola sama dgn Shopee): status berubah hanya setelah "Ya".
//   Memakai endpoint mockup-print yang sudah ada untuk setiap produk yang belum ditandai.
function OfflineBulkConfirm({ data, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const { printed, missing } = data;
  const confirm = async () => {
    setBusy(true);
    const failed = [];
    for (const c of printed) {
      const pending = (c.productCards || [c]).filter(pc => !pc.product?.mockup_print_at);
      try {
        for (const pc of pending) await axios.post(`${API}/orders/${encodeURIComponent(c.order.order_number)}/products/${encodeURIComponent(pc.product.id)}/mockup-print`, {}, { withCredentials: true });
      } catch (e) { failed.push({ n: c.order.order_number, msg: e?.response?.data?.detail || e.message }); }
    }
    setBusy(false);
    if (failed.length) toast.error(`${printed.length - failed.length} berhasil, ${failed.length} gagal ditandai (tetap Belum Dicetak): ${failed.map(f => `${f.n} — ${f.msg}`).join("; ")}`);
    else toast.success(`${printed.length} nota ditandai Sudah Dicetak.`);
    await onDone([...failed.map(f => f.n), ...missing.map(m => m.orderNumber)]);
  };
  return <div className="modal-backdrop" style={{ zIndex: 1000 }}>
    <div className="modal" style={{ maxWidth: 480 }} data-testid="rido-bulk-confirm">
      <h3 style={{ marginTop: 0 }}>Apakah {printed.length} nota berhasil dicetak?</h3>
      <div style={{ fontWeight: 600, marginBottom: 6 }} data-testid="rido-bulk-summary">{printed.length} tercetak · {missing.length} gagal memuat gambar</div>
      <p className="muted small">Periksa hasil printer. Status baru berubah setelah Anda menekan "Ya".</p>
      <ol className="small" style={{ maxHeight: 180, overflow: "auto", margin: "0 0 0 18px", padding: 0 }} data-testid="rido-bulk-confirm-list">
        {printed.map(c => <li key={c.order.order_number}>{c.order.order_number}{c.order.customer_name ? ` · ${c.order.customer_name}` : ""}</li>)}</ol>
      {missing.length > 0 && <div className="warn-box small" data-testid="rido-bulk-skipped">Gagal memuat gambar (tetap Belum Dicetak, tetap terpilih untuk dicetak lagi): {missing.map(m => m.orderNumber).join(", ")}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <button type="button" className="primary" disabled={busy || printed.length === 0} onClick={confirm} data-testid="rido-bulk-confirm-yes">
          {busy ? "Menandai…" : `Ya, semua tercetak (${printed.length})`}</button>
        <button type="button" className="soft-btn" disabled={busy} onClick={onClose} data-testid="rido-bulk-confirm-no">Belum / Gagal — tetap Belum Dicetak</button>
      </div>
    </div>
  </div>;
}

function PenyiapanCetakActions({ card, idx, busy, onMarkMockupPrint, compact, onPreview, onToggleExpand, expanded, onOpenDetail }) {
  const afterPrint = useRef(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [localBusy, setLocalBusy] = useState(false);
  const [err, setErr] = useState("");
  const orderNumber = card.order?.order_number || "";
  const pcs = Array.isArray(card.productCards) && card.productCards.length > 0
    ? card.productCards
    : [card];
  const pendingCards = pcs.filter(pc => !pc.product?.mockup_print_at);
  const printedCount = pcs.length - pendingCards.length;
  const totalCount = pcs.length;
  const allDone = pendingCards.length === 0 && totalCount > 0;
  // Guard pencegahan double submit: tangkap in-flight di dalam komponen juga.
  const isBusy = busy || localBusy;
  const doConfirmMark = async () => {
    if (!onMarkMockupPrint || pendingCards.length === 0) return;
    if (localBusy) return;
    setLocalBusy(true);
    setErr("");
    try {
      // Panggil mockup-print untuk SEMUA produk yang belum ditandai.
      //   onMarkMockupPrint sudah async-safe di parent (set busyKey per item);
      //   kita bungkus Promise.all agar menunggu semua selesai sebelum tutup modal.
      await Promise.all(pendingCards.map(pc => Promise.resolve(onMarkMockupPrint({
        order: pc.order,
        product: pc.product,
        key: `mp::${pc.order.order_number}::${pc.product.id}`,
      }))));
      setConfirmOpen(false);
    } catch (e) {
      setErr(e?.message || "Gagal menandai mockup dicetak.");
      // Tetap buka modal — parent akan menampilkan error via `notify`.
    } finally {
      setLocalBusy(false);
    }
  };
  const doDownload = async () => {
    if (dlBusy) return;
    setDlBusy(true);
    try {
      await unduhMockup(orderNumber);
    } catch (e) {
      toast.error(e?.message || "Gagal mengunduh mockup.");
    } finally {
      setDlBusy(false);
    }
  };
  const modals = <>
    {sizeOpen && <CetakMockupSizeModal orderNumber={orderNumber}
      onPrint={async (paper) => { await cetakMockup(orderNumber, paper); if (pendingCards.length) afterPrint.current = true; }}
      onClose={() => { setSizeOpen(false); if (afterPrint.current) { afterPrint.current = false; setErr(""); setConfirmOpen(true); } }}/>}
    {confirmOpen && <KonfirmasiCetakModal orderNumber={orderNumber} pendingCount={pendingCards.length} totalCount={totalCount}
      onCancel={() => { if (!localBusy) setConfirmOpen(false); }} onConfirm={doConfirmMark}/>}
  </>;
  if (compact) {
    const items = [
      !allDone && { key: "mark", label: isBusy ? "Memproses…" : "Tandai Sudah Dicetak", disabled: isBusy, onSelect: () => { setErr(""); setConfirmOpen(true); } },
      { key: "dl", label: dlBusy ? "Mengunduh…" : "Unduh Mockup", disabled: isBusy || dlBusy, onSelect: doDownload },
      { key: "expand", label: expanded ? "Tutup Rincian Ukuran" : "Rincian Ukuran & File", onSelect: onToggleExpand },
      { key: "detail", label: "Buka Nota", onSelect: onOpenDetail },
    ].filter(Boolean);
    return <div className="rido-actions">
      <button type="button" className="primary shopee-wh-btn rido-btn-print" data-testid={`wh-prep-print-${idx}`} onClick={() => setSizeOpen(true)}
        disabled={isBusy} title={allDone ? "Cetak ulang (status tidak berubah)" : "Pilih A5 / A4 lalu cetak — konfirmasi muncul setelahnya"}>
        <Printer size={15}/> {allDone ? "Cetak Ulang" : "Cetak"}</button>
      <button type="button" className="soft-btn shopee-wh-btn" onClick={onPreview} data-testid={`wh-prep-preview-${idx}`}><ImageIcon size={15}/> Pratinjau</button>
      <RidoMoreMenu items={items} testId={`wh-prep-more-${idx}`}/>
      {printedCount > 0 && pendingCards.length > 0 && <span className="rido-note" data-testid={`wh-prep-print-progress-${idx}`}>{printedCount}/{totalCount} sudah dicetak</span>}
      {err && <span className="rido-note" style={{color:"#991b1b"}} data-testid={`wh-prep-mark-err-${idx}`}>{err}</span>}
      {modals}
    </div>;
  }
  return (
    <>
      <button
        type="button"
        className="soft-btn small-btn"
        data-testid={`wh-prep-print-${idx}`}
        onClick={() => setSizeOpen(true)}
        disabled={isBusy}
        title="Pilih ukuran penyajian (A5/A4) lalu buka dialog cetak."
      >
        <Printer size={13}/> Cetak Mockup
      </button>
      <button
        type="button"
        className="soft-btn small-btn"
        data-testid={`wh-prep-download-${idx}`}
        onClick={doDownload}
        disabled={isBusy || dlBusy}
        title="Unduh file mockup ke perangkat ini."
      >
        <Printer size={13} style={{opacity:0}} aria-hidden="true"/>
        {dlBusy ? "Mengunduh…" : "Unduh Mockup"}
      </button>
      {allDone ? (
        <span className="chip chip-safe wh-prep-done-chip" data-testid={`wh-prep-print-done-${idx}`} title="Semua mockup di kartu ini sudah ditandai dicetak.">
          <CheckCircle2 size={12}/> Sudah dicetak
        </span>
      ) : (
        <button
          type="button"
          className="primary small-btn"
          data-testid={`wh-prep-mark-print-${idx}`}
          onClick={() => { setErr(""); setConfirmOpen(true); }}
          disabled={isBusy}
          title="Buka konfirmasi penandaan — mockup fisik sudah selesai dicetak."
        >
          🖨 {isBusy ? "Memproses…" : "Tandai Mockup Dicetak"}
        </button>
      )}
      {printedCount > 0 && pendingCards.length > 0 && (
        <span className="rido-print-progress" data-testid={`wh-prep-print-progress-${idx}`}>
          {printedCount}/{totalCount} sudah dicetak
        </span>
      )}
      {sizeOpen && (
        <CetakMockupSizeModal
          orderNumber={orderNumber}
          onClose={() => setSizeOpen(false)}
        />
      )}
      {confirmOpen && (
        <KonfirmasiCetakModal
          orderNumber={orderNumber}
          pendingCount={pendingCards.length}
          totalCount={totalCount}
          onCancel={() => { if (!localBusy) setConfirmOpen(false); }}
          onConfirm={doConfirmMark}
        />
      )}
      {err && (
        <span className="muted small" style={{color:"#991b1b"}} data-testid={`wh-prep-mark-err-${idx}`}>{err}</span>
      )}
    </>
  );
}




// ============================================================================
// Iter 521 — ShellaShopeePane: pane terpisah untuk antrean Finalisasi Shopee.
//   Scope aman (Opsi A): HANYA process="bordir" yang masuk antrean. Backend
//   `/api/shopee/shella/queue` sudah apply gate; FE hanya render + konfirmasi.
//   Side-effect tombol "Selesaikan Pesanan": POST ke
//   `/api/shopee/orders/{on}/shella-finalize` → stamp shella_finalized_* saja
//   (tidak mengubah status marketplace / pengiriman / routing baru).
// ============================================================================
function ShellaShopeePane({ canFinalize, operator, isAdmin }) {
  // Iter 554 — Pagination + debounce search + fast counts.
  //   • Fetch terpisah per bucket (ready / history) dari endpoint baru
  //     `/api/shopee/shella/queue?view=&page=&page_size=20&q=`.
  //   • Counts (Siap Difinalisasi, Selesai Hari Ini, Riwayat) datang akurat
  //     dari backend berdasarkan SELURUH database — tidak lagi dihitung dari
  //     slice yang di-fetch.
  //   • Pencarian men-trigger request baru (debounce 350ms) sehingga mencakup
  //     seluruh database, bukan hanya halaman yang sedang ditampilkan.
  //   • Sibling gate finalisasi tetap divalidasi di backend untuk SETIAP card
  //     yang muncul (baik di halaman pertama maupun page berikutnya). Endpoint
  //     finalize juga re-validate — pagination tidak melemahkan gate.
  const [bucket, setBucket] = useState("ready"); // ready | history
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [counts, setCounts] = useState(null);  // null = belum dimuat → sembunyikan 0 palsu
  const [scope, setScope] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [showUntrackedDetail, setShowUntrackedDetail] = useState(false);
  const reqIdRef = useRef(0);

  // Debounce input pencarian: hindari request pada tiap keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQDeb(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Reset ke halaman 1 saat bucket / query berubah.
  useEffect(() => { setPage(1); }, [bucket, qDeb]);

  const fetchPage = useCallback(async (pageNum, append) => {
    const myId = ++reqIdRef.current;
    if (append) setLoadingMore(true); else setLoading(true);
    setErr("");
    try {
      const r = await axios.get(`${API}/shopee/shella/queue`, {
        params: { view: bucket, page: pageNum, page_size: 20, q: qDeb || "" },
        withCredentials: true,
        timeout: 20_000,
      });
      // Hindari race: hanya terapkan response yg paling terakhir di-request.
      if (reqIdRef.current !== myId) return;
      const d = r.data || {};
      const newItems = Array.isArray(d.items) ? d.items : [];
      setItems(prev => append ? [...prev, ...newItems] : newItems);
      setTotal(Number(d.total || 0));
      setHasMore(Boolean(d.has_more));
      setCounts(d.counts || {});
      setScope(d.scope || {});
    } catch (e) {
      if (reqIdRef.current !== myId) return;
      setErr(e.response?.data?.detail || e.message || "Gagal memuat antrean Shopee");
      if (!append) { setItems([]); setTotal(0); setHasMore(false); }
    } finally {
      if (reqIdRef.current === myId) {
        if (append) setLoadingMore(false); else setLoading(false);
      }
    }
  }, [bucket, qDeb]);

  // Trigger fetch tiap bucket / q / page berubah.
  useEffect(() => {
    fetchPage(page, page > 1);
  }, [fetchPage, page]);

  const reload = useCallback(() => {
    setPage(1);
    fetchPage(1, false);
  }, [fetchPage]);
  // Iter 647 — Sinkron dengan Scan Cepat Shella tanpa refresh manual.
  useEffect(() => {
    window.addEventListener("shella-fast-done", reload);
    window.addEventListener("shella-queue-changed", reload);
    return () => { window.removeEventListener("shella-fast-done", reload); window.removeEventListener("shella-queue-changed", reload); };
  }, [reload]);

  const doFinalize = async (card) => {
    const ok = await notify.confirm({
      title: "Selesaikan Pesanan Shopee",
      message: `Pesanan ${card.order_number} (${card.customer_account || "—"}) akan ditandai SELESAI oleh Shella. Catatan: ini hanya finalisasi internal TAMFARIS — TIDAK mengubah status Shopee Seller Centre atau mengirim barang ke pembeli.`,
      primaryLabel: "Ya, Selesaikan",
    });
    if (!ok) return;
    setBusy(card.order_number);
    try {
      const r = await axios.post(
        `${API}/shopee/orders/${encodeURIComponent(card.order_number)}/shella-finalize`,
        {}, { withCredentials: true }
      );
      if (r.data?.already_finalized) {
        notify.info({ title: "Sudah difinalisasi", message: `Pesanan ${card.order_number} sebelumnya sudah difinalisasi.` });
      } else {
        notify.success({ title: "Berhasil", message: `Pesanan Shopee ${card.order_number} masuk Riwayat Selesai.` });
      }
      reload();
    } catch (e) {
      notify.error({
        title: "Gagal finalisasi",
        message: e.response?.data?.detail || "Silakan coba lagi.",
      });
    } finally { setBusy(""); }
  };

  const list = items;
  const untracked = (counts && counts.untracked_notice) || {};
  const untrackedTotal = (untracked.bordir_sablon_dtf || 0)
                       + (untracked.sablon_dtf || 0)
                       + (untracked.sablon_dtf_waiting || 0)
                       + (untracked.bordir_sablon_dtf_waiting || 0)
                       + (untracked.sublim_full_print || 0);

  // Tampilan counter: tampilkan "…" saat `counts` belum tersedia (hindari 0 palsu).
  const countsReady = counts !== null;
  const readyCount    = countsReady ? (counts.ready || 0) : null;
  const doneTodayCount = countsReady ? (counts.done_today || 0) : null;

  return (
    <div className="selesai-shopee-pane" data-testid="shella-shopee-pane">
      <div className="selesai-summary" data-testid="shella-shopee-summary">
        <SelesaiTile label="Siap Difinalisasi" value={readyCount === null ? "…" : readyCount} tone="final"
          testId="shella-shopee-sum-ready"
          active={bucket === "ready"} onClick={() => setBucket("ready")}/>
        <SelesaiTile label="Selesai Hari Ini" value={doneTodayCount === null ? "…" : doneTodayCount} tone="sudah"
          testId="shella-shopee-sum-done-today"
          active={bucket === "history"} onClick={() => setBucket("history")}/>
      </div>

      {untrackedTotal > 0 && (
        <div className="shella-untracked" data-testid="shella-shopee-untracked-notice">
          <div className="shella-untracked-head">
            <strong data-testid="shella-shopee-untracked-count">
              {untrackedTotal} pesanan belum dapat difinalisasi
            </strong>
            <button type="button" className="link-btn" onClick={() => setShowUntrackedDetail(v => !v)}
              data-testid="shella-shopee-untracked-toggle">
              {showUntrackedDetail ? "Sembunyikan" : "Lihat Rincian"}
            </button>
          </div>
          <p className="shella-untracked-msg muted small">
            {untracked.sablon_dtf > 0 || untracked.bordir_sablon_dtf > 0
              ? "Status penyelesaian Sablon/DTF belum tersedia."
              : (untracked.sublim_full_print > 0
                  ? "Status penyelesaian Sublim belum tersedia."
                  : "Proses produksi tersebut belum didukung finalisasi.")}
          </p>
          {showUntrackedDetail && (
            <ul className="shella-untracked-detail" data-testid="shella-shopee-untracked-detail">
              {untracked.bordir_sablon_dtf > 0 && (
                <li>Bordir + Sablon/DTF: <b>{untracked.bordir_sablon_dtf}</b> pesanan</li>
              )}
              {untracked.sablon_dtf > 0 && (
                <li>Sablon/DTF murni: <b>{untracked.sablon_dtf}</b> pesanan</li>
              )}
              {untracked.sublim_full_print > 0 && (
                <li>Sublim: <b>{untracked.sublim_full_print}</b> pesanan</li>
              )}
            </ul>
          )}
        </div>
      )}

      <div className="selesai-filter-bar selesai-filter-bar-compact" data-testid="shella-shopee-filter-bar">
        <input type="text" className="selesai-search" data-testid="shella-shopee-search"
          placeholder="Cari nota / akun / desain…"
          value={q} onChange={(e) => setQ(e.target.value)}/>
        <div className="selesai-chips selesai-chips-bucket" role="group" aria-label="Toggle bucket">
          {[["ready","Siap Difinalisasi"],["history","Riwayat Selesai"]].map(([k,l]) => (
            <button key={k} type="button" data-testid={`shella-shopee-bucket-${k}`}
              className={`selesai-chip-btn ${bucket === k ? "active" : ""}`}
              onClick={() => setBucket(k)}>{l}</button>
          ))}
        </div>
        <button type="button" className="soft-btn small-btn" data-testid="shella-shopee-refresh"
          onClick={reload} disabled={loading}>
          {loading ? "Memuat…" : "Muat Ulang"}
        </button>
      </div>

      {err && (
        <div className="monitor-v2-error" data-testid="shella-shopee-error" style={{display:"flex", gap:8, alignItems:"center", justifyContent:"space-between"}}>
          <span>{err}</span>
          <button type="button" className="soft-btn small-btn" onClick={reload}
            data-testid="shella-shopee-retry">
            Coba Lagi
          </button>
        </div>
      )}

      {loading ? (
        <div className="op-empty" data-testid="shella-shopee-loading">
          <h4 className="op-empty-title">Memuat antrean Shopee…</h4>
          <p className="op-empty-msg muted small">Memuat 20 pesanan terbaru…</p>
        </div>
      ) : !err && list.length === 0 ? (
        <div className="op-empty" data-testid="shella-shopee-empty">
          <div className="op-empty-icon" aria-hidden="true"><CheckCircle2 size={32}/></div>
          <h4 className="op-empty-title" data-testid="shella-shopee-empty-title">
            {qDeb
              ? `Tidak ada hasil untuk "${qDeb}"`
              : (bucket === "ready" ? "Tidak ada pesanan Shopee siap difinalisasi"
                                    : "Belum ada riwayat finalisasi Shopee")}
          </h4>
          <p className="op-empty-msg">
            {bucket === "ready"
              ? "Pesanan akan muncul setelah Operator Bordir menyelesaikan pekerjaan dan Gudang (Adit) ACC Barang Siap."
              : "Pesanan Shopee yang Anda finalisasi akan tercatat di sini."}
          </p>
        </div>
      ) : !err ? (
        <>
          <ul className="selesai-list" data-testid="shella-shopee-list">
            {list.map((c, i) => (
              <ShellaShopeeCard key={`${c.order_number}-${i}`} card={c} idx={i} bucket={bucket}
                canFinalize={canFinalize && bucket === "ready"}
                busy={busy === c.order_number}
                onPreview={(mockups) => setPreview({ mockups, idx: 0, title: c.design_title || c.order_number })}
                onFinalize={() => doFinalize(c)}/>
            ))}
          </ul>
          <div className="selesai-pagebar" data-testid="shella-shopee-pagebar"
            style={{display:"flex", justifyContent:"center", alignItems:"center", gap:12, padding:"12px 0"}}>
            <span className="muted small" data-testid="shella-shopee-page-info">
              Menampilkan {list.length} dari {total}
              {qDeb ? ` (filter: "${qDeb}")` : ""}
            </span>
            {hasMore && (
              <button type="button" className="soft-btn small-btn"
                data-testid="shella-shopee-load-more"
                disabled={loadingMore}
                onClick={() => setPage(p => p + 1)}>
                {loadingMore ? "Memuat…" : "Muat Berikutnya"}
              </button>
            )}
          </div>
        </>
      ) : null}

      {preview && preview.mockups?.length > 0 && (
        <div className="modal-overlay" onClick={() => setPreview(null)}
          data-testid="shella-shopee-preview-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{maxWidth:900, maxHeight:"90vh"}}>
            <div className="modal-header">
              <h3>{preview.title}
                {preview.mockups.length > 1 && (
                  <span className="muted small" style={{marginLeft:8}}>
                    ({preview.idx + 1} / {preview.mockups.length})
                  </span>
                )}
              </h3>
              <button type="button" onClick={() => setPreview(null)}
                data-testid="shella-shopee-preview-close">×</button>
            </div>
            <div className="modal-body" style={{textAlign:"center", position:"relative"}}>
              <img src={`${API}/shopee/files/${encodeURIComponent(preview.mockups[preview.idx].file_id)}/preview`}
                alt={preview.title}
                data-testid="shella-shopee-preview-img"
                style={{maxWidth:"100%", maxHeight:"70vh"}}/>
              {preview.mockups.length > 1 && (
                <div style={{display:"flex", justifyContent:"center", gap:8, marginTop:12}}>
                  <button type="button" className="soft-btn small-btn"
                    data-testid="shella-shopee-preview-prev"
                    onClick={() => setPreview(p => ({...p, idx: (p.idx - 1 + p.mockups.length) % p.mockups.length}))}>
                    ← Sebelumnya
                  </button>
                  <button type="button" className="soft-btn small-btn"
                    data-testid="shella-shopee-preview-next"
                    onClick={() => setPreview(p => ({...p, idx: (p.idx + 1) % p.mockups.length}))}>
                    Berikutnya →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShellaShopeeCard({ card, idx, bucket, canFinalize, busy, onPreview, onFinalize }) {
  // Iter 521c — Semua mockup valid dari backend. Primary = terbaru; sisanya
  //   dijadikan fallback utk `ShopeeMockupThumb` (yg sudah punya timeout 15s,
  //   placeholder "Gagal muat", dan tombol Coba Lagi dari Iter 519).
  const mockups = Array.isArray(card.mockups) ? card.mockups : [];
  const thumbUrl = card.thumb_file_id
    ? `${API}/shopee/files/${encodeURIComponent(card.thumb_file_id)}/thumbnail?size=400`
    : null;
  const fallbackUrls = mockups.slice(1)
    .filter(m => m.file_id && m.file_id !== card.thumb_file_id)
    .map(m => `${API}/shopee/files/${encodeURIComponent(m.file_id)}/thumbnail?size=400`);
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("id-ID", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" });
    } catch { return iso.slice(0, 16).replace("T", " "); }
  };
  return (
    <li className="selesai-card" data-testid={`shella-shopee-card-${card.order_number}`}>
      <ShopeeCancelTag job={card} id={card.order_number}/>
      <div className="selesai-card-identity">
        <div className="selesai-card-thumb-wrap" data-testid={`shella-shopee-thumb-wrap-${card.order_number}`}>
          <ShopeeMockupThumb
            thumbUrl={thumbUrl}
            fallbackUrls={fallbackUrls}
            testId={`shella-shopee-thumb-${card.order_number}`}
            onClickPreview={mockups.length ? () => onPreview(mockups) : null}
          />
          {mockups.length > 1 && (
            <span className="selesai-card-thumb-badge"
              data-testid={`shella-shopee-thumb-count-${card.order_number}`}
              title={`${mockups.length} mockup`}>
              {mockups.length}
            </span>
          )}
        </div>
        <div className="selesai-card-body">
          <div className="selesai-card-head">
            <strong className="selesai-card-name" data-testid={`shella-shopee-order-${card.order_number}`}>
              {card.order_number}
            </strong>
            <span className="selesai-card-meta muted small">
              Shopee · {card.process_label || "Bordir"}
            </span>
          </div>
          <p className="selesai-card-sub muted small" style={{margin:"4px 0 0"}}>
            Akun: <b>{card.customer_account || "—"}</b>
            {card.design_title && <> · <span>{card.design_title}</span></>}
          </p>
          <p className="selesai-card-sub muted small" style={{margin:"4px 0 0"}}>
            Desainer: {card.pic_designer_name || "—"}
            {card.bordir_executed_by_name && <> · Bordir: {card.bordir_executed_by_name}</>}
            {card.sablon_done_by_name && <> · DTF: {card.sablon_done_by_name}</>}
            {card.press_done_by_name && <> · Press: {card.press_done_by_name}</>}
          </p>
          <p className="selesai-card-sub muted small" style={{margin:"4px 0 0"}}>
            {bucket === "ready"
              ? (() => {
                  // Iter 548 — Tampilkan progres per-process (bordir / sablon_dtf / bordir_sablon_dtf).
                  const parts = [];
                  parts.push(<>Gudang ACC: {fmtDate(card.warehouse_completed_at)}</>);
                  if (card.process === "bordir" || card.process === "bordir_sablon_dtf") {
                    parts.push(<> · Bordir selesai: {fmtDate(card.bordir_executed_at)}</>);
                  }
                  if (card.process === "sablon_dtf" || card.process === "bordir_sablon_dtf") {
                    parts.push(<> · DTF selesai: {fmtDate(card.sablon_done_at)}</>);
                    parts.push(<> · Press selesai: {fmtDate(card.press_done_at)}</>);
                  }
                  return parts;
                })()
              : <>Difinalisasi: {fmtDate(card.shella_finalized_at)} oleh <b>{card.shella_finalized_by_name || "—"}</b></>}
          </p>
        </div>
      </div>
      {bucket === "ready" && canFinalize && (
        <div className="selesai-card-actions">
          {/* Iter 548 — Defensive: backend sudah filter not-ready, tapi jika
              not_ready_reason terisi (race), tampilkan tombol disabled dgn alasan. */}
          {card.not_ready_reason ? (
            <div data-testid={`shella-shopee-waiting-${card.order_number}`}>
              <button type="button" className="primary-btn" disabled
                data-testid={`shella-shopee-finalize-${card.order_number}`}>
                Selesaikan Pesanan
              </button>
              <span className="muted small" style={{marginLeft:8, color:"#92400e"}}>
                ⏳ {card.not_ready_reason}
              </span>
            </div>
          ) : (
            <button type="button" className="primary-btn" onClick={onFinalize} disabled={busy}
              data-testid={`shella-shopee-finalize-${card.order_number}`}>
              {busy ? "Memproses…" : "Selesaikan Pesanan"}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

// ============================================================================
// Iter 248 — Finalisasi Dashboard (Shella & Owner/Admin).
//   Tab "Selesai" = pusat FINALISASI (bukan pickup). Nota yg semua produknya
//   sudah tuntas produksi masuk ke bucket "Menunggu Finalisasi". Shella cek
//   fisik & klik "Selesaikan Pesanan" → order_status jadi "Selesai" → masuk
//   workflow pickup di Admin Toko.
//   Toggle bucket: "Menunggu Finalisasi" (aksi) vs "Selesai" (history).
// ============================================================================
function SelesaiDashboard({ orders, user, operator, isAdmin, onOpen, onChanged }) {
  const [q, setQ] = useState("");
  const [datePeriod, setDatePeriod] = useState("all"); // all | today | week | month
  const [bucket, setBucket] = useState("menunggu"); // menunggu | selesai
  const [busy, setBusy] = useState("");
  const [previewOrder, setPreviewOrder] = useState(null); // {order_number, customer_name}
  // Iter 521 — Tab sumber Offline | Shopee. Default: Offline (fitur existing
  //   tanpa perubahan). State persist di localStorage agar Shella tidak harus
  //   pindah tab setiap buka. Shopee pane memakai komponen terpisah
  //   `ShellaShopeePane` yang fetch /api/shopee/shella/queue (endpoint baru,
  //   terisolasi dari koleksi `orders`).
  const [source, setSource] = useState(() => {
    try { return initSource("shella", localStorage.getItem("shella_source") === "shopee" ? "shopee" : "offline"); }
    catch { return "offline"; }
  });
  useEffect(() => {
    try { localStorage.setItem("shella_source", source); } catch {}
  }, [source]);
  const canFinalize = isAdmin || operator?.team === "Selesai";
  // Iter 248 UI revision — Shella (team Selesai operator) TIDAK melihat info keuangan/pickup.
  //   Owner/Admin tetap melihat info lengkap.
  const isShellaView = !isAdmin && operator?.team === "Selesai";
  const todayISO = new Date().toISOString().slice(0,10);

  // Waktu produk terakhir tuntas (best-effort dari history stage_to === "Selesai")
  const productionDoneAt = (o) => {
    let latest = "";
    for (const p of (o?.products || [])) {
      for (const h of (p.production_pic_history || [])) {
        if (h?.stage_to === "Selesai" && h.at && h.at > latest) latest = h.at;
      }
    }
    return latest || o?.updated_at || "";
  };
  // Waktu difinalisasi (order_status=Selesai)
  const finalizedAt = (o) => o?.finalized_at || o?.updated_at || productionDoneAt(o);

  const isInDatePeriod = (iso, period) => {
    if (period === "all") return true;
    if (!iso) return false;
    if (period === "today") return iso.slice(0,10) === todayISO;
    if (period === "month") return iso.slice(0,7) === todayISO.slice(0,7);
    if (period === "week") {
      const t = new Date(iso).getTime();
      if (Number.isNaN(t)) return false;
      const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - 6);
      return t >= d.getTime();
    }
    return true;
  };

  const menungguOrders = (orders || []).filter(o => o?.order_status === "Menunggu Finalisasi");
  const selesaiOrders = (orders || []).filter(o => o?.order_status === "Selesai");
  const siapCount = menungguOrders.length;
  const selesaiTodayCount = selesaiOrders.filter(o => finalizedAt(o).slice(0,10) === todayISO).length;

  const sourceOrders = bucket === "menunggu" ? menungguOrders : selesaiOrders;
  const dateOf = bucket === "menunggu" ? productionDoneAt : finalizedAt;

  const filtered = sourceOrders.filter(o => {
    if (!isInDatePeriod(dateOf(o), datePeriod)) return false;
    if (!q.trim()) return true;
    const ql = q.toLowerCase();
    return (o.order_number || "").toLowerCase().includes(ql)
        || (o.customer_name || "").toLowerCase().includes(ql);
  }).sort((a,b) => (dateOf(b) || "").localeCompare(dateOf(a) || ""));

  const doFinalize = async (o) => {
    const ok = await notify.confirm({
      title: "Selesaikan Pesanan",
      message: `Nota ${o.order_number} atas nama ${o.customer_name || "—"} akan ditandai SELESAI. Setelah ini nota masuk workflow pickup di Admin Toko. Pastikan seluruh produk sudah lengkap secara fisik.`,
      primaryLabel: "Ya, Selesaikan Pesanan",
    });
    if (!ok) return;
    setBusy(o.order_number);
    try {
      await axios.post(`${API}/orders/${o.order_number}/finalize`, {}, { withCredentials: true });
      Promise.resolve(onChanged()).catch(() => {});
      notify.success({ title: "Berhasil", message: `Nota ${o.order_number} sudah difinalisasi.` });
    } catch (e) {
      notify.error({ title: "Gagal finalisasi", message: e.response?.data?.detail || "Silakan coba lagi." });
    } finally { setBusy(""); }
  };

  const clearFilter = () => { setQ(""); setDatePeriod("all"); };
  const hasFilter = q.trim() || datePeriod !== "all";

  return (
    <div className="selesai-dash" data-testid="selesai-dash">
      <header className="selesai-head">
        <div>
          <p className="eyebrow">Selesai · Finalisasi</p>
          <h2 className="selesai-title" data-testid="selesai-title">Finalisasi Pesanan</h2>
          <p className="muted small">Nota yang semua produknya sudah tuntas produksi. Cek fisik &amp; klik Selesaikan Pesanan untuk memindahkan ke workflow pickup di Admin Toko.</p>
        </div>
      </header>

      {canFinalize && <LiveQueueBar team="Selesai" onOfflineChanged={onChanged} tid="shella-live" events={{ shopee: "shella-queue-changed" }}/>}
      {canFinalize && <ShellaFastScan onOfflineChanged={onChanged}/>}
      {/* Iter 521 — Tab sumber Offline / Shopee (reuse CSS .rido-primary-strip).
          Offline = fitur existing tanpa perubahan. Shopee = antrean baru
          khusus pekerjaan bordir yang gate penyelesaiannya aman
          (status=FIX + warehouse_state=completed + bordir_state=done). */}
      <div className="shella-source-strip mpb-tabs" role="tablist"
        aria-label="Sumber Finalisasi" data-testid="shella-source-strip">
        <SourceTab channel="offline" active={source==="offline"} onClick={() => { pickSource("shella", "offline", setSource); }} testid="shella-source-offline"/>
        <SourceTab channel="shopee" active={tabOf(source)==="shopee"} onClick={() => pickSource("shella", "shopee", setSource)} testid="shella-source-shopee"/>
        <SourceTab channel="tokopedia" active={tabOf(source)==="tokopedia"} onClick={() => pickSource("shella", "tokopedia", setSource)} testid="shella-source-tokopedia"/>
      </div>
      {source === "shopee" && <TokopediaBanner/>}

      {source === "shopee" ? (
        <ShellaShopeePane canFinalize={canFinalize} operator={operator} isAdmin={isAdmin}/>
      ) : (<>
      <div className="selesai-summary" data-testid="selesai-summary">
        <SelesaiTile label="Siap Difinalisasi" value={siapCount} tone="final" testId="selesai-sum-siap"
          active={bucket === "menunggu"} onClick={() => setBucket("menunggu")}/>
        <SelesaiTile label="Selesai Hari Ini" value={selesaiTodayCount} tone="sudah" testId="selesai-sum-today"
          active={bucket === "selesai"} onClick={() => setBucket("selesai")}/>
      </div>

      <div className="selesai-filter-bar selesai-filter-bar-compact" data-testid="selesai-filter-bar">
        <input type="text" className="selesai-search" data-testid="selesai-search"
          placeholder="Cari nota / pelanggan…"
          value={q} onChange={(e) => setQ(e.target.value)}/>
        <div className="selesai-chips selesai-chips-bucket" role="group" aria-label="Toggle bucket">
          {[["menunggu","Menunggu Finalisasi"],["selesai","Riwayat Selesai"]].map(([k,l]) => (
            <button key={k} type="button" data-testid={`selesai-bucket-${k}`}
              className={`selesai-chip-btn ${bucket === k ? "active" : ""}`}
              onClick={() => setBucket(k)}>{l}</button>
          ))}
        </div>
        <div className="selesai-chips selesai-chips-date" role="group" aria-label="Filter tanggal">
          {[["all","Semua"],["today","Hari Ini"],["week","7 Hari"],["month","Bulan Ini"]].map(([k,l]) => (
            <button key={k} type="button" data-testid={`selesai-date-${k}`}
              className={`selesai-chip-btn selesai-chip-btn-sm ${datePeriod === k ? "active" : ""}`}
              onClick={() => setDatePeriod(k)}>{l}</button>
          ))}
        </div>
        {hasFilter && (
          <button type="button" className="soft-btn small-btn" data-testid="selesai-filter-clear" onClick={clearFilter}>
            Reset
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="op-empty" data-testid="selesai-empty">
          <div className="op-empty-icon" aria-hidden="true"><CheckCircle2 size={32}/></div>
          <h4 className="op-empty-title" data-testid="selesai-empty-title">
            {sourceOrders.length === 0
              ? (bucket === "menunggu" ? "Tidak ada nota siap difinalisasi" : "Belum ada nota selesai")
              : "Tidak ada nota yang cocok filter"}
          </h4>
          <p className="op-empty-msg">
            {sourceOrders.length === 0
              ? (bucket === "menunggu"
                  ? "Nota akan muncul di sini otomatis setelah semua produk tuntas produksi."
                  : "Nota selesai akan tercatat di sini setelah difinalisasi.")
              : "Coba ubah filter tanggal atau kosongkan pencarian."}
          </p>
        </div>
      ) : (
        <ul className="selesai-list" data-testid="selesai-list">
          {filtered.map((o, i) => (
            <FinalisasiCard
              key={o.order_number} order={o} idx={i}
              bucket={bucket} isShellaView={isShellaView}
              productionDoneAt={productionDoneAt(o)}
              finalizedAt={finalizedAt(o)}
              canFinalize={canFinalize} busy={busy === o.order_number}
              onDetail={() => onOpen(o)}
              onPreview={() => setPreviewOrder({ order_number: o.order_number, customer_name: o.customer_name })}
              onFinalize={() => doFinalize(o)}
            />
          ))}
        </ul>
      )}
      {previewOrder && <MockupPreviewModal
        orderNumber={previewOrder.order_number}
        customerName={previewOrder.customer_name}
        onClose={() => setPreviewOrder(null)}
      />}
      </>)}
    </div>
  );
}

function SelesaiTile({ label, value, tone, testId, active, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      className={`selesai-tile selesai-tile-${tone} ${active ? "active" : ""} ${onClick ? "selesai-tile-clickable" : ""}`}
      data-testid={testId}
      onClick={onClick || undefined}
    >
      <span className="selesai-tile-label">{label}</span>
      <strong className="selesai-tile-value" data-testid={`${testId}-value`}>{value}</strong>
    </Tag>
  );
}

function FinalisasiCard({ order, idx, bucket, isShellaView, productionDoneAt, finalizedAt, canFinalize, busy, onDetail, onPreview, onFinalize }) {
  const products = order.products || [];
  const totalPcs = products.reduce((s, p) => {
    const sizes = Array.isArray(p.sizes) ? p.sizes : [];
    const n = Number(p.quantity || 0) || sizes.reduce((ss, x) => ss + Number(x.quantity || 0), 0);
    return s + n;
  }, 0);
  const dateLabel = (iso) => iso
    ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";
  const isMenunggu = bucket === "menunggu";
  const statusPill = isMenunggu
    ? { cls: "chip-final", label: "MENUNGGU FINALISASI" }
    : { cls: "chip-safe", label: "SELESAI" };
  // Iter 248 UI — payment/pickup info HANYA utk Owner/Admin (isShellaView=false).
  const showFinanceInfo = !isShellaView;
  const paymentPill = order.payment_status === "Lunas"
    ? { cls: "chip-safe", label: "Lunas" }
    : { cls: "chip-amber", label: order.payment_status || "Belum Bayar" };

  // Iter 377 — Helper: skrining nilai atribut produk yg tidak membantu Shella.
  //   Nilai kosong / dash / "Tidak Berlaku" / "N/A" disembunyikan dari kartu
  //   agar fokus Shella jelas: nama produk, jumlah, preview, selesaikan.
  const meaningful = (v) => {
    if (v === null || v === undefined) return false;
    const s = String(v).trim();
    if (!s) return false;
    const lo = s.toLowerCase();
    if (lo === "-" || lo === "–" || lo === "—") return false;
    if (lo === "n/a" || lo === "na") return false;
    if (lo === "tidak berlaku" || lo === "tidak ada") return false;
    return true;
  };

  // Iter 377 — Mode Shella (ringkas, fokus finalisasi) vs mode Admin (lengkap).
  //   Multi-produk digabung ke 1 baris summary utk Shella; nama produk diambil
  //   dari produk pertama & disebut "+ N lainnya" bila lebih dari satu.
  const firstProd = products[0];
  const firstProdName = firstProd ? (firstProd.custom_name || firstProd.name || "Produk") : "Produk";
  const extraCount = Math.max(0, products.length - 1);

  // =========================================================================
  // Mode Riwayat Selesai (bucket=selesai) — kartu lebih ringkas.
  // =========================================================================
  if (!isMenunggu) {
    return (
      <li className={`selesai-card selesai-card-riwayat picked ${isShellaView ? "shella-view" : ""}`} data-testid={`selesai-card-${idx}`}>
        <div className="selesai-card-riwayat-row">
          <QueueMockupThumb orderNumber={order.order_number} onOpenMockup={onPreview}
            testId={`selesai-card-mockup-thumb-${idx}`}/>
          <div className="selesai-card-riwayat-info">
            <div className="selesai-card-riwayat-top">
              <span className="card-kicker" data-testid={`selesai-card-nota-${idx}`}>{order.order_number}</span>
              <span className={`chip ${statusPill.cls}`} data-testid={`selesai-card-status-${idx}`}>{statusPill.label}</span>
            </div>
            <strong className="selesai-card-riwayat-cust" data-testid={`selesai-card-cust-${idx}`}>{order.customer_name || "—"}</strong>
            {ownerManualTag(order) && <span className="omx-tag" data-testid={`selesai-card-owner-manual-${idx}`}>{ownerManualTag(order)}</span>}
            <p className="selesai-card-riwayat-prod" data-testid={`selesai-card-prods-${idx}`}>
              {firstProdName}{extraCount > 0 && <> + {extraCount} lainnya</>} · <strong>{totalPcs} pcs</strong>
            </p>
            <p className="muted small" data-testid={`selesai-card-completed-${idx}`}>
              Selesai {dateLabel(finalizedAt)}
              {!showFinanceInfo && order.finalized_by && <> · oleh <strong>{order.finalized_by}</strong></>}
            </p>
            {showFinanceInfo && order.finalized_by && (
              <p className="muted small" data-testid={`selesai-card-finalized-info-${idx}`}>
                Difinalisasi oleh <strong>{order.finalized_by}</strong>
                {order.pickup_status === "Diambil" && order.picked_up_at
                  ? ` · Diambil ${dateLabel(order.picked_up_at)}${order.picked_up_by ? ` · ${order.picked_up_by}` : ""}`
                  : " · Menunggu pickup di Admin Toko"}
              </p>
            )}
          </div>
        </div>
        <div className="selesai-card-riwayat-actions">
          <button type="button" className="soft-btn small-btn" data-testid={`selesai-card-preview-btn-${idx}`}
            onClick={onPreview}>
            Lihat Preview
          </button>
          <button type="button" className="soft-btn small-btn" data-testid={`selesai-card-detail-${idx}`} onClick={onDetail}>
            Detail Nota
          </button>
        </div>
      </li>
    );
  }

  // =========================================================================
  // Mode Menunggu Finalisasi — fokus: identitas → produk → thumbnail → aksi.
  // =========================================================================
  return (
    <li className={`selesai-card unpicked ${isShellaView ? "shella-view" : ""}`} data-testid={`selesai-card-${idx}`}>
      {/* Head: order# & customer kecil (referensi) + status pill */}
      <div className="selesai-card-head selesai-card-head-compact">
        <div className="selesai-card-ref">
          <span className="card-kicker" data-testid={`selesai-card-nota-${idx}`}>{order.order_number}</span>
          <strong className="selesai-card-cust-name" data-testid={`selesai-card-cust-${idx}`}>{order.customer_name || "—"}</strong>
          {ownerManualTag(order) && <span className="omx-tag" data-testid={`selesai-card-owner-manual-${idx}`}>{ownerManualTag(order)}</span>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
          <span className={`chip ${statusPill.cls}`} data-testid={`selesai-card-status-${idx}`}>{statusPill.label}</span>
          <span className="muted small" data-testid={`selesai-card-completed-${idx}`}>
            Produksi tuntas {dateLabel(productionDoneAt)}
          </span>
        </div>
      </div>

      {/* Body: thumbnail + ringkasan produk. Multi-produk digabung dalam kartu. */}
      <div className="selesai-card-body selesai-card-body-products selesai-card-body-with-thumb">
        <div className="selesai-card-thumb-wrap">
          <QueueMockupThumb orderNumber={order.order_number} onOpenMockup={onPreview}
            testId={`selesai-card-mockup-thumb-${idx}`}/>
        </div>
        <div className="selesai-card-body-info">
          <p className="selesai-card-summary">
            <strong>{products.length}</strong> produk · Total <strong>{totalPcs} pcs</strong>
            {meaningful(order.deadline) && <span className="muted small"> · DL {order.deadline}</span>}
          </p>
          <ul className="selesai-prod-focus" data-testid={`selesai-card-prods-${idx}`}>
            {products.map((p, pi) => {
              const sizes = Array.isArray(p.sizes) ? p.sizes : [];
              const sizeStr = sizes.length > 0
                ? sizes.filter(s => meaningful(s.size) && Number(s.quantity||0) > 0)
                       .map(s => `${s.size}: ${s.quantity||0}`).join(" · ")
                : "";
              return (
                <li key={p.id || pi} className="selesai-prod-item" data-testid={`selesai-card-prod-${idx}-${pi}`}>
                  <div className="selesai-prod-info">
                    <strong className="selesai-prod-name">{p.custom_name || p.name || "Produk"}</strong>
                    {/* Iter 377 — Hanya tampilkan atribut yg memang bermakna (bukan kosong/"Tidak Berlaku").
                        Shella tidak butuh melihat chip teknis kosong. */}
                    {(meaningful(p.color) || meaningful(p.material) || meaningful(p.sleeve)) && (
                      <div className="selesai-prod-attrs">
                        {meaningful(p.color) && <span className="chip chip-attr" data-testid={`selesai-card-prod-color-${idx}-${pi}`}>Warna: <strong>{p.color}</strong></span>}
                        {meaningful(p.material) && <span className="chip chip-attr">Bahan: <strong>{p.material}</strong></span>}
                        {meaningful(p.sleeve) && <span className="chip chip-attr">Lengan: <strong>{p.sleeve}</strong></span>}
                      </div>
                    )}
                    <p className="selesai-prod-sizes" data-testid={`selesai-card-prod-sizes-${idx}-${pi}`}>
                      <strong>{p.quantity||0} pcs</strong>{sizeStr && <> — {sizeStr}</>}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
          {/* Owner/Admin only: payment + pickup info */}
          {showFinanceInfo && (
            <p className="selesai-card-meta muted small" data-testid={`selesai-card-finance-${idx}`}>
              Pembayaran: <span className={`chip ${paymentPill.cls}`} data-testid={`selesai-card-payment-${idx}`}>{paymentPill.label}</span>
              {typeof order.grand_total === "number" && <span> · Total Rp {order.grand_total.toLocaleString("id-ID")}</span>}
              {typeof order.remaining === "number" && order.remaining > 0 && <span> · Sisa Rp {order.remaining.toLocaleString("id-ID")}</span>}
            </p>
          )}
        </div>
      </div>

      {/* Actions: Selesaikan Pesanan paling menonjol (full-width), sekunder di baris bawah. */}
      <div className="selesai-card-actions selesai-card-actions-finalisasi">
        {canFinalize && (
          <button type="button" className="primary selesai-finalize-btn selesai-finalize-primary"
            data-testid={`selesai-card-finalize-btn-${idx}`}
            onClick={onFinalize} disabled={busy}>
            {busy ? "Memproses…" : "✓ Selesaikan Pesanan"}
          </button>
        )}
        <div className="selesai-card-actions-secondary">
          <button type="button" className="soft-btn small-btn selesai-preview-compact"
            data-testid={`selesai-card-preview-btn-${idx}`} onClick={onPreview}>
            Lihat Preview
          </button>
          <button type="button" className="soft-btn small-btn" data-testid={`selesai-card-detail-${idx}`} onClick={onDetail}>
            Detail Nota
          </button>
        </div>
      </div>
    </li>
  );
}

